import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react"
import type React from "react"
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom"
import { copy, dirFor, formatLocalized, text } from "./lib/copy"
import type { Locale } from "./lib/copy"
import {
  ApiError,
  banAdminTopic,
  blockSession,
  blockAdminReportedUser,
  cancelMatchRequest,
  cancelMatchRequestOnPageExit,
  closeAdminRoom,
  createMatchRequest,
  endSession,
  endSessionOnPageExit,
  getAdminDashboard,
  getAdminRooms,
  getAdminReport,
  getAdminReports,
  getAdminReportedUsers,
  getAdminTopicBans,
  getAdminTopicSuggestions,
  getTopicSuggestions,
  getAvailability,
  getMatchRequestStatus,
  getSessionEvents,
  getMyReports,
  getMe,
  getHistory,
  getNotifications,
  readNotification,
  readAllNotifications,
  getContacts,
  getContactRequests,
  requestContact,
  respondToContactRequest,
  removeContact,
  getDirectCalls,
  getDirectCall,
  startDirectCall,
  transitionDirectCall,
  registerDevice,
  joinRoomCandidate,
  leaveSessionParticipant,
  leaveSessionParticipantOnPageExit,
  nextSession,
  ballotOpenSeatVote,
  rateSession,
  recoverSessionWithParticipant,
  reportSession,
  requestUnblockClaim,
  reviewAdminReport,
  skipRoomCandidate,
  startOpenSeatVote,
  subscribeMatchEvents,
  unblockAdminReportedUser,
  unbanAdminTopic,
  updateMyProfile,
} from "./lib/api"
import { connectRealtime } from "./lib/realtime"
import type { ConnectionState, RealtimeConnection, RemoteMediaParticipant } from "./lib/realtime"
import { ensureMediaPermission, stopMediaStream } from "./lib/media"
import { isAppVisible, notifyMatchFound, requestMatchAlertPermission, watchAppVisibility } from "./lib/background"
import { captureReportEvidence } from "./lib/evidence"
import type { AdminDashboard, AdminRoomSummary, BannedTopic, ContactRequestSummary, ContactSummary, DirectCallResponse, DirectCallSummary, HistoryItem, MatchFound, MeResponse, Mode, RatingValue, ReportedUserSummary, ReportStatus, ReportSummary, RoomParticipant, RoomPreview, RoomState, TopicSuggestion, UserModerationBlock, UserNotification } from "./lib/types"
import { ArrowLeftIcon, BanIcon, BellIcon, BoltIcon, CheckIcon, EyeOffIcon, FlagIcon, HistoryIcon, MicIcon, PhoneIcon, PhoneOffIcon, PictureInPictureIcon, RefreshCwIcon, SearchIcon, SettingsIcon, UsersIcon, VideoIcon, XIcon } from "./lib/icons"
import { AVATAR_PRESETS, AvatarFace, normalizeAvatarPresetId } from "./lib/avatars"
import { AuthFlowError, currentAuthSession, deleteAccount, isGoogleSignInConfigured, isQuietAuthError, logout, refreshAuthSession, renderGoogleSignInButton, signInWithApple, userFromMe } from "./lib/auth"
import type { AuthSession, AuthUser } from "./lib/auth"
import {
  answerNativeSystemCall,
  endNativePictureInPicture,
  endNativeSystemCall,
  isNativeIOS,
  onNativeCallAction,
  onNativeNotificationTap,
  onNativePushToken,
  pendingNativeCallActions,
  prepareNativePictureInPicture,
  registerNativeNotifications,
  reportNativeCallConnected,
  startNativeOutgoingCall,
  startNativePictureInPicture,
} from "./lib/native"
import type { NativeCallAction, NativePushToken } from "./lib/native"

const MAX_TOPIC_LENGTH = 160
const APP_VERSION = __JOOWFAK_VERSION__
const SUPPORT_NAME = "G. R. Kashani"
const SUPPORT_EMAIL = "grkashani@gmail.com"
const SUPPORT_PHONE = "+447707700023"
const COMPANY_NAME = "PetaProc"
const COPYRIGHT_NOTICE = "Copyright 2026 PetaProc. All rights reserved."
const storedMatchKey = "joowfak.match"
const storedTweaksKey = "joowfak.tweaks"
const storedShellPrefsKey = "joowfak.shellPrefs"
const seenOnboardingKey = "joowfak.seenFirstMatch"
const peerReconnectGraceSeconds = 6
const callChromeIdleMs = 4000

type DictationState = "idle" | "requesting" | "listening"
type DictationError = "" | "denied" | "unavailable"
type SpeechRecognitionResultLike = ArrayLike<{ transcript: string }>
type SpeechRecognitionEventLike = { results: ArrayLike<SpeechRecognitionResultLike> }
type SpeechRecognitionErrorEventLike = { error?: string }
type SpeechRecognitionLike = {
  lang: string
  interimResults: boolean
  continuous: boolean
  onresult: ((event: SpeechRecognitionEventLike) => void) | null
  onerror: ((event?: SpeechRecognitionErrorEventLike) => void) | null
  onend: (() => void) | null
  start: () => void
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike

type Vibe = "coral" | "violet" | "teal" | "sunset"
type Energy = "calm" | "balanced" | "lively"
type MatchingMotion = "radar" | "constellation" | "pulse"
type LiveLayout = "full" | "orbs" | "minimal"
type ThemeMode = "light" | "dark"
type RatingOption = { value: RatingValue; label: LocalizedText; shortLabel: LocalizedText }
type AdminMetricKey = "onlineNow" | "activeSessions" | "averageDurationSeconds" | "matchRatePercent" | "reportsToday"
type MetricTrendDirection = "up" | "down" | "steady"
type AuthStatus = "checking" | "signed-in" | "signed-out"
type AdminRoomFilter = "all" | "live" | "solo" | "stale"
type NetworkQualityLevel = "good" | "fair" | "poor" | "unknown"

interface NetworkConnectionLike extends EventTarget {
  downlink?: number
  effectiveType?: string
  rtt?: number
}

interface NetworkSnapshot {
  downlink?: number
  effectiveType?: string
  rtt?: number
}

interface AdminMetricSample {
  timestamp: number
  onlineNow: number
  activeSessions: number
  averageDurationSeconds: number
  matchRatePercent: number
  reportsToday: number
}

interface MetricTrend {
  direction: MetricTrendDirection
  label: string
}

interface ShellPrefs {
  locale?: Locale
  theme?: ThemeMode
}

interface AppTweaks {
  vibe: Vibe
  energy: Energy
  matchingMotion: MatchingMotion
  liveLayout: LiveLayout
}

const defaultTweaks: AppTweaks = {
  vibe: "coral",
  energy: "balanced",
  matchingMotion: "radar",
  liveLayout: "full",
}

const vibePalettes: Record<Vibe, Record<string, string>> = {
  coral: {
    "--fak": "#E25C3B",
    "--fak-deep": "#B23E22",
    "--fak-bright": "#FF7A52",
    "--fak-amber": "#F2A93B",
    "--fak-tint": "#FBEAE3",
    "--fak-tint-dark": "#2A1610",
    "--fak-rgb": "226,92,59",
    "--fak-highlight": "#FFC6AD",
    "--fak-dark": "#20120C",
    "--fak-darker": "#130B07",
  },
  violet: {
    "--fak": "#6A4BE0",
    "--fak-deep": "#412C9E",
    "--fak-bright": "#9078FF",
    "--fak-amber": "#B9A6FF",
    "--fak-tint": "#ECE8FB",
    "--fak-tint-dark": "#181030",
    "--fak-rgb": "106,75,224",
    "--fak-highlight": "#D8CEFF",
    "--fak-dark": "#16102C",
    "--fak-darker": "#0F0C1C",
  },
  teal: {
    "--fak": "#149A8B",
    "--fak-deep": "#0E6E62",
    "--fak-bright": "#2ECBB6",
    "--fak-amber": "#5FE0CB",
    "--fak-tint": "#DDF4F0",
    "--fak-tint-dark": "#082825",
    "--fak-rgb": "20,154,139",
    "--fak-highlight": "#A7EEE3",
    "--fak-dark": "#08211F",
    "--fak-darker": "#061312",
  },
  sunset: {
    "--fak": "#E8893A",
    "--fak-deep": "#B05A18",
    "--fak-bright": "#FFB258",
    "--fak-amber": "#FFD27A",
    "--fak-tint": "#FBEDDD",
    "--fak-tint-dark": "#2C1A0B",
    "--fak-rgb": "232,137,58",
    "--fak-highlight": "#FFD6A0",
    "--fak-dark": "#2C1A0B",
    "--fak-darker": "#171008",
  },
}

interface AppState {
  mode: Mode
  modeSelected: boolean
  safetyAccepted: boolean
  topicText: string
  requestId?: string
  match?: MatchFound
  restoredMatch: boolean
  startedAt?: number
  endedSessionId?: string
  endedPeerToken?: string
  endedParticipantToken?: string
  endedTopic?: string
  endedMinutes: number
  reported: boolean
  blocked: boolean
}

type Action =
  | { type: "mode"; mode: Mode }
  | { type: "safety"; accepted: boolean }
  | { type: "topic"; topicText: string }
  | { type: "request"; requestId: string }
  | { type: "clear-request" }
  | { type: "matched"; match: MatchFound }
  | { type: "ended"; minutes: number }
  | { type: "reported" }
  | { type: "blocked" }
  | { type: "reset-call" }

type LocalizedText = { fa: string; en: string }

const ui = {
  signInTitle: { fa: "ورود به فک", en: "Sign in to Fak" },
  signInBody: { fa: "برای ورود به اتاق‌های موضوعی امن، با Apple وارد شو.", en: "Sign in with Apple to join safe topic rooms." },
  signInBodyWithGoogle: { fa: "برای ورود به اتاق‌های موضوعی امن، ورود با Apple یا Google لازمه.", en: "Sign in with Apple or Google to join safe topic rooms." },
  signInApple: { fa: "ورود با Apple", en: "Sign in with Apple" },
  signInApple2fa: {
    fa: "ورود با Apple به فعال بودن تأیید دومرحله‌ای روی Apple Account نیاز دارد.",
    en: "Sign in with Apple requires two-factor authentication on your Apple Account.",
  },
  signInAppleFailed: { fa: "ورود با Apple کامل نشد. دوباره امتحان کن.", en: "Sign in with Apple did not complete. Try again." },
  signInAppleUnavailable: { fa: "ورود با Apple فعلاً در دسترس نیست.", en: "Sign in with Apple is unavailable right now." },
  signInAppleNotConfigured: { fa: "ورود با Apple هنوز تنظیم نشده است.", en: "Sign in with Apple is not configured yet." },
  signInHeader: { fa: "ورود", en: "Sign in" },
  signInToStart: { fa: "ورود و شروع", en: "Sign in to start" },
  signInRequiredTitle: { fa: "برای ادامه وارد شو", en: "Sign in to continue" },
  signInRequiredBody: { fa: "می‌تونی جو فک رو قبل از ورود ببینی. شروع تطبیق، تماس، گزارش‌ها و تنظیمات به حساب خصوصی نیاز دارد.", en: "You can explore JooW Fak before signing in. Starting a match, calls, reports, and settings require a private account." },
  signInGoogleUnavailable: { fa: "ورود Google آماده نیست", en: "Google sign-in is not ready" },
  checkingSession: { fa: "در حال بررسی ورود…", en: "Checking session…" },
  signOut: { fa: "خروج", en: "Sign out" },
  deleteAccount: { fa: "حذف حساب", en: "Delete account" },
  deleteAccountDone: { fa: "حساب حذف شد.", en: "Account deleted." },
  deleteAccountFailed: { fa: "حذف حساب انجام نشد.", en: "Account deletion failed." },
  liveTopicRoom: { fa: "اتاق زندهٔ موضوعی", en: "Live topic room" },
  skipTopic: { fa: "موضوع کوتاه‌تر، اتاق دقیق‌تر", en: "a clearer topic finds a better room" },
  aiFinds: { fa: "هوش مصنوعی اتاق نزدیک به موضوعت را پیشنهاد می‌دهد", en: "AI suggests a room close to your topic" },
  topicLabel: { fa: "موضوع", en: "Topic" },
  self: { fa: "تو", en: "You" },
  youShort: { fa: "تو", en: "You" },
  attendees: { fa: "نفر", en: "attendees" },
  roomPreviewTitle: { fa: "اتاق موضوعی پیدا شد", en: "Topic room found" },
  roomPreviewBody: { fa: "قبل از ورود، موضوع و شرکت‌کننده‌ها را ببین و بعد تصمیم بگیر.", en: "Review the topic and participants before joining." },
  joinRoom: { fa: "ملحق شو", en: "Join" },
  nextRoom: { fa: "اتاق بعدی", en: "Next room" },
  roomParticipants: { fa: "شرکت‌کننده‌های اتاق", en: "Room participants" },
  roomVoteQuestion: { fa: "یه نفر دیگه هم بیاد؟", en: "Open one more seat?" },
  strictMajority: { fa: "بیشتر از نصف نفرهای فعال باید موافق باشند", en: "more than half of active people must agree" },
  openSeatVote: { fa: "باز کردن یک صندلی دیگر", en: "Open one more seat" },
  openSeatReady: { fa: "صندلی باز آماده است", en: "Open seat ready" },
  voteYes: { fa: "آره", en: "Yes" },
  voteNo: { fa: "نه", en: "No" },
  voteCancel: { fa: "پس گرفتن رأی", en: "Cancel vote" },
  roomVoteStarted: { fa: "رأی‌گیری شروع شد.", en: "Vote started." },
  roomVoteSaved: { fa: "رأیت ذخیره شد.", en: "Vote saved." },
  roomVoteCancelled: { fa: "رأیت پس گرفته شد.", en: "Vote canceled." },
  anyTopic: { fa: "موضوع مشخص نشده", en: "Topic not set" },
  topicPending: { fa: "موضوع را وارد کن", en: "Enter a topic" },
  matchAlertTitle: { fa: "جو فک وصل شد", en: "JooW Fak matched" },
  matchAlertBody: { fa: "برگرد به اپ تا گفت‌وگو را شروع کنی.", en: "Return to the app to start the conversation." },
  momentumUp: { fa: "داره جون می‌گیره", en: "growing" },
  momentumDown: { fa: "رو به خاموشی", en: "cooling down" },
  momentumSteady: { fa: "ثابت", en: "steady" },
  wholeRoomReport: { fa: "کل اتاق", en: "Whole room" },
  reportSaved: { fa: "گزارش ثبت شد", en: "Report saved" },
  blockedSaved: { fa: "دیگه وصلتون نمی‌کنیم", en: "You will not be reconnected" },
  accountBlockedTitle: { fa: "حسابت فعلاً مسدوده", en: "Your account is blocked for now" },
  accountBlockedBody: {
    fa: "به خاطر تعداد زیاد گزارش‌های معتبر، فعلاً نمی‌تونی فک‌زنی جدید شروع کنی.",
    en: "Because of a high number of valid reports, you cannot start a new room right now.",
  },
  requestUnblockReview: { fa: "درخواست بررسی رفع مسدودی", en: "Request unblock review" },
  unblockClaimUserHint: { fa: "اگر فکر می‌کنی اشتباه شده، یک درخواست بررسی برای ادمین‌ها بفرست.", en: "If you think this was a mistake, send admins a review request." },
  unblockClaimSent: { fa: "درخواستت برای ادمین‌ها ثبت شد.", en: "Your request is now visible to admins." },
  unblockClaimFailed: { fa: "درخواست ثبت نشد. کمی بعد دوباره امتحان کن.", en: "The request was not saved. Try again shortly." },
  profileMenu: { fa: "پروفایل من", en: "My profile" },
  avatarTitle: { fa: "آواتار اتاق", en: "Room avatar" },
  avatarHint: { fa: "یک سبک آماده انتخاب کن تا در اتاق‌ها کنار نامت دیده شود.", en: "Choose a preset style to show beside your name in rooms." },
  notifications: { fa: "اعلان‌ها", en: "Notifications" },
  noNotifications: { fa: "اعلان تازه‌ای نداری.", en: "You have no notifications yet." },
  markAllRead: { fa: "همه خوانده شد", en: "Mark all read" },
  history: { fa: "تاریخچه", en: "History" },
  noHistory: { fa: "هنوز به اتاقی نپیوستی.", en: "You have not joined a room yet." },
  contacts: { fa: "مخاطب‌ها", en: "Contacts" },
  noContacts: { fa: "هنوز مخاطبی نداری.", en: "No contacts yet." },
  contactRequests: { fa: "درخواست‌های مخاطب", en: "Contact requests" },
  requestContact: { fa: "درخواست افزودن مخاطب", en: "Request JooW contact" },
  contactRequestSent: { fa: "درخواست مخاطب فرستاده شد.", en: "Contact request sent." },
  contactRequestPending: { fa: "در انتظار پاسخ", en: "Waiting for response" },
  contactAccepted: { fa: "درخواست پذیرفته شد.", en: "Contact request accepted." },
  contactDeclined: { fa: "درخواست رد شد.", en: "Contact request declined." },
  contactRemoved: { fa: "مخاطب حذف شد.", en: "Contact removed." },
  accept: { fa: "پذیرفتن", en: "Accept" },
  decline: { fa: "رد کردن", en: "Decline" },
  cancel: { fa: "لغو", en: "Cancel" },
  remove: { fa: "حذف", en: "Remove" },
  call: { fa: "تماس", en: "Call" },
  voiceCall: { fa: "تماس صوتی", en: "Voice call" },
  videoCall: { fa: "تماس تصویری", en: "Video call" },
  incomingCall: { fa: "تماس ورودی جو فک", en: "Incoming JooW Fak call" },
  outgoingCall: { fa: "در حال تماس…", en: "Calling…" },
  callConnecting: { fa: "در حال وصل کردن تماس…", en: "Connecting call…" },
  callEnded: { fa: "تماس پایان یافت", en: "Call ended" },
  callUnavailable: { fa: "این تماس دیگر در دسترس نیست.", en: "This call is no longer available." },
  participantActions: { fa: "گزینه‌های شرکت‌کننده", en: "Participant actions" },
  sharedTopicOnly: { fa: "درخواست فقط برای کسی فرستاده می‌شود که در همین اتاق با تو بوده است.", en: "Requests can only be sent to someone who shared this room with you." },
  profileSetupTitle: { fa: "نام نمایشی‌ات را انتخاب کن", en: "Choose your display name" },
  profileSetupBody: { fa: "این نام همراه یک کد عمومی در اتاق‌ها دیده می‌شود. لازم نیست یکتا باشد و ایمیلت نمایش داده نمی‌شود.", en: "This name is shown in rooms with a public code. It does not need to be unique, and your email is never shown." },
  profileNameLabel: { fa: "نام نمایشی", en: "Display name" },
  profileNamePlaceholder: { fa: "مثلاً Navid", en: "For example, Navid" },
  saveProfile: { fa: "ذخیره و ادامه", en: "Save and continue" },
  profileNameRequired: { fa: "نام نمایشی باید بین ۲ تا ۴۰ کاراکتر باشد.", en: "Display name must be 2-40 characters." },
  profileRequiredSearch: { fa: "قبل از جست‌وجوی اتاق، نام نمایشی‌ات را ذخیره کن.", en: "Save a display name before searching for a room." },
  ratingAwful: { fa: "افتضاح", en: "Awful" },
  ratingBad: { fa: "بد", en: "Poor" },
  ratingModerate: { fa: "معمولی", en: "Okay" },
  ratingGood: { fa: "خوب", en: "Good" },
  ratingSuper: { fa: "عالی", en: "Great" },
  ratingAwfulShort: { fa: "خیلی بد", en: "Shitty" },
  ratingBadShort: { fa: "بد", en: "Bad" },
  ratingModerateShort: { fa: "معمولی", en: "Moderate" },
  ratingGoodShort: { fa: "خوب", en: "Good" },
  ratingSuperShort: { fa: "عالی", en: "Super" },
  adminTitle: { fa: "کنسول مدیریت", en: "Admin console" },
  adminOverview: { fa: "نمای کلی", en: "Overview" },
  adminRealtime: { fa: "امروز · زمان واقعی", en: "Today · realtime" },
  adminSearch: { fa: "جستجوی کد گفت‌وگو یا JooW ID…", en: "Search conversation code or JooW ID…" },
  adminLive: { fa: "گفت‌وگوهای زنده", en: "Live conversations" },
  adminRooms: { fa: "اتاق‌ها", en: "Rooms" },
  adminReports: { fa: "گزارش‌ها", en: "Reports" },
  adminTopics: { fa: "موضوع‌ها", en: "Topics" },
  adminUsers: { fa: "کاربران گزارش‌شده", en: "Reported users" },
  adminSafety: { fa: "ایمنی", en: "Safety" },
  adminSettings: { fa: "تنظیمات", en: "Settings" },
  adminLogout: { fa: "خروج", en: "Sign out" },
  adminNoIdentity: { fa: "این کنسول ایمیل یا هویت ورود کاربران را نمایش نمی‌دهد. فقط نام نمایشی و کد عمومی قابل حسابرسی اتاق دیده می‌شود.", en: "This console does not show login email or private account identity. It shows only display names and auditable public room codes." },
  reporterPrivate: { fa: "گزارش‌دهندهٔ واردشده · هویت خصوصی محفوظ", en: "Signed-in reporter · private identity hidden" },
  metricOnline: { fa: "آنلاین الان", en: "Online now" },
  metricActive: { fa: "گفت‌وگوهای فعال", en: "Active conversations" },
  metricActiveShort: { fa: "فعال", en: "Active" },
  metricAverage: { fa: "میانگین مدت", en: "Average duration" },
  metricMatch: { fa: "نرخ تطبیق", en: "Match rate" },
  metricReports: { fa: "گزارش‌های امروز", en: "Reports today" },
  positiveMore: { fa: "بیشتر", en: "more" },
  positiveLess: { fa: "کمتر", en: "less" },
  steady: { fa: "بدون تغییر", en: "steady" },
  filter: { fa: "فیلتر", en: "Filter" },
  code: { fa: "نشانه", en: "Code" },
  mode: { fa: "حالت", en: "Mode" },
  duration: { fa: "مدت", en: "Duration" },
  vibe: { fa: "هم‌حال", en: "Vibe" },
  status: { fa: "وضعیت", en: "Status" },
  reported: { fa: "گزارش شده", en: "Reported" },
  reportQueue: { fa: "گزارش‌های در صف", en: "Queued reports" },
  review: { fa: "بررسی", en: "Review" },
  seeAllReports: { fa: "دیدن همهٔ گزارش‌ها", en: "See all reports" },
  hotTopics: { fa: "موضوع‌های داغ", en: "Hot topics" },
  minutesAgo: { fa: "{minutes} دقیقه پیش", en: "{minutes} min ago" },
  harassment: { fa: "مزاحمت و توهین", en: "Harassment or abuse" },
  inappropriate: { fa: "محتوای نامناسب", en: "Inappropriate content" },
  spam: { fa: "اسپم", en: "Spam" },
  adminDenied: { fa: "دسترسی مدیریت در دسترس نیست.", en: "Admin access is unavailable." },
  adminDeniedBody: {
    fa: "این حساب در پایگاه دادهٔ جو فک به عنوان ادمین فعال نشده.",
    en: "This account is not enabled as a JooW Fak admin in the database.",
  },
  adminDeniedAction: { fa: "برگشت به اپ", en: "Back to app" },
  loading: { fa: "در حال بارگذاری…", en: "Loading…" },
  refreshing: { fa: "در حال تازه‌سازی…", en: "Refreshing…" },
  refresh: { fa: "تازه‌سازی", en: "Refresh" },
  lastUpdated: { fa: "آخرین به‌روزرسانی: {time}", en: "Last updated: {time}" },
  liveApi: { fa: "زنده از API", en: "Live API" },
  noLiveSessions: { fa: "گفت‌وگوی زندهٔ واقعی نداریم.", en: "No real live conversations." },
  noRooms: { fa: "اتاق چندنفرهٔ فعالی نداریم.", en: "No active multiparty rooms." },
  noOpenSeat: { fa: "صندلی باز ندارد", en: "No open seat" },
  roomLive: { fa: "گفت‌وگوی فعال", en: "Live conversation" },
  roomSolo: { fa: "یک شرکت‌کننده باقی مانده", en: "One participant remains" },
  roomStale: { fa: "اتاق مانده", en: "Stale room" },
  roomsAll: { fa: "همه", en: "All" },
  roomsLive: { fa: "زنده", en: "Live" },
  roomsSolo: { fa: "تک‌نفره", en: "Solo" },
  roomsStale: { fa: "مانده", en: "Stale" },
  closeStaleRoom: { fa: "بستن اتاق مانده", en: "Force close stale room" },
  staleRoomClosed: { fa: "اتاق مانده بسته شد.", en: "Stale room closed." },
  peerLeftTitle: { fa: "هم‌صحبتت از اتاق خارج شد", en: "Your peer left the room" },
  peerLeftBody: { fa: "می‌تونی اتاق موضوعی بعدی را پیدا کنی یا به صفحهٔ اصلی برگردی.", en: "You can find the next topic room or return to the main screen." },
  peerReconnectTitle: { fa: "در حال تلاش برای اتصال دوباره", en: "Trying to reconnect" },
  peerReconnectBody: { fa: "{seconds} ثانیه تا نمایش گزینه‌های خروج", en: "{seconds}s before showing leave options" },
  exitToHome: { fa: "صفحهٔ اصلی", en: "Main menu" },
  devLiveSessions: { fa: "{count} نشست تست/توسعه از گفت‌وگوی واقعی جدا شده.", en: "{count} dev/test active session(s) are separate from real conversations." },
  noReports: { fa: "گزارشی برای این فیلتر نیست.", en: "No reports for this view." },
  noHotTopics: { fa: "هنوز موضوع واقعی ثبت نشده.", en: "No real topics yet." },
  noReportedUsers: { fa: "در این بازه کسی از آستانهٔ گزارش چندنفره رد نشده.", en: "No one crossed the multi-reporter threshold in this range." },
  noBlockedUsers: { fa: "فعلاً مسدودی سراسری فعال نیست.", en: "No app-wide user blocks are active." },
  noUnblockClaims: { fa: "درخواست رفع مسدودی در صف نیست.", en: "No unblock requests are queued." },
  noBannedTopics: { fa: "موضوع ممنوع فعالی نداریم.", en: "No active banned topics." },
  noSecurityEvents: { fa: "در این بازه گزارش ایمنی نداریم.", en: "No safety reports in this range." },
  adminProfile: { fa: "پروفایل ادمین", en: "Admin profile" },
  openClient: { fa: "باز کردن اپ کاربر", en: "Open client app" },
  clientSettings: { fa: "تنظیمات کاربر", en: "Client settings" },
  adminSettingsMenu: { fa: "تنظیمات ادمین", en: "Admin settings" },
  toggleLanguage: { fa: "تغییر زبان", en: "Switch language" },
  switchToEnglish: { fa: "English", en: "English" },
  switchToPersian: { fa: "فارسی", en: "Persian" },
  toggleTheme: { fa: "تغییر تم", en: "Switch theme" },
  today: { fa: "امروز", en: "Today" },
  sevenDays: { fa: "۷ روز", en: "7 days" },
  thirtyDays: { fa: "۳۰ روز", en: "30 days" },
  reportTotal: { fa: "کل گزارش‌ها", en: "Total reports" },
  confirmedReports: { fa: "تأییدشده", en: "Confirmed" },
  dismissedReports: { fa: "ردشده", en: "Dismissed" },
  blockedReports: { fa: "مسدودی‌ها", en: "Blocks" },
  readOnly: { fa: "خواندنی", en: "Read-only" },
  markValid: { fa: "گزارش معتبر شد", en: "Report marked valid" },
  markInvalid: { fa: "گزارش بی‌اعتبار شد", en: "Report marked invalid" },
  markPending: { fa: "گزارش به صف بررسی برگشت", en: "Report moved back to review" },
  resetReview: { fa: "برگردان به صف", en: "Back to review" },
  minReporters: { fa: "حداقل گزارش‌دهنده", en: "Minimum reporters" },
  applyFilters: { fa: "اعمال", en: "Apply" },
  filterUpdating: { fa: "در حال اعمال", en: "Applying" },
  filtersApplied: { fa: "اعمال شد", en: "Applied" },
  distinctReportersHint: { fa: "آستانه با گزارش‌دهنده‌های متفاوت و فقط برای کاربر واقعی جو محاسبه می‌شود؛ گزارش‌های تکراری یک نفر یا طرف تستی، کاربر قابل مسدودی نمی‌سازد.", en: "Thresholds count different reporters and only real JooW targets; repeat reports by one user or a dev peer do not create a blockable user." },
  decreaseReporters: { fa: "کم کردن حداقل گزارش‌دهنده", en: "Decrease minimum reporters" },
  increaseReporters: { fa: "زیاد کردن حداقل گزارش‌دهنده", en: "Increase minimum reporters" },
  reporters: { fa: "گزارش‌دهنده", en: "reporters" },
  reportEvents: { fa: "گزارش", en: "reports" },
  sessionsCount: { fa: "گفت‌وگو", en: "sessions" },
  blockUser: { fa: "مسدود از اپ", en: "Block from app" },
  unblockUser: { fa: "رفع مسدودی", en: "Unblock" },
  blockedUsers: { fa: "مسدودی‌های فعال", en: "Active blocks" },
  unblockClaims: { fa: "درخواست‌های رفع مسدودی", en: "Unblock requests" },
  unblockClaimHint: { fa: "کاربر مسدود بدون نمایش هویت، درخواست بررسی داده.", en: "A blocked user requested review without exposing identity." },
  claimReviewRequested: { fa: "درخواست بررسی", en: "Review requested" },
  reportedUserRisk: { fa: "ریسک گزارش چندنفره", en: "Multi-reporter risk" },
  userBlockHint: { fa: "مسدودی با کلید ایمنی داخلی اعمال می‌شود؛ ایمیل کاربر نمایش داده نمی‌شود.", en: "Blocks apply by internal safety key; user email is not shown." },
  userBlocked: { fa: "کاربر از اپ مسدود شد.", en: "User blocked from the app." },
  userUnblocked: { fa: "مسدودی کاربر برداشته شد.", en: "User block removed." },
  topicBan: { fa: "ممنوع کردن موضوع", en: "Ban Topic" },
  topicRestore: { fa: "رفع ممنوعیت", en: "Restore" },
  bannedTopics: { fa: "موضوع‌های ممنوع", en: "Banned topics" },
  topicBanPlaceholder: { fa: "موضوعی که نباید درباره‌اش فک‌زنی شود…", en: "Topic that should not be discussed…" },
  topicBanned: { fa: "موضوع ممنوع شد.", en: "Topic banned." },
  topicRestored: { fa: "موضوع دوباره آزاد شد.", en: "Topic restored." },
  activeBan: { fa: "فعال", en: "Active" },
  restored: { fa: "آزاد شده", en: "Restored" },
  adminLocalControls: { fa: "کنترل‌های همین کنسول", en: "This console controls" },
  appShellSource: { fa: "زبان و تم اولیه از دستگاه می‌آیند و اینجا قابل تغییرند.", en: "Initial language and theme come from this device and can be changed here." },
  customizeDisplay: { fa: "ظاهر و زبان جو فک", en: "JooW Fak display" },
  customizeDisplayHint: { fa: "انتخاب‌های نمایش روی همین دستگاه ذخیره می‌شوند.", en: "Display choices are saved on this device." },
  superAppDisplayActive: { fa: "کنترل با تنظیمات همین دستگاه", en: "Controlled by this device" },
  appDisplayActive: { fa: "کنترل با تنظیمات همین اپ", en: "Controlled by this app" },
  settingsTitle: { fa: "تنظیمات", en: "Options" },
  myReports: { fa: "گزارش‌های من", en: "My reports" },
  accent: { fa: "رنگ", en: "Accent" },
  energy: { fa: "انرژی", en: "Energy" },
  matchingStyle: { fa: "حالت پیدا کردن", en: "Matching style" },
  liveLayout: { fa: "چیدمان تماس", en: "Call layout" },
  member: { fa: "عضو جوو", en: "JooW member" },
  persian: { fa: "فارسی", en: "Persian" },
  english: { fa: "انگلیسی", en: "English" },
  lightTheme: { fa: "روشن", en: "Light" },
  darkTheme: { fa: "تاریک", en: "Dark" },
  coral: { fa: "مرجانی", en: "Coral" },
  violet: { fa: "بنفش", en: "Violet" },
  teal: { fa: "سبزآبی", en: "Teal" },
  sunset: { fa: "غروب", en: "Sunset" },
  calm: { fa: "آرام", en: "Calm" },
  balanced: { fa: "متعادل", en: "Balanced" },
  lively: { fa: "زنده", en: "Lively" },
  radar: { fa: "رادار", en: "Radar" },
  constellation: { fa: "صورت فلکی", en: "Constellation" },
  pulse: { fa: "حلقه‌ها", en: "Pulse rings" },
  layoutFull: { fa: "تمام‌صفحه", en: "Full bleed" },
  layoutOrbs: { fa: "دو گوی", en: "Twin orbs" },
  layoutMinimal: { fa: "کارت مینیمال", en: "Minimal card" },
  showControls: { fa: "نمایش کنترل‌ها", en: "Show call controls" },
  hideControls: { fa: "پنهان کردن کنترل‌ها", en: "Hide call controls" },
  reportPeer: { fa: "گزارش هم‌صحبت", en: "Report conversation" },
  reportPrompt: { fa: "چی پیش اومد؟ گزارش برای بررسی ایمنی جو ارسال می‌شود.", en: "What happened? Your report goes to JooW Safety for review." },
  reportConversation: { fa: "گفت‌وگو", en: "Conversation" },
  reportNotes: { fa: "توضیح بیشتر (اختیاری)", en: "More detail (optional)" },
  reportNotesPlaceholder: { fa: "هر چی کمک می‌کنه بهتر بررسی کنیم…", en: "Anything that helps us review it…" },
  reportBlock: { fa: "به این شرکت‌کننده دوباره وصل نشوم", en: "Do not reconnect me to this participant" },
  reportBlockedPrivate: { fa: "این مسدودی به همین گزارش وصل می‌شود و با کد عمومی طرف مقابل اجرا می‌شود.", en: "This block is attached to this report and enforced against the other participant's public room code." },
  reportEvidenceConsent: { fa: "قبول دارم محتوای گزارش، توضیحاتم و داده‌های ایمنی در دسترس برای بررسی به تیم ایمنی جو ارسال و نگهداری شود.", en: "I consent to sending and retaining my report contents, notes, and available safety metadata for JooW Safety review." },
  reportEvidenceCapturing: { fa: "در حال ضبط ۱۰ ثانیه نمونهٔ قابل بررسی…", en: "Capturing a 10 second review sample…" },
  reportEvidenceCaptured: { fa: "نمونهٔ بررسی همراه گزارش فرستاده می‌شود.", en: "A review sample will be sent with this report." },
  reportEvidenceUnavailable: { fa: "نمونهٔ صوت/تصویر در این لحظه در دسترس نیست؛ گزارش با رضایت و فراداده ثبت می‌شود.", en: "Audio/video evidence is not available right now; the report will keep consent and metadata." },
  evidenceConsent: { fa: "رضایت بررسی", en: "Review consent" },
  evidenceClip: { fa: "نمونهٔ بررسی", en: "Review sample" },
  evidenceAudio: { fa: "صوت", en: "Audio" },
  evidenceVideo: { fa: "ویدیو", en: "Video" },
  evidenceUnavailable: { fa: "نمونه‌ای ذخیره نشده", en: "No sample stored" },
  evidenceMetadataOnly: { fa: "فرادادهٔ نمونه ثبت شده ولی فایل قابل پخش در دسترس نیست.", en: "Sample metadata is stored, but the playable clip is unavailable." },
  consentCaptured: { fa: "ثبت شده", en: "Captured" },
  consentMissing: { fa: "ثبت نشده", en: "Missing" },
  sendReport: { fa: "ارسال گزارش", en: "Send report" },
  reportAndLeave: { fa: "گزارش و خروج", en: "Report and leave" },
  modeRequired: { fa: "قبل از شروع حالت گفت‌وگو رو انتخاب کن.", en: "Choose voice or video before starting." },
  startNeedsSignIn: { fa: "برای شروع باید وارد شوی.", en: "Sign in before starting." },
  startNeedsSafety: { fa: "برای شروع، قانون ایمنی را تأیید کن.", en: "Accept the safety rule before starting." },
  startNeedsMode: { fa: "برای شروع، Voice یا Video را انتخاب کن.", en: "Choose Voice or Video before starting." },
  topicRequired: { fa: "برای پیدا کردن اتاق، یک موضوع کوتاه بنویس.", en: "Enter a short topic to find a room." },
  startTopicTooLong: { fa: "موضوع را کوتاه‌تر کن تا جست‌وجو شروع شود.", en: "Shorten the topic before starting." },
  onboardingMicrocopy: {
    fa: "اولین بارته؟ یک موضوع کوتاه بنویس؛ قبل از ورود، اتاق و شرکت‌کننده‌ها را می‌بینی.",
    en: "First time here? Enter a short topic; you will review the room and participants before joining.",
  },
  searchingExact: { fa: "کیفیت جست‌وجو: موضوع دقیق", en: "Search quality: exact topic" },
  searchingSemantic: { fa: "کیفیت جست‌وجو: هم‌معنی‌ها و نزدیک‌ها", en: "Search quality: semantic neighbors" },
  searchingExpanded: { fa: "کیفیت جست‌وجو: اتاق‌های نزدیک و صف گسترده", en: "Search quality: expanded rooms and queue" },
  searchingNoTopic: { fa: "کیفیت جست‌وجو: موضوع دقیق‌تر، اتاق بهتر", en: "Search quality: clearer topic, better room" },
  networkGood: { fa: "شبکه خوب", en: "Network good" },
  networkFair: { fa: "شبکه متوسط", en: "Network fair" },
  networkPoor: { fa: "شبکه ضعیف", en: "Network poor" },
  networkUnknown: { fa: "شبکه نامشخص", en: "Network unknown" },
  pip: { fa: "تصویر در تصویر", en: "Picture in Picture" },
  pipUnavailable: { fa: "تصویر در تصویر در این دستگاه برای تماس زنده در دسترس نیست.", en: "Picture in Picture is unavailable for this live call on this device." },
  reasonRequired: { fa: "اول دلیل گزارش رو انتخاب کن.", en: "Choose a report reason first." },
  otherReasonRequired: { fa: "برای «موارد دیگر» چند کلمه توضیح بده.", en: "Add a short note for Other." },
  evidenceConsentRequired: { fa: "برای ارسال گزارش باید رضایت بررسی داده‌ها رو تأیید کنی.", en: "Confirm review-data consent before sending the report." },
  dismiss: { fa: "بی‌خیال", en: "Cancel" },
  reportSubmittedTitle: { fa: "گزارشت ثبت شد", en: "Report submitted" },
  reportSubmittedBody: { fa: "تیم ایمنی جو ظرف ۲۴ ساعت بررسی‌اش می‌کنه. نتیجه رو تو «گزارش‌های من» می‌بینی.", en: "JooW Safety reviews it within 24 hours. You can see the result in My reports." },
  continueNext: { fa: "جست‌وجوی اتاق بعدی", en: "Find another room" },
  statusPending: { fa: "در حال بررسی", en: "Under review" },
  statusValid: { fa: "تأیید شد", en: "Confirmed" },
  statusInvalid: { fa: "رد شد", en: "Dismissed" },
  actionValid: { fa: "اقدام انجام شد؛ کد ایمنی طرف محدود شد.", en: "Action taken. The participant safety code was limited." },
  actionWarning: { fa: "اقدام انجام شد — اخطار ثبت شد.", en: "Action taken. A warning was recorded." },
  actionInvalid: { fa: "گزارش معتبر تشخیص داده نشد.", en: "The report was not found valid." },
  valid: { fa: "معتبر", en: "Valid" },
  invalid: { fa: "بی‌اعتبار", en: "Invalid" },
} satisfies Record<string, LocalizedText>

const ratingOptions: RatingOption[] = [
  { value: "super", label: ui.ratingSuper, shortLabel: ui.ratingSuperShort },
  { value: "good", label: ui.ratingGood, shortLabel: ui.ratingGoodShort },
  { value: "moderate", label: ui.ratingModerate, shortLabel: ui.ratingModerateShort },
  { value: "bad", label: ui.ratingBad, shortLabel: ui.ratingBadShort },
  { value: "awful", label: ui.ratingAwful, shortLabel: ui.ratingAwfulShort },
]

const fallbackState: AppState = {
  mode: "voice",
  modeSelected: false,
  safetyAccepted: false,
  topicText: "",
  endedMinutes: 0,
  reported: false,
  blocked: false,
  restoredMatch: false,
}

const AppContext = createContext<{
  state: AppState
  dispatch: React.Dispatch<Action>
  locale: Locale
  theme: ThemeMode
  setLocale: (locale: Locale) => void
  setTheme: (theme: ThemeMode) => void
  tweaks: AppTweaks
  setTweaks: React.Dispatch<React.SetStateAction<AppTweaks>>
  t: (value: LocalizedText) => string
  f: (value: LocalizedText, vars: Record<string, string | number>) => string
} | null>(null)

const AuthContext = createContext<{
  status: AuthStatus
  user: AuthUser | null
  error: string
  signingIn: boolean
  completeSignIn: (session: AuthSession) => void
  signInAppleAccount: () => Promise<AuthSession>
  signOut: () => Promise<void>
  clearError: () => void
} | null>(null)

export function App() {
  const [state, dispatch] = useReducer(reducer, undefined, loadInitialState)
  const [shellPrefs, setShellPrefs] = useState(loadShellPrefs)
  const [systemTheme, setSystemTheme] = useState<ThemeMode>(deviceTheme)
  const [tweaks, setTweaks] = useState(loadTweaks)
  const fallbackPrefs = useMemo(() => ({ locale: deviceLocale(), theme: systemTheme }), [systemTheme])
  const locale = shellPrefs.locale || fallbackPrefs.locale
  const theme = shellPrefs.theme || fallbackPrefs.theme
  const setLocale = useCallback((nextLocale: Locale) => {
    setShellPrefs((current) => ({ ...current, locale: nextLocale }))
  }, [])
  const setTheme = useCallback((nextTheme: ThemeMode) => {
    setShellPrefs((current) => ({ ...current, theme: nextTheme }))
  }, [])
  const translate = useCallback((valueToTranslate: LocalizedText) => text(valueToTranslate, locale), [locale])
  const translateFormat = useCallback((valueToTranslate: LocalizedText, vars: Record<string, string | number>) => {
    return formatLocalized(valueToTranslate, vars, locale)
  }, [locale])
  const value = useMemo(() => ({
    state,
    dispatch,
    locale,
    theme,
    setLocale,
    setTheme,
    tweaks,
    setTweaks,
    t: translate,
    f: translateFormat,
  }), [state, locale, theme, setLocale, setTheme, tweaks, translate, translateFormat])

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)")
    const updateSystemTheme = () => setSystemTheme(media.matches ? "dark" : "light")
    updateSystemTheme()
    media.addEventListener?.("change", updateSystemTheme)
    return () => media.removeEventListener?.("change", updateSystemTheme)
  }, [])

  useEffect(() => {
    document.documentElement.lang = locale === "fa" ? "fa-IR" : "en"
    document.documentElement.dir = dirFor(locale)
    document.documentElement.dataset.theme = theme
  }, [locale, theme])

  useEffect(() => {
    window.localStorage.setItem(storedShellPrefsKey, JSON.stringify(shellPrefs))
  }, [shellPrefs])

  useEffect(() => {
    window.localStorage.setItem(storedTweaksKey, JSON.stringify(tweaks))
  }, [tweaks])

  useEffect(() => {
    document.documentElement.dataset.fakEnergy = tweaks.energy
    document.documentElement.dataset.fakMatching = tweaks.matchingMotion
    document.documentElement.dataset.fakLiveLayout = tweaks.liveLayout
    const palette = vibePalettes[tweaks.vibe]
    for (const [name, value] of Object.entries(palette)) {
      document.documentElement.style.setProperty(name, value)
    }
  }, [tweaks])

  useEffect(() => {
    const sessionId = state.match?.sessionId || ""
    const participantToken = state.match?.participantToken || ""
    const requestId = state.requestId || ""
    if (!sessionId && !requestId) return undefined

    let flushed = false
    const flushActiveConversation = () => {
      if (flushed) return
      flushed = true
      if (sessionId) {
        if (participantToken) leaveSessionParticipantOnPageExit(sessionId, participantToken)
        else endSessionOnPageExit(sessionId)
        return
      }
      cancelMatchRequestOnPageExit(requestId)
    }

    window.addEventListener("pagehide", flushActiveConversation)
    window.addEventListener("beforeunload", flushActiveConversation)
    return () => {
      window.removeEventListener("pagehide", flushActiveConversation)
      window.removeEventListener("beforeunload", flushActiveConversation)
    }
  }, [state.match?.sessionId, state.match?.participantToken, state.requestId])

  return (
    <AppContext.Provider value={value}>
      <ScrollReset />
      <div className="joowfak-app" dir={dirFor(locale)} data-theme={theme}>
        <AuthGate>
          <>
            <IncomingCallWatcher />
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/topic" element={<TopicPage />} />
              <Route path="/matching" element={<MatchingPage />} />
              <Route path="/call" element={<CallPage />} />
              <Route path="/calls/:callId" element={<DirectCallPage />} />
              <Route path="/switching" element={<SwitchingPage />} />
              <Route path="/ended" element={<EndedPage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/history" element={<HistoryPage />} />
              <Route path="/contacts" element={<ContactsPage />} />
              <Route path="/reports" element={<MyReportsPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/help" element={<HelpPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/admin" element={<AdminGate />} />
              <Route path="/admin/live" element={<AdminGate />} />
              <Route path="/admin/rooms" element={<AdminGate />} />
              <Route path="/admin/reports" element={<AdminGate />} />
              <Route path="/admin/reports/:reportId" element={<AdminGate />} />
              <Route path="/admin/topics" element={<AdminGate />} />
              <Route path="/admin/users" element={<AdminGate />} />
              <Route path="/admin/security" element={<AdminGate />} />
              <Route path="/admin/settings" element={<AdminGate />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </>
        </AuthGate>
      </div>
    </AppContext.Provider>
  )
}

function AuthGate({ children }: { children: React.ReactNode }) {
  const { t } = useAppState()
  const location = useLocation()
  const [status, setStatus] = useState<AuthStatus>("checking")
  const [user, setUser] = useState<AuthUser | null>(() => currentAuthSession()?.user || null)
  const [error, setError] = useState("")
  const [signingIn, setSigningIn] = useState(false)

  const completeSignIn = useCallback((session: AuthSession) => {
    setError("")
    setUser(session.user)
    setStatus("signed-in")
  }, [])

  const signInAppleAccount = useCallback(async () => {
    if (signingIn) throw new AuthFlowError("apple_failed", "Sign in with Apple is already in progress")
    try {
      setSigningIn(true)
      setError("")
      const session = await signInWithApple()
      completeSignIn(session)
      return session
    } catch (err) {
      setError(isQuietAuthError(err) ? "" : authErrorMessage(err, t))
      throw err
    } finally {
      setSigningIn(false)
    }
  }, [completeSignIn, signingIn, t])

  const signOut = useCallback(async () => {
    await logout()
    setUser(null)
    setError("")
    setStatus("signed-out")
  }, [])

  useEffect(() => {
    let cancelled = false
    getMe()
      .then((profile) => {
        if (cancelled) return
        const stored = currentAuthSession()
        const profileUser = userFromMe(profile)
        setUser(stored?.user ? {
          ...stored.user,
          displayName: profileUser.displayName || stored.user.displayName,
          email: profileUser.email || stored.user.email,
          avatarUrl: profileUser.avatarUrl || stored.user.avatarUrl,
          avatarPreset: profileUser.avatarPreset || stored.user.avatarPreset,
          publicCode: profileUser.publicCode || stored.user.publicCode,
          profileComplete: profileUser.profileComplete ?? stored.user.profileComplete,
        } : profileUser)
        setStatus("signed-in")
      })
      .catch(async () => {
        const refreshed = await refreshAuthSession()
        if (cancelled) return
        setUser(refreshed?.user || null)
        setStatus(refreshed ? "signed-in" : "signed-out")
      })
    return () => {
      cancelled = true
    }
  }, [])

  const authValue = useMemo(() => ({
    status,
    user,
    error,
    signingIn,
    completeSignIn,
    signInAppleAccount,
    signOut,
    clearError: () => setError(""),
  }), [completeSignIn, error, signInAppleAccount, signOut, signingIn, status, user])
  const publicRoute = location.pathname === "/help" || location.pathname === "/privacy"
  const previewRoute = location.pathname === "/" || location.pathname === "/topic"
  const protectedRoute = !publicRoute && !previewRoute
  const guardedContent = protectedRoute && status === "checking"
    ? <main className="auth-stage"><section className="auth-panel"><p>{t(ui.checkingSession)}</p></section></main>
    : protectedRoute && status === "signed-out"
    ? <main className="auth-stage"><AuthPrompt variant="panel" /></main>
    : children

  return <AuthContext.Provider value={authValue}><NativeServices />{guardedContent}</AuthContext.Provider>
}

function NativeServices() {
  const auth = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (auth.status !== "signed-in" || !isNativeIOS()) return undefined
    let disposed = false
    const handles: Array<{ remove: () => Promise<void> }> = []
    const handled = new Set<string>()

    const saveToken = async (event: NativePushToken) => {
      if (!event.token || disposed) return
      await registerDevice({
        token: event.token,
        kind: event.kind,
        environment: event.environment,
        deviceName: event.deviceName,
      }).catch(() => undefined)
    }
    const handleCallAction = async (event: NativeCallAction) => {
      if (!event.callId || disposed) return
      const key = `${event.action}:${event.callId}`
      if (handled.has(key)) return
      handled.add(key)
      if (event.action === "accept") {
        await transitionDirectCall(event.callId, "accept").catch(() => undefined)
        if (!disposed) navigate(`/calls/${encodeURIComponent(event.callId)}`)
      } else if (event.action === "end") {
        await transitionDirectCall(event.callId, "end").catch(() => undefined)
      }
    }

    const setup = async () => {
      const pushHandle = await onNativePushToken((event) => void saveToken(event))
      const callHandle = await onNativeCallAction((event) => void handleCallAction(event))
      const notificationHandle = await onNativeNotificationTap((event) => {
        if (event.resourceId && event.kind === "incoming_call") navigate(`/calls/${encodeURIComponent(event.resourceId)}`)
        else if (event.kind === "contact_request" || event.kind === "contact_accepted") navigate("/contacts")
        else navigate("/notifications")
      })
      for (const handle of [pushHandle, callHandle, notificationHandle]) {
        if (!handle) continue
        if (disposed) await handle.remove()
        else handles.push(handle)
      }
      if (disposed) return
      const registration = await registerNativeNotifications().catch(() => null)
      if (registration?.environment) {
        if (registration.standardToken) await saveToken({ kind: "standard", token: registration.standardToken, environment: registration.environment })
        if (registration.voipToken) await saveToken({ kind: "voip", token: registration.voipToken, environment: registration.environment })
      }
      const actions = await pendingNativeCallActions().catch(() => [])
      for (const action of actions) await handleCallAction(action)
    }
    void setup()
    return () => {
      disposed = true
      for (const handle of handles) void handle.remove()
    }
  }, [auth.status, navigate])

  return null
}

function AuthPrompt({ variant = "inline", onDone }: { variant?: "inline" | "panel"; onDone?: (session?: AuthSession) => void }) {
  const { t } = useAppState()
  const auth = useAuth()
  const googleButtonRef = useRef<HTMLDivElement | null>(null)
  const googleEnabled = isGoogleSignInConfigured()
  const [pending, setPending] = useState(false)

  useEffect(() => {
    if (!googleEnabled || auth.status !== "signed-out" || !googleButtonRef.current) return
    const target = googleButtonRef.current
    target.innerHTML = ""
    renderGoogleSignInButton(target, (session) => {
      auth.completeSignIn(session)
      onDone?.(session)
    }, (err) => undefined)
      .catch(() => undefined)
  }, [auth, googleEnabled, onDone])

  const apple = async () => {
    try {
      setPending(true)
      const session = await auth.signInAppleAccount()
      onDone?.(session)
    } catch {
      // The shared auth state owns the visible error.
    } finally {
      setPending(false)
    }
  }

  return (
    <section className={variant === "panel" ? "auth-panel" : "auth-panel inline-auth-panel"}>
      <h1>{t(variant === "panel" ? ui.signInTitle : ui.signInRequiredTitle)}</h1>
      <p>{t(variant === "panel" ? (googleEnabled ? ui.signInBodyWithGoogle : ui.signInBody) : ui.signInRequiredBody)}</p>
      <button className="primary-action" disabled={pending || auth.signingIn || auth.status === "checking"} onClick={() => void apple()}>
        {pending || auth.signingIn || auth.status === "checking" ? t(copy.actions.pending) : t(ui.signInApple)}
      </button>
      <p className="auth-note">{t(ui.signInApple2fa)}</p>
      {googleEnabled && <div className="google-auth-slot" ref={googleButtonRef} />}
      {auth.error && <p className="inline-error">{auth.error}</p>}
    </section>
  )
}

function authErrorMessage(error: unknown, t: (value: LocalizedText) => string): string {
  if (error instanceof AuthFlowError) {
    if (error.code === "apple_requires_2fa") return t(ui.signInApple2fa)
    if (error.code === "apple_not_configured") return t(ui.signInAppleNotConfigured)
    if (error.code === "apple_unavailable" || error.code === "apple_no_identity_token") return t(ui.signInAppleUnavailable)
    return t(ui.signInAppleFailed)
  }
  if (error instanceof Error && error.message && error.message !== "[object Object]") return error.message
  if (typeof error === "string" && error && error !== "[object Object]") return error
  return t(ui.signInAppleFailed)
}

function AuthModal({ onClose, onDone }: { onClose: () => void; onDone?: (session?: AuthSession) => void }) {
  const { t } = useAppState()
  const done = (session?: AuthSession) => {
    onDone?.(session)
    onClose()
  }
  return (
    <div className="auth-modal-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose()
    }}>
      <div className="auth-modal" role="dialog" aria-modal="true">
        <button type="button" className="auth-close" aria-label={t(ui.dismiss)} onClick={onClose}>×</button>
        <AuthPrompt onDone={done} />
      </div>
    </div>
  )
}

function ProfileSetupModal({ onClose, onDone }: { onClose: () => void; onDone?: (session: AuthSession) => void }) {
  const { t } = useAppState()
  const auth = useAuth()
  const [displayName, setDisplayName] = useState(auth.user?.displayName || "")
  const [pending, setPending] = useState(false)
  const [error, setError] = useState("")
  const normalized = displayName.trim().replace(/\s+/g, " ")
  const valid = [...normalized].length >= 2 && [...normalized].length <= 40
  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!valid) {
      setError(t(ui.profileNameRequired))
      return
    }
    setPending(true)
    setError("")
    try {
      const session = await updateMyProfile({ displayName: normalized })
      auth.completeSignIn(session)
      onDone?.(session)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : t(copy.errors.connection))
    } finally {
      setPending(false)
    }
  }
  return (
    <div className="auth-modal-backdrop" role="presentation">
      <form className="auth-modal profile-setup-modal" role="dialog" aria-modal="true" onSubmit={(event) => void submit(event)}>
        <button type="button" className="auth-close" aria-label={t(ui.dismiss)} onClick={onClose}>×</button>
        <h1>{t(ui.profileSetupTitle)}</h1>
        <p>{t(ui.profileSetupBody)}</p>
        <label className="profile-name-field">
          <span>{t(ui.profileNameLabel)}</span>
          <input
            autoFocus
            value={displayName}
            maxLength={48}
            onChange={(event) => setDisplayName(event.target.value)}
            placeholder={t(ui.profileNamePlaceholder)}
          />
        </label>
        <button className="primary-action" disabled={pending} type="submit">
          {pending ? t(copy.actions.pending) : t(ui.saveProfile)}
        </button>
        {error && <p className="inline-error">{error}</p>}
      </form>
    </div>
  )
}

function AdminGate() {
  const { t } = useAppState()
  const auth = useAuth()
  const [status, setStatus] = useState<"checking" | "allowed" | "denied">("checking")

  useEffect(() => {
    let cancelled = false
    if (auth.status !== "signed-in") {
      setStatus("checking")
      return () => {
        cancelled = true
      }
    }
    setStatus("checking")
    getMe()
      .then((me) => {
        if (!cancelled) setStatus(me.isAdmin ? "allowed" : "denied")
      })
      .catch((error) => {
        if (cancelled) return
        if (isAuthRequiredError(error)) {
          void auth.signOut()
          return
        }
        setStatus("denied")
      })
    return () => {
      cancelled = true
    }
  }, [auth])

  if (auth.status === "checking" || status === "checking") {
    return <main className="auth-stage"><section className="auth-panel"><p>{t(ui.checkingSession)}</p></section></main>
  }
  if (auth.status !== "signed-in") {
    return <main className="auth-stage"><AuthPrompt variant="panel" /></main>
  }
  if (status === "denied") {
    return <AdminAccessDeniedPage />
  }
  return <AdminPage />
}

function reducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case "mode":
      sessionStorage.setItem("joowfak.mode", action.mode)
      return { ...state, mode: action.mode, modeSelected: true }
    case "safety":
      sessionStorage.setItem("joowfak.safetyAccepted", action.accepted ? "true" : "false")
      return { ...state, safetyAccepted: action.accepted }
    case "topic":
      sessionStorage.setItem("joowfak.topic", action.topicText)
      return { ...state, topicText: action.topicText }
    case "request":
      sessionStorage.setItem("joowfak.requestId", action.requestId)
      return { ...state, requestId: action.requestId }
    case "clear-request":
      sessionStorage.removeItem("joowfak.requestId")
      return { ...state, requestId: undefined }
    case "matched":
      sessionStorage.setItem(storedMatchKey, JSON.stringify({ match: action.match, startedAt: Date.now() }))
      sessionStorage.removeItem("joowfak.requestId")
      return { ...state, match: action.match, restoredMatch: false, startedAt: Date.now(), requestId: undefined, reported: false, blocked: false }
    case "reported":
      return { ...state, reported: true }
    case "blocked":
      return { ...state, blocked: true }
    case "ended":
      sessionStorage.removeItem(storedMatchKey)
      sessionStorage.removeItem("joowfak.requestId")
      return {
        ...state,
        endedTopic: state.match?.topicLabel || state.topicText,
        endedSessionId: state.match?.sessionId,
        endedPeerToken: state.match?.peerToken,
        endedParticipantToken: state.match?.participantToken,
        endedMinutes: action.minutes,
        match: undefined,
        restoredMatch: false,
        startedAt: undefined,
        requestId: undefined,
      }
    case "reset-call":
      sessionStorage.removeItem(storedMatchKey)
      sessionStorage.removeItem("joowfak.requestId")
      return { ...state, match: undefined, restoredMatch: false, startedAt: undefined, requestId: undefined, reported: false, blocked: false }
    default:
      return state
  }
}

function loadInitialState(): AppState {
  const topicText = sessionStorage.getItem("joowfak.topic") || ""
  const requestId = sessionStorage.getItem("joowfak.requestId") || undefined
  const storedMode = sessionStorage.getItem("joowfak.mode")
  const safetyAccepted = sessionStorage.getItem("joowfak.safetyAccepted") === "true"
  const storedMatch = safeStoredMatch()
  return {
    ...fallbackState,
    mode: storedMode === "video" ? "video" : "voice",
    modeSelected: storedMode === "voice" || storedMode === "video",
    safetyAccepted,
    topicText,
    requestId,
    match: storedMatch?.match,
    restoredMatch: Boolean(storedMatch?.match),
    startedAt: storedMatch?.startedAt,
  }
}

function loadTweaks(): AppTweaks {
  const raw = window.localStorage.getItem(storedTweaksKey)
  if (!raw) return defaultTweaks
  try {
    const parsed = JSON.parse(raw) as Partial<AppTweaks>
    return {
      vibe: isVibe(parsed.vibe) ? parsed.vibe : defaultTweaks.vibe,
      energy: isEnergy(parsed.energy) ? parsed.energy : defaultTweaks.energy,
      matchingMotion: isMatchingMotion(parsed.matchingMotion) ? parsed.matchingMotion : defaultTweaks.matchingMotion,
      liveLayout: isLiveLayout(parsed.liveLayout) ? parsed.liveLayout : defaultTweaks.liveLayout,
    }
  } catch {
    window.localStorage.removeItem(storedTweaksKey)
    return defaultTweaks
  }
}

function loadShellPrefs(): ShellPrefs {
  const raw = window.localStorage.getItem(storedShellPrefsKey)
  if (!raw) return {}
  try {
    const parsed = JSON.parse(raw) as Partial<ShellPrefs>
    return {
      ...(isLocale(parsed.locale) ? { locale: parsed.locale } : {}),
      ...(isTheme(parsed.theme) ? { theme: parsed.theme } : {}),
    }
  } catch {
    window.localStorage.removeItem(storedShellPrefsKey)
    return {}
  }
}

function isVibe(value: unknown): value is Vibe {
  return value === "coral" || value === "violet" || value === "teal" || value === "sunset"
}

function isLocale(value: unknown): value is Locale {
  return value === "fa" || value === "en"
}

function isTheme(value: unknown): value is ThemeMode {
  return value === "light" || value === "dark"
}

function defaultDisplayPrefs(): Required<ShellPrefs> {
  return {
    locale: deviceLocale(),
    theme: deviceTheme(),
  }
}

function deviceLocale(): Locale {
  const languages = [navigator.language, ...(navigator.languages || [])].filter(Boolean)
  return languages.some((value) => value.toLowerCase().startsWith("fa")) ? "fa" : "en"
}

function deviceTheme(): ThemeMode {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light"
}

function isEnergy(value: unknown): value is Energy {
  return value === "calm" || value === "balanced" || value === "lively"
}

function isMatchingMotion(value: unknown): value is MatchingMotion {
  return value === "radar" || value === "constellation" || value === "pulse"
}

function isLiveLayout(value: unknown): value is LiveLayout {
  return value === "full" || value === "orbs" || value === "minimal"
}

function safeStoredMatch(): { match: MatchFound; startedAt: number } | null {
  const raw = sessionStorage.getItem(storedMatchKey)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as { match?: MatchFound; startedAt?: number }
    if (parsed.match?.sessionId && (parsed.match.peerToken || parsed.match.participantToken)) {
      return { match: parsed.match, startedAt: parsed.startedAt || Date.now() }
    }
  } catch {
    sessionStorage.removeItem(storedMatchKey)
  }
  return null
}

function activeMatchRoomState(match?: MatchFound): RoomState | null {
  if (!match) return null
  return {
    sessionId: match.sessionId,
    attendeeCount: match.attendeeCount || match.participants?.filter((participant) => participant.active).length || 2,
    participants: match.participants || [],
    liveness: match.liveness || { remainingSeconds: 0, momentum: "steady" },
    vote: match.vote,
  }
}

function mergeRoomParticipantMetadata(next: RoomState, previous: RoomState | null, seed: RoomParticipant[] = []): RoomState {
  const known = new Map<string, RoomParticipant>()
  for (const participant of [...(seed || []), ...(previous?.participants || [])]) {
    if (!known.has(participant.id)) known.set(participant.id, participant)
  }
  const participants = next.participants.map((participant) => {
    const prior = known.get(participant.id)
    return {
      ...participant,
      displayName: participant.displayName?.trim() || prior?.displayName?.trim() || undefined,
      avatarUrl: participant.avatarUrl || prior?.avatarUrl || undefined,
      avatarPreset: participant.avatarPreset || prior?.avatarPreset || undefined,
      publicCode: participant.publicCode || prior?.publicCode || undefined,
      topicLabel: participant.topicLabel || prior?.topicLabel || undefined,
      topicKeywords: participant.topicKeywords || prior?.topicKeywords || undefined,
    }
  })
  return { ...next, participants }
}

function ScrollReset() {
  const location = useLocation()
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 })
  }, [location.pathname])
  return null
}

function useAppState() {
  const ctx = useContext(AppContext)
  if (!ctx) throw new Error("AppContext missing")
  return ctx
}

function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error("AuthContext missing")
  return ctx
}

function useNetworkQuality(connectionState: ConnectionState, t: (value: LocalizedText) => string): { level: NetworkQualityLevel; label: string } {
  const [snapshot, setSnapshot] = useState<NetworkSnapshot>(() => readNetworkSnapshot())

  useEffect(() => {
    const connection = networkConnection()
    if (!connection) return undefined
    const update = () => setSnapshot(readNetworkSnapshot())
    connection.addEventListener?.("change", update)
    const timer = window.setInterval(update, 5000)
    return () => {
      connection.removeEventListener?.("change", update)
      window.clearInterval(timer)
    }
  }, [])

  const level = classifyNetworkQuality(snapshot, connectionState)
  return {
    level,
    label: level === "good" ? t(ui.networkGood) : level === "fair" ? t(ui.networkFair) : level === "poor" ? t(ui.networkPoor) : t(ui.networkUnknown),
  }
}

function networkConnection(): NetworkConnectionLike | null {
  const nav = navigator as Navigator & { connection?: NetworkConnectionLike; mozConnection?: NetworkConnectionLike; webkitConnection?: NetworkConnectionLike }
  return nav.connection || nav.mozConnection || nav.webkitConnection || null
}

function readNetworkSnapshot(): NetworkSnapshot {
  const connection = typeof navigator === "undefined" ? null : networkConnection()
  return {
    downlink: connection?.downlink,
    effectiveType: connection?.effectiveType,
    rtt: connection?.rtt,
  }
}

function classifyNetworkQuality(snapshot: NetworkSnapshot, connectionState: ConnectionState): NetworkQualityLevel {
  if (connectionState === "failed" || connectionState === "unavailable") return "poor"
  if (connectionState === "connecting" || connectionState === "reconnecting") return "fair"
  const effectiveType = String(snapshot.effectiveType || "").toLowerCase()
  if (effectiveType === "slow-2g" || effectiveType === "2g") return "poor"
  if (effectiveType === "3g") return "fair"
  if (typeof snapshot.downlink === "number") {
    if (snapshot.downlink < 0.7) return "poor"
    if (snapshot.downlink < 2) return "fair"
    return "good"
  }
  if (typeof snapshot.rtt === "number") {
    if (snapshot.rtt > 600) return "poor"
    if (snapshot.rtt > 250) return "fair"
    return "good"
  }
  // iOS WebViews commonly do not expose Network Information. A connected
  // realtime transport is still a useful signal, so avoid showing a dead-end
  // "unknown" label when the call itself is healthy.
  return connectionState === "connected" ? "good" : "fair"
}

function getSpeechRecognition(): SpeechRecognitionCtor | undefined {
  const speechWindow = window as Window & {
    SpeechRecognition?: SpeechRecognitionCtor
    webkitSpeechRecognition?: SpeechRecognitionCtor
  }
  return speechWindow.SpeechRecognition || speechWindow.webkitSpeechRecognition
}

function canUseTopicDictation(): boolean {
  return Boolean(getSpeechRecognition() && navigator.mediaDevices?.getUserMedia)
}

function isMicPermissionDenied(error: unknown): boolean {
  const name = typeof error === "object" && error && "name" in error ? String((error as { name?: string }).name) : ""
  return name === "NotAllowedError" || name === "SecurityError" || name === "PermissionDeniedError"
}

async function requestDictationMicPermission(): Promise<void> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error("microphone media devices are unavailable")
  }
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
  stopMediaStream(stream)
}

function useTopicDictation(onTranscript: (transcript: string) => void) {
  const [isSupported, setIsSupported] = useState(false)
  const [dictationState, setDictationState] = useState<DictationState>("idle")
  const [dictationError, setDictationError] = useState<DictationError>("")

  useEffect(() => {
    setIsSupported(canUseTopicDictation())
  }, [])

  const startDictation = useCallback(async () => {
    if (dictationState !== "idle") return
    const Recognition = getSpeechRecognition()
    if (!Recognition || !navigator.mediaDevices?.getUserMedia) {
      setIsSupported(false)
      setDictationError("unavailable")
      return
    }

    setDictationError("")
    setDictationState("requesting")
    try {
      await requestDictationMicPermission()
    } catch (error) {
      setDictationState("idle")
      setDictationError(isMicPermissionDenied(error) ? "denied" : "unavailable")
      return
    }

    try {
      const recognition = new Recognition()
      recognition.lang = "fa-IR"
      recognition.interimResults = false
      recognition.continuous = false
      recognition.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map((result) => result[0]?.transcript || "")
          .join(" ")
          .trim()
        if (transcript) onTranscript(transcript)
      }
      recognition.onerror = (event) => {
        setDictationState("idle")
        setDictationError(event?.error === "not-allowed" || event?.error === "service-not-allowed" ? "denied" : "unavailable")
      }
      recognition.onend = () => setDictationState("idle")
      setDictationState("listening")
      recognition.start()
    } catch {
      setDictationState("idle")
      setIsSupported(false)
      setDictationError("unavailable")
    }
  }, [dictationState, onTranscript])

  return { dictationState, dictationError, isSupported, startDictation }
}

function appendTranscript(value: string, transcript: string): string {
  return [value, transcript].filter(Boolean).join(" ").slice(0, MAX_TOPIC_LENGTH + 20)
}

function startDisabledReason(
  topicText: string,
  modeSelected: boolean,
  safetyAccepted: boolean,
  tooLong: boolean,
  authStatus: AuthStatus,
  t: (value: LocalizedText) => string,
): string {
  if (authStatus === "checking") return t(ui.checkingSession)
  if (tooLong) return t(ui.startTopicTooLong)
  if (!topicText.trim()) return t(ui.topicRequired)
  if (!modeSelected) return t(ui.startNeedsMode)
  if (!safetyAccepted) return t(ui.startNeedsSafety)
  return ""
}

function HomePage() {
  const { state, dispatch, locale, t, f } = useAppState()
  const auth = useAuth()
  const [availability, setAvailability] = useState(t(copy.home.online_count))
  const [draft, setDraft] = useState(state.topicText)
  const [selectedChip, setSelectedChip] = useState("")
  const [showSignIn, setShowSignIn] = useState(false)
  const [showOnboarding, setShowOnboarding] = useState(() => window.localStorage.getItem(seenOnboardingKey) !== "true")
  const addDictatedDraft = useCallback((transcript: string) => {
    setDraft((value) => appendTranscript(value, transcript))
    setSelectedChip("")
  }, [])
  const { dictationState, dictationError, isSupported: isDictationSupported, startDictation } = useTopicDictation(addDictatedDraft)
  const tooLong = [...draft].length > MAX_TOPIC_LENGTH
  const canStart = state.modeSelected && state.safetyAccepted && !tooLong && draft.trim() !== ""
  const disabledReason = startDisabledReason(draft, state.modeSelected, state.safetyAccepted, tooLong, auth.status, t)
  const navigate = useNavigate()
  const isDictationActive = dictationState === "requesting" || dictationState === "listening"
  const voiceInputLabel = isDictationActive ? t(copy.topic.dictation_on) : t(copy.topic.dictation)
  const dictationFeedback = dictationState === "requesting"
    ? t(copy.topic.dictation_permission)
    : dictationState === "listening"
    ? t(copy.topic.dictation_on)
    : (dictationError ? (dictationError === "denied" ? t(copy.errors.mic_denied) : t(copy.topic.voice_input_unavailable)) : "")

  useEffect(() => {
    let cancelled = false
    getAvailability()
      .then((result) => {
        if (cancelled) return
        const waiting = state.mode === "video" ? result.videoWaiting : result.voiceWaiting
        setAvailability(f(copy.home.availability_count, { waiting: formatNumber(waiting, locale), active: formatNumber(result.activeSessions, locale) }))
      })
      .catch(() => setAvailability(t(copy.home.availability_fallback)))
    return () => {
      cancelled = true
    }
  }, [f, locale, state.mode, t])

  const startMatching = useCallback(() => {
    window.localStorage.setItem(seenOnboardingKey, "true")
    setShowOnboarding(false)
    dispatch({ type: "topic", topicText: draft.trim() })
    navigate("/matching")
  }, [dispatch, draft, navigate])

  const submit = () => {
    if (!canStart) return
    if (auth.status !== "signed-in") {
      setShowSignIn(true)
      return
    }
    startMatching()
  }
  const afterSignIn = () => {
    startMatching()
  }
  const handleTopicKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter" || event.nativeEvent.isComposing) return
    event.preventDefault()
    submit()
  }

  return (
    <main className="client-stage">
      <section className="client-home-frame" aria-label={t(copy.brand.name)}>
        <div className="client-home-main">
          <ClientHeader />
          <div className="client-mobile-hero">
            <div className="mobile-online">{availability}</div>
            <h1>{t(copy.home.hero)}</h1>
            <p>{t(copy.home.hero_sub)}</p>
          </div>
          <div className="desktop-intent">
            <span className="warm-pill"><BoltIcon aria-hidden="true" />{t(ui.liveTopicRoom)}</span>
            <h1>{t(copy.home.hero)}</h1>
            <p>{t(copy.home.hero_sub)}</p>
            <label className="desktop-topic-box">
              <SearchIcon className="search-dot" aria-hidden="true" />
              <input value={draft} maxLength={MAX_TOPIC_LENGTH + 20} onChange={(event) => { setDraft(event.target.value); setSelectedChip("") }} onKeyDown={handleTopicKeyDown} placeholder={t(copy.topic.input_ph)} />
              {isDictationSupported && (
                <button
                  type="button"
                  className={[
                    "mic-square",
                    isDictationActive ? "listening" : "",
                  ].filter(Boolean).join(" ")}
                  aria-label={voiceInputLabel}
                  title={voiceInputLabel}
                  onClick={() => void startDictation()}
                >
                  <MicIcon aria-hidden="true" />
                </button>
              )}
            </label>
            {dictationFeedback && (
              <p className={dictationError ? "dictation-status dictation-error desktop-dictation-status" : "dictation-status desktop-dictation-status"}>{dictationFeedback}</p>
            )}
            <div className={tooLong ? "topic-count invalid desktop-topic-count" : "topic-count desktop-topic-count"}>{formatNumber([...draft].length, locale)}/{formatNumber(MAX_TOPIC_LENGTH, locale)}</div>
            <TopicChips value={draft} activeValue={selectedChip} onChange={setDraft} onSelect={setSelectedChip} counts />
            <div className="desktop-actions">
              <ModeSwitch value={state.mode} active={state.modeSelected} onChange={(mode) => dispatch({ type: "mode", mode })} />
              <button className="primary-action desktop-home-cta" disabled={!canStart || auth.status === "checking"} title={disabledReason} onClick={submit}>
                <BoltIcon aria-hidden="true" />
                {auth.status === "signed-in" ? t(copy.home.cta_start) : t(ui.signInToStart)}
              </button>
            </div>
            {showOnboarding && <p className="onboarding-microcopy">{t(ui.onboardingMicrocopy)}</p>}
            {disabledReason && <p className="start-disabled-hint">{disabledReason}</p>}
            <StartRequirements
              modeSelected={state.modeSelected}
              safetyAccepted={state.safetyAccepted}
              tooLong={tooLong}
              onSafetyChange={(accepted) => dispatch({ type: "safety", accepted })}
            />
            <div className="privacy-line"><EyeOffIcon aria-hidden="true" />{t(copy.home.trust)}</div>
          </div>
          <div className="mobile-only-stack">
            <label className="topic-search mobile-home-search">
              <SearchIcon className="search-dot" aria-hidden="true" />
              <input value={draft} maxLength={MAX_TOPIC_LENGTH + 20} onChange={(event) => { setDraft(event.target.value); setSelectedChip("") }} onKeyDown={handleTopicKeyDown} placeholder={t(copy.topic.input_ph)} />
              {isDictationSupported && (
                <button
                  type="button"
                  className={[
                    "topic-mic",
                    isDictationActive ? "listening" : "",
                  ].filter(Boolean).join(" ")}
                  aria-label={voiceInputLabel}
                  title={voiceInputLabel}
                  onClick={() => void startDictation()}
                >
                  <MicIcon aria-hidden="true" />
                </button>
              )}
            </label>
            {dictationFeedback && (
              <p className={dictationError ? "dictation-status dictation-error" : "dictation-status"}>{dictationFeedback}</p>
            )}
            <div className={tooLong ? "topic-count invalid" : "topic-count"}>{formatNumber([...draft].length, locale)}/{formatNumber(MAX_TOPIC_LENGTH, locale)}</div>
            <div className="chip-heading">{t(copy.topic.hot_label)}</div>
            <TopicChips value={draft} activeValue={selectedChip} onChange={setDraft} onSelect={setSelectedChip} counts />
            <p className="optional-note">{t(copy.topic.optional_note)}</p>
            <ModeSwitch value={state.mode} active={state.modeSelected} onChange={(mode) => dispatch({ type: "mode", mode })} />
            <StartRequirements
              modeSelected={state.modeSelected}
              safetyAccepted={state.safetyAccepted}
              tooLong={tooLong}
              onSafetyChange={(accepted) => dispatch({ type: "safety", accepted })}
            />
            {showOnboarding && <p className="onboarding-microcopy">{t(ui.onboardingMicrocopy)}</p>}
            <div className="trust-strip"><EyeOffIcon aria-hidden="true" />{t(copy.home.trust)}</div>
            <button className="primary-action mobile-home-cta" disabled={!canStart || auth.status === "checking"} title={disabledReason} onClick={submit}>
              <BoltIcon aria-hidden="true" />
              {auth.status === "signed-in" ? t(copy.home.cta_start) : t(ui.signInToStart)}
            </button>
            {disabledReason && <p className="start-disabled-hint">{disabledReason}</p>}
          </div>
        </div>
        <div className="client-home-visual">
          <div className="online-badge"><span />{availability}</div>
          <div className="aura-visual"><BoltIcon aria-hidden="true" /></div>
          <span className="float-topic topic-a">{text(copy.topic.chips[0], locale)}</span>
          <span className="float-topic topic-b">{text(copy.topic.chips[1], locale)}</span>
          <span className="float-topic topic-c">{text(copy.topic.chips[2], locale)}</span>
          <div className="ai-badge">{t(ui.aiFinds)}</div>
        </div>
        {showSignIn && <AuthModal onClose={() => setShowSignIn(false)} onDone={afterSignIn} />}
      </section>
    </main>
  )
}

function TopicPage() {
  const { state, dispatch, locale, t } = useAppState()
  const auth = useAuth()
  const [textValue, setTextValue] = useState(state.topicText)
  const [selectedChip, setSelectedChip] = useState("")
  const [showSignIn, setShowSignIn] = useState(false)
  const [showOnboarding, setShowOnboarding] = useState(() => window.localStorage.getItem(seenOnboardingKey) !== "true")
  const addDictatedText = useCallback((transcript: string) => {
    setTextValue((value) => appendTranscript(value, transcript))
    setSelectedChip("")
  }, [])
  const { dictationState, dictationError, isSupported: isDictationSupported, startDictation } = useTopicDictation(addDictatedText)
  const tooLong = [...textValue].length > MAX_TOPIC_LENGTH
  const canStart = state.modeSelected && state.safetyAccepted && !tooLong && textValue.trim() !== ""
  const disabledReason = startDisabledReason(textValue, state.modeSelected, state.safetyAccepted, tooLong, auth.status, t)
  const navigate = useNavigate()
  const isDictationActive = dictationState === "requesting" || dictationState === "listening"
  const voiceInputLabel = isDictationActive ? t(copy.topic.dictation_on) : t(copy.topic.dictation)
  const dictationFeedback = dictationState === "requesting"
    ? t(copy.topic.dictation_permission)
    : dictationState === "listening"
    ? t(copy.topic.dictation_on)
    : (dictationError ? (dictationError === "denied" ? t(copy.errors.mic_denied) : t(copy.topic.voice_input_unavailable)) : "")
  const startMatching = useCallback(() => {
    window.localStorage.setItem(seenOnboardingKey, "true")
    setShowOnboarding(false)
    dispatch({ type: "topic", topicText: textValue.trim() })
    navigate("/matching")
  }, [dispatch, navigate, textValue])
  const submit = () => {
    if (!canStart) return
    if (auth.status !== "signed-in") {
      setShowSignIn(true)
      return
    }
    startMatching()
  }
  const afterSignIn = () => {
    startMatching()
  }
  const handleTopicKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter" || event.nativeEvent.isComposing) return
    event.preventDefault()
    submit()
  }

  return (
    <main className="mobile-stage light">
      <section className="phone-frame topic-frame">
        <MobileTopBar title={t(copy.topic.title)} backTo="/" />
        <div className="topic-content">
          <h1>{t(copy.topic.heading)}</h1>
          <span className="ai-chip">{t(copy.topic.ai_chip)}</span>
          <label className="topic-search">
            <SearchIcon className="search-dot" aria-hidden="true" />
            <input value={textValue} maxLength={MAX_TOPIC_LENGTH + 20} onChange={(event) => { setTextValue(event.target.value); setSelectedChip("") }} onKeyDown={handleTopicKeyDown} placeholder={t(copy.topic.input_ph)} />
            {isDictationSupported && (
              <button
                type="button"
                className={[
                  "topic-mic",
                  isDictationActive ? "listening" : "",
                ].filter(Boolean).join(" ")}
                aria-label={voiceInputLabel}
                title={voiceInputLabel}
                onClick={() => void startDictation()}
              >
                <MicIcon aria-hidden="true" />
              </button>
            )}
          </label>
          {dictationFeedback && (
            <p className={dictationError ? "dictation-status dictation-error" : "dictation-status"}>{dictationFeedback}</p>
          )}
          <div className={tooLong ? "topic-count invalid" : "topic-count"}>{formatNumber([...textValue].length, locale)}/{formatNumber(MAX_TOPIC_LENGTH, locale)}</div>
          <div className="chip-heading">{t(copy.topic.hot_label)}</div>
          <TopicChips value={textValue} activeValue={selectedChip} onChange={setTextValue} onSelect={setSelectedChip} counts />
          <p className="optional-note">{t(copy.topic.optional_note)}</p>
          <div className="topic-mode-gate">
            <ModeSwitch value={state.mode} active={state.modeSelected} onChange={(mode) => dispatch({ type: "mode", mode })} />
          </div>
          <StartRequirements
            modeSelected={state.modeSelected}
            safetyAccepted={state.safetyAccepted}
            tooLong={tooLong}
            onSafetyChange={(accepted) => dispatch({ type: "safety", accepted })}
          />
          {showOnboarding && <p className="onboarding-microcopy">{t(ui.onboardingMicrocopy)}</p>}
        </div>
        <div className="bottom-cta">
          <button className="primary-action" disabled={!canStart || auth.status === "checking"} title={disabledReason} onClick={submit}>
            <BoltIcon aria-hidden="true" />
            {auth.status === "signed-in" ? t(copy.topic.cta_find) : t(ui.signInToStart)}
          </button>
          {disabledReason && <p className="start-disabled-hint">{disabledReason}</p>}
        </div>
        {showSignIn && <AuthModal onClose={() => setShowSignIn(false)} onDone={afterSignIn} />}
      </section>
    </main>
  )
}

function StartRequirements({ modeSelected, safetyAccepted, tooLong, onSafetyChange }: {
  modeSelected: boolean
  safetyAccepted: boolean
  tooLong: boolean
  onSafetyChange: (accepted: boolean) => void
}) {
  const { t } = useAppState()
  return (
    <div className="start-requirements">
      <label className="safety-consent">
        <input type="checkbox" checked={safetyAccepted} onChange={(event) => onSafetyChange(event.target.checked)} />
        <span>{t(copy.safety.consent)}</span>
      </label>
      {!modeSelected && <p className="inline-error">{t(ui.modeRequired)}</p>}
      {!safetyAccepted && <p className="inline-error">{t(copy.safety.consent_required)}</p>}
      {tooLong && <p className="inline-error">{t(copy.topic.too_long)}</p>}
    </div>
  )
}

function MatchingPage() {
  const { state, dispatch, tweaks, t } = useAppState()
  const [status, setStatus] = useState<"searching" | "found" | "room_preview" | "error" | "blocked">("searching")
  const [roomPreview, setRoomPreview] = useState<RoomPreview | null>(null)
  const [invitedMatch, setInvitedMatch] = useState<MatchFound | null>(null)
  const [error, setError] = useState("")
  const [claiming, setClaiming] = useState(false)
  const [claimSent, setClaimSent] = useState(false)
  const [claimError, setClaimError] = useState("")
  const [cancelling, setCancelling] = useState(false)
  const [searchSeconds, setSearchSeconds] = useState(0)
  const [searchNonce, setSearchNonce] = useState(0)
  const navigate = useNavigate()

  useEffect(() => {
    if (status !== "searching") {
      setSearchSeconds(0)
      return undefined
    }
    const startedAt = Date.now()
    const timer = window.setInterval(() => setSearchSeconds(Math.floor((Date.now() - startedAt) / 1000)), 1000)
    return () => window.clearInterval(timer)
  }, [status])

  useEffect(() => {
    if (state.match) {
      setStatus("found")
      const timeout = window.setTimeout(() => navigate("/call"), 520)
      return () => window.clearTimeout(timeout)
    }

    let unsubscribe = () => {}
    let cancelled = false
    const finishMatch = (match: MatchFound) => {
      if (cancelled) return
      vibrateMatchFound()
      if (!isAppVisible()) void notifyMatchFound(t(ui.matchAlertTitle), t(ui.matchAlertBody))
      setInvitedMatch(match)
      setStatus("found")
    }
    const showRoomPreview = (preview: RoomPreview) => {
      if (cancelled) return
      setRoomPreview(preview)
      setStatus("room_preview")
    }
    const listenForMatch = (requestId: string) => {
      unsubscribe = subscribeMatchEvents(requestId, (event) => {
        if (event.type === "found" && event.match) {
          finishMatch(event.match)
        }
        if (event.type === "room_preview" && event.roomPreview) {
          showRoomPreview(event.roomPreview)
        }
        if (event.type === "error") {
          setStatus("error")
          setError(event.error || t(copy.errors.connection))
        }
      })
    }
    const createFreshRequest = async () => {
      const result = await createMatchRequest({ topicText: state.topicText, mode: state.mode })
      if (cancelled) return
      dispatch({ type: "request", requestId: result.requestId })
      if (result.match) {
        finishMatch(result.match)
        return
      }
      if (result.roomPreview) {
        showRoomPreview(result.roomPreview)
        return
      }
      listenForMatch(result.requestId)
    }

    setStatus("searching")
    setRoomPreview(null)
    setInvitedMatch(null)
    setError("")
    setClaimSent(false)
    setClaimError("")
    void (async () => {
        if (cancelled) return
        void requestMatchAlertPermission()

        if (state.requestId) {
          try {
            const current = await getMatchRequestStatus(state.requestId)
            if (cancelled) return
            if (current.status === "found" && current.match) {
              finishMatch(current.match)
              return
            }
            if (current.status === "room_preview" && current.roomPreview) {
              showRoomPreview(current.roomPreview)
              return
            }
            if (current.status === "searching") {
              listenForMatch(current.requestId)
              return
            }
          } catch {
            if (cancelled) return
          }
          dispatch({ type: "clear-request" })
          return
        }

        await createFreshRequest()
      })()
      .catch((err: Error) => {
        if (cancelled) return
        if (err instanceof ApiError && err.code === "user_moderation_blocked") {
          setStatus("blocked")
          setError(t(ui.accountBlockedBody))
          dispatch({ type: "clear-request" })
          return
        }
        setStatus("error")
        setError(err.message)
      })
    return () => {
      cancelled = true
      unsubscribe()
    }
  }, [dispatch, navigate, searchNonce, state.match, state.mode, state.requestId, state.topicText, t])

  const cancel = async () => {
    setCancelling(true)
    try {
      if (state.requestId) await cancelMatchRequest(state.requestId)
      dispatch({ type: "reset-call" })
      navigate("/")
    } catch (err) {
      setStatus("error")
      setError(err instanceof Error ? err.message : t(copy.errors.connection))
    } finally {
      setCancelling(false)
    }
  }

  const joinPreview = async () => {
    if (!state.requestId || !roomPreview) return
    setCancelling(true)
    setError("")
    try {
      await ensureMediaPermission(state.mode)
      const match = await joinRoomCandidate(state.requestId, roomPreview.candidateId)
      setStatus("found")
      dispatch({ type: "matched", match })
    } catch (err) {
      setStatus("error")
      setError(err instanceof Error ? err.message : t(copy.errors.connection))
    } finally {
      setCancelling(false)
    }
  }

  const joinInvitedMatch = async () => {
    if (!invitedMatch) return
    setCancelling(true)
    setError("")
    try {
      await ensureMediaPermission(state.mode)
      dispatch({ type: "matched", match: invitedMatch })
    } catch (err) {
      setStatus("error")
      setError(state.mode === "video" ? t(copy.errors.camera_denied) : (err instanceof Error ? err.message : t(copy.errors.connection)))
    } finally {
      setCancelling(false)
    }
  }

  const skipInvitedMatch = async () => {
    if (!invitedMatch) return
    setCancelling(true)
    setError("")
    try {
      if (invitedMatch.participantToken) await leaveSessionParticipant(invitedMatch.sessionId, invitedMatch.participantToken, "declined")
      else await endSession(invitedMatch.sessionId)
      setInvitedMatch(null)
      dispatch({ type: "clear-request" })
      setStatus("searching")
      setSearchNonce((value) => value + 1)
    } catch (err) {
      setStatus("error")
      setError(err instanceof Error ? err.message : t(copy.errors.connection))
    } finally {
      setCancelling(false)
    }
  }

  const skipPreview = async () => {
    if (!state.requestId || !roomPreview) return
    setCancelling(true)
    setError("")
    try {
      const next = await skipRoomCandidate(state.requestId, roomPreview.candidateId)
      if (next.status === "room_preview" && next.roomPreview) {
        setRoomPreview(next.roomPreview)
        setStatus("room_preview")
      } else {
        setRoomPreview(null)
        setStatus("searching")
        let stop = () => {}
        stop = subscribeMatchEvents(state.requestId, (event) => {
          if (event.type === "found" && event.match) {
            stop()
            setStatus("found")
            setInvitedMatch(event.match)
          }
          if (event.type === "room_preview" && event.roomPreview) {
            stop()
            setRoomPreview(event.roomPreview)
            setStatus("room_preview")
          }
          if (event.type === "error") {
            stop()
            setStatus("error")
            setError(event.error || t(copy.errors.connection))
          }
        })
      }
    } catch (err) {
      setStatus("error")
      setError(err instanceof Error ? err.message : t(copy.errors.connection))
    } finally {
      setCancelling(false)
    }
  }

  const submitUnblockClaim = async () => {
    setClaiming(true)
    setClaimError("")
    try {
      await requestUnblockClaim()
      setClaimSent(true)
    } catch {
      setClaimError(t(ui.unblockClaimFailed))
    } finally {
      setClaiming(false)
    }
  }

  const quality = matchingQualityCopy(searchSeconds, state.topicText, t)
  return (
    <main className="matching-stage">
      <section className={`matching-frame motion-${tweaks.matchingMotion}`}>
        <header className="dark-header">
          <LogoMark />
          <span>{t(copy.brand.name)}</span>
          <span className="topic-pill">{state.topicText ? `${t(ui.topicLabel)}: ${state.topicText}` : t(ui.topicPending)}</span>
        </header>
        <div className="matching-center">
          <MatchingVisual motion={tweaks.matchingMotion} status={status} />
          <h1>{status === "found" ? t(ui.roomPreviewTitle) : status === "room_preview" ? t(ui.roomPreviewTitle) : status === "blocked" ? t(ui.accountBlockedTitle) : t(copy.matching.searching)}</h1>
          <p>{status === "error" || status === "blocked" ? error : status === "room_preview" || status === "found" ? t(ui.roomPreviewBody) : t(copy.matching.stats)}</p>
          {status === "blocked" ? (
            <div className="blocked-account-panel">
              <BanIcon aria-hidden="true" />
              <span>{claimSent ? t(ui.unblockClaimSent) : t(ui.unblockClaimUserHint)}</span>
              {claimError && <strong>{claimError}</strong>}
              <div className="blocked-account-actions">
                <button className="primary-action" disabled={claiming || claimSent} onClick={() => void submitUnblockClaim()}>
                  {claiming ? t(copy.actions.pending) : claimSent ? t(ui.claimReviewRequested) : t(ui.requestUnblockReview)}
                </button>
                <button className="ghost-action" onClick={() => {
                  dispatch({ type: "reset-call" })
                  navigate("/")
                }}>{t(copy.matching.cancel)}</button>
              </div>
            </div>
          ) : status === "found" && invitedMatch ? (
            <RoomInvitationCard match={invitedMatch} pending={cancelling} onJoin={() => void joinInvitedMatch()} onSkip={() => void skipInvitedMatch()} />
          ) : status === "room_preview" && roomPreview ? (
            <RoomPreviewCard preview={roomPreview} pending={cancelling} onJoin={() => void joinPreview()} onSkip={() => void skipPreview()} />
          ) : (
            <>
              {status === "searching" && <div className="matching-quality"><BoltIcon aria-hidden="true" />{quality}</div>}
              <div className="ai-match-line"><BoltIcon aria-hidden="true" />{t(copy.matching.ai_line)}</div>
              <button className="ghost-action" disabled={cancelling} onClick={() => void cancel()}>
                {cancelling ? t(copy.actions.pending) : t(copy.matching.cancel)}
              </button>
            </>
          )}
        </div>
      </section>
    </main>
  )
}

function RoomPreviewCard({ preview, pending, onJoin, onSkip }: { preview: RoomPreview; pending: boolean; onJoin: () => void; onSkip: () => void }) {
  const { locale, t } = useAppState()
  const remaining = formatRoomLiveness(preview.livenessRemainingSeconds, locale)
  return (
    <div className="room-preview-card">
      <div className="room-preview-facts">
        <span>{topicTextOrAny(preview.topicLabel, t)}</span>
        <b>{formatNumber(preview.attendeeCount, locale)} {t(ui.attendees)}</b>
      </div>
      <div className={`room-liveness ${preview.momentum}`}>
        <BoltIcon aria-hidden="true" />
        <span>{remaining}</span>
        <small>{momentumLabel(preview.momentum, t)}</small>
      </div>
      <div className="room-preview-actions">
        <button className="primary-action" disabled={pending} onClick={onJoin}>{pending ? t(copy.actions.pending) : t(ui.joinRoom)}</button>
        <button className="ghost-action" disabled={pending} onClick={onSkip}>{t(ui.nextRoom)}</button>
      </div>
    </div>
  )
}

function RoomInvitationCard({ match, pending, onJoin, onSkip }: { match: MatchFound; pending: boolean; onJoin: () => void; onSkip: () => void }) {
  const { locale, t } = useAppState()
  const participants = match.participants?.filter((participant) => participant.active) || []
  const score = match.matchScore ? toPercent(match.matchScore, locale) : ""
  return (
    <div className="room-preview-card room-invitation-card">
      <div className="room-preview-facts">
        <span>{topicTextOrAny(match.topicLabel, t)}</span>
        <b>{formatNumber(match.attendeeCount || participants.length || 2, locale)} {t(ui.attendees)}</b>
      </div>
      {score && <div className="room-liveness steady"><BoltIcon aria-hidden="true" /><span>{score}</span><small>{t(copy.live.match_score).replace("{score}", score)}</small></div>}
      <div className="room-invitation-participants" aria-label={t(ui.roomParticipants)}>
        {participants.map((participant) => (
          <div key={participant.id} className={participant.id === match.selfParticipantId ? "self" : ""}>
            <span className="room-invitation-avatar">
			  <AvatarFace presetId={participant.avatarPreset} />
            </span>
            <strong>{participantDisplayName(participant, t)}</strong>
            <small>{participantPublicCode(participant)} · {participantTopicText(participant, t)}</small>
          </div>
        ))}
      </div>
      <div className="room-preview-actions">
        <button className="primary-action" disabled={pending} onClick={onJoin}>{pending ? t(copy.actions.pending) : t(ui.joinRoom)}</button>
        <button className="ghost-action" disabled={pending} onClick={onSkip}>{t(ui.nextRoom)}</button>
      </div>
    </div>
  )
}

function MatchingVisual({ motion, status }: { motion: MatchingMotion; status: "searching" | "found" | "room_preview" | "error" | "blocked" }) {
  if (motion === "constellation") {
    return (
      <div className={status === "found" ? "constellation-match found" : "constellation-match"} aria-hidden="true">
        <svg viewBox="0 0 230 230">
          <line x1="115" y1="118" x2="60" y2="55" />
          <line x1="115" y1="118" x2="180" y2="70" />
          <line x1="115" y1="118" x2="50" y2="160" />
          <line className="active" x1="115" y1="118" x2="175" y2="170" />
        </svg>
        <span className="node one" />
        <span className="node two" />
        <span className="node three" />
        <span className="node found-node" />
        <div className="constellation-avatar" />
      </div>
    )
  }
  if (motion === "pulse") {
    return (
      <div className="pulse-match" aria-hidden="true">
        <span className="pulse-ring one" />
        <span className="pulse-ring two" />
        <span className="pulse-ring three" />
        <div className="pulse-core"><BoltIcon aria-hidden="true" /></div>
      </div>
    )
  }
  return (
    <div className={status === "found" ? "radar found" : "radar"} aria-hidden="true">
      <div className="radar-ring outer" />
      <div className="radar-ring mid" />
      <div className="radar-ring inner" />
      <div className="radar-sweep" />
      <span className="radar-dot primary" />
      <span className="radar-dot secondary" />
      <span className="radar-dot third" />
      <div className="radar-avatar" />
    </div>
  )
}

function CallPage() {
  const { state, dispatch, locale, tweaks, t, f } = useAppState()
  const navigate = useNavigate()
  const [mic, setMic] = useState(true)
  const [camera, setCamera] = useState(state.mode === "video")
  const [seconds, setSeconds] = useState(0)
  const [connectionState, setConnectionState] = useState<ConnectionState>("connecting")
  const [connectionProvider, setConnectionProvider] = useState<RealtimeConnection["provider"] | "">("")
  const [actionError, setActionError] = useState("")
  const [pendingAction, setPendingAction] = useState<"" | "report" | "end" | "next">("")
  const [showReport, setShowReport] = useState(false)
  const [leavingCall, setLeavingCall] = useState(false)
  const [localStream, setLocalStream] = useState<MediaStream | null>(null)
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null)
  const [remoteParticipants, setRemoteParticipants] = useState<RemoteMediaParticipant[]>([])
  const [remotePeerCount, setRemotePeerCount] = useState<number | null>(null)
  const [hadRemotePresence, setHadRemotePresence] = useState(false)
  const [peerLeftCountdown, setPeerLeftCountdown] = useState(0)
  const [roomState, setRoomState] = useState<RoomState | null>(activeMatchRoomState(state.match))
  const [roomBusy, setRoomBusy] = useState<"" | "vote" | "yes" | "no" | "cancel">("")
  const [roomNotice, setRoomNotice] = useState("")
  const [chromeVisible, setChromeVisible] = useState(true)
  const [chromeNudge, setChromeNudge] = useState(0)
  const [stripOverlayOpen, setStripOverlayOpen] = useState(false)
  const connectionRef = useRef<RealtimeConnection | null>(null)
  const cameraBeforeBackgroundRef = useRef(false)
  const activeMatch = state.match
  const pipPeerName = activeMatch?.participants?.find((participant) => participant.id !== activeMatch.selfParticipantId)?.displayName
    || activeMatch?.topicLabel
    || "JooW Fak"
  const networkQuality = useNetworkQuality(connectionState, t)
  const syncRoomState = useCallback((next: RoomState, seed: RoomParticipant[] = activeMatch?.participants || []) => {
    setRoomState((previous) => mergeRoomParticipantMetadata(next, previous, seed))
  }, [activeMatch?.participants])

  const revealChrome = useCallback(() => {
    setChromeVisible(true)
    setChromeNudge((value) => value + 1)
  }, [])
  const toggleChrome = useCallback(() => {
    setChromeVisible((visible) => !visible)
    setChromeNudge((value) => value + 1)
  }, [])

  const requestPictureInPicture = useCallback(async (): Promise<boolean> => {
    if (await startNativePictureInPicture()) return true
    const video = document.querySelector<HTMLVideoElement>(".remote-video[data-has-stream='true']")
      || document.querySelector<HTMLVideoElement>(".self-video[data-has-stream='true']")
    if (!video) return false
    const pictureVideo = video as HTMLVideoElement & {
      requestPictureInPicture?: () => Promise<unknown>
      webkitSetPresentationMode?: (mode: "inline" | "picture-in-picture") => void
      webkitPresentationMode?: string
    }
    try {
      if (document.pictureInPictureElement === video || pictureVideo.webkitPresentationMode === "picture-in-picture") return true
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture?.()
        return false
      }
      if (typeof pictureVideo.requestPictureInPicture === "function") {
        await pictureVideo.requestPictureInPicture()
        return true
      }
      if (typeof pictureVideo.webkitSetPresentationMode === "function") {
        pictureVideo.webkitSetPresentationMode("picture-in-picture")
        return true
      }
    } catch {
      return false
    }
    return false
  }, [])

  useEffect(() => {
    if (state.mode !== "video" || !activeMatch?.signaling.pipTicket) return undefined
    void prepareNativePictureInPicture(activeMatch.signaling, pipPeerName).catch(() => false)
    return () => { void endNativePictureInPicture() }
  }, [activeMatch?.sessionId, activeMatch?.signaling.pipTicket, activeMatch?.signaling.selfIdentity, activeMatch?.signaling.url, pipPeerName, state.mode])

  const togglePictureInPicture = async () => {
    if (document.pictureInPictureElement) {
      await document.exitPictureInPicture?.()
      return
    }
    if (!await requestPictureInPicture()) setActionError(t(ui.pipUnavailable))
  }

  useEffect(() => {
    const interval = window.setInterval(() => {
      if (state.startedAt) setSeconds(Math.floor((Date.now() - state.startedAt) / 1000))
    }, 1000)
    return () => window.clearInterval(interval)
  }, [state.startedAt])

  useEffect(() => {
    let disposed = false
    let unsubscribeState = () => {}
    let unsubscribeLocalStream = () => {}
    let unsubscribeRemoteStream = () => {}
    let unsubscribeRemoteParticipants = () => {}
    let unsubscribePeerCount = () => {}
    let supportsRemoteParticipants = false
    if (!activeMatch) return
    setConnectionState("connecting")
    setLocalStream(null)
    setRemoteStream(null)
    setRemoteParticipants([])
    setRemotePeerCount(null)
    setConnectionProvider("")
    setHadRemotePresence(false)
    const syncRemoteStream = (stream?: MediaStream) => {
      if (disposed) return
      const tracks = stream?.getTracks() || []
      if (tracks.length > 0) setHadRemotePresence(true)
      const nextStream = tracks.length > 0 ? new MediaStream(tracks) : null
      setRemoteStream(nextStream)
      if (!supportsRemoteParticipants) {
        const fallbackParticipants = remoteParticipantsFromStream(nextStream)
        setRemoteParticipants(fallbackParticipants)
        if (fallbackParticipants.length > 0) setRemotePeerCount(fallbackParticipants.length)
      }
    }
    const syncRemoteParticipants = (participants: RemoteMediaParticipant[]) => {
      if (disposed) return
      setRemoteParticipants(participants)
      if (participants.length > 0) {
        setHadRemotePresence(true)
        setRemotePeerCount(participants.length)
      }
    }
    const syncPeerCount = (count: number) => {
      if (disposed) return
      setRemotePeerCount(count)
      if (count > 0) setHadRemotePresence(true)
    }
    const connect = async () => {
      const matchToConnect = state.restoredMatch
        ? await recoverSessionWithParticipant(activeMatch.sessionId, activeMatch.peerToken, activeMatch.participantToken || "")
        : activeMatch
      if (disposed) return
      const connection = await connectRealtime(matchToConnect, state.mode)
      if (disposed) {
        void connection.disconnect("unmount")
        return
      }
      connectionRef.current = connection
      setConnectionProvider(connection.provider)
      supportsRemoteParticipants = Boolean(connection.onRemoteParticipantsChange)
      setLocalStream(connection.localStream || null)
      syncRemoteStream(connection.remoteStream)
      if (connection.onState) {
        unsubscribeState = connection.onState((nextState) => {
          if (!disposed) setConnectionState(nextState)
        })
      }
      unsubscribeLocalStream = connection.onLocalStreamChange?.((stream) => {
        if (!disposed) setLocalStream(stream ? new MediaStream(stream.getTracks()) : null)
      }) || (() => {})
      unsubscribeRemoteStream = connection.onRemoteStreamChange?.(syncRemoteStream) || (() => {})
      unsubscribeRemoteParticipants = connection.onRemoteParticipantsChange?.(syncRemoteParticipants) || (() => {})
      unsubscribePeerCount = connection.onPeerCountChange?.(syncPeerCount) || (() => {})
      if (connection.remoteParticipants?.length) syncRemoteParticipants(connection.remoteParticipants)
      setConnectionState(connection.state)
      if (typeof connection.peerCount === "number") syncPeerCount(connection.peerCount)
      setActionError(connection.error || "")
      const initialRoomState = activeMatchRoomState(matchToConnect)
      if (initialRoomState) syncRoomState(initialRoomState, matchToConnect.participants || [])
    }
    connect()
      .catch((err: Error) => {
        if (disposed) {
          return
        }
        setConnectionState("failed")
        setActionError(err.message)
        if (state.restoredMatch) {
          dispatch({ type: "reset-call" })
        }
      })
    return () => {
      disposed = true
      unsubscribeState()
      unsubscribeLocalStream()
      unsubscribeRemoteStream()
      unsubscribeRemoteParticipants()
      unsubscribePeerCount()
      void connectionRef.current?.disconnect("unmount")
      connectionRef.current = null
      setLocalStream(null)
      setRemoteStream(null)
      setRemoteParticipants([])
      setConnectionProvider("")
    }
  }, [activeMatch, dispatch, state.mode, state.restoredMatch, syncRoomState])

  useEffect(() => {
    if (state.mode !== "video") return undefined
    return watchAppVisibility((active) => {
      const connection = connectionRef.current
      if (!connection) return
      if (!active && camera) {
        cameraBeforeBackgroundRef.current = true
        void requestPictureInPicture().then((pipStarted) => {
          if (pipStarted) return
          setCamera(false)
          void connection.setCameraEnabled(false).catch(() => undefined)
        })
      }
      if (active && cameraBeforeBackgroundRef.current) {
        cameraBeforeBackgroundRef.current = false
        setCamera(true)
        void (connection.resumeAfterInterruption?.({ mic, camera: true }) || connection.setCameraEnabled(true)).catch(() => undefined)
      }
    })
  }, [camera, mic, requestPictureInPicture, state.mode])

  useEffect(() => {
    if (!activeMatch?.participantToken) return undefined
    let cancelled = false
    const load = async () => {
      try {
        const state = await getSessionEvents(activeMatch.sessionId, activeMatch.participantToken || "")
        if (!cancelled) syncRoomState(state)
      } catch {
        // Room state is nice-to-have during the call; media errors surface elsewhere.
      }
    }
    void load()
    const timer = window.setInterval(() => void load(), 4000)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [activeMatch?.participantToken, activeMatch?.sessionId, syncRoomState])

  const activeParticipants = roomState?.participants.filter((participant) => participant.active) || []
  const selfParticipantId = activeMatch?.selfParticipantId || ""
  const selfIsActive = Boolean(selfParticipantId) && activeParticipants.some((participant) => participant.id === selfParticipantId)
  const activePeerCount = selfParticipantId
    ? activeParticipants.filter((participant) => participant.id !== selfParticipantId).length
    : Math.max(0, activeParticipants.length - 1)
  const roomStateSaysPeerLeft = Boolean(roomState && selfIsActive && activeParticipants.length === 1 && activePeerCount === 0)
  const realtimeSaysPeerLeft = remotePeerCount === 0 && hadRemotePresence && connectionState !== "connecting"
  const peerLeftDetected = Boolean(activeMatch) && (roomStateSaysPeerLeft || realtimeSaysPeerLeft)
  const peerLeft = peerLeftDetected && peerLeftCountdown <= 0
  const peerReconnecting = peerLeftDetected && peerLeftCountdown > 0

  // Mirrors what VideoBackdrop actually renders, including its legacy single-stream
  // fallback, so a peer visible on screen is never counted as zero.
  const mediaParticipantsNow = remoteParticipants.length > 0
    ? remoteParticipants
    : remoteParticipantsFromStream(remoteStream)
  const livePeerCount = Math.max(activePeerCount, mediaParticipantsNow.length, remotePeerCount ?? 0)
  const remoteHasVideo = mediaParticipantsNow.some((participant) => participant.hasVideo)
  const isDuo = state.mode === "video" && livePeerCount <= 1
  const voteOpen = roomState?.vote?.status === "active" || roomState?.vote?.status === "approved"
  // Anything safety- or state-critical pins the chrome open so the timer cannot
  // strand the user behind a bare video with no visible way to act.
  const chromeLocked = !isDuo
    || !remoteHasVideo
    || showReport
    || stripOverlayOpen
    || voteOpen
    || peerLeft
    || peerReconnecting
    || connectionState !== "connected"
    || actionError !== ""

  useEffect(() => {
    if (!peerLeftDetected) {
      setPeerLeftCountdown(0)
      return undefined
    }
    setPeerLeftCountdown(peerReconnectGraceSeconds)
    const timer = window.setInterval(() => {
      setPeerLeftCountdown((value) => {
        if (value <= 1) {
          window.clearInterval(timer)
          return 0
        }
        return value - 1
      })
    }, 1000)
    return () => window.clearInterval(timer)
  }, [peerLeftDetected])

  useEffect(() => {
    if (chromeLocked) setChromeVisible(true)
  }, [chromeLocked])

  useEffect(() => {
    if (!chromeVisible || chromeLocked) return undefined
    const timer = window.setTimeout(() => setChromeVisible(false), callChromeIdleMs)
    return () => window.clearTimeout(timer)
  }, [chromeLocked, chromeNudge, chromeVisible])

  if (!activeMatch) return leavingCall ? null : <Navigate to="/" replace />

  const score = activeMatch.matchScore ? toPercent(activeMatch.matchScore, locale) : ""
  const orderedRemoteParticipants = orderRemoteParticipants(remoteParticipants, roomState, activeMatch.selfParticipantId || "", remoteStream)
  const end = async (reason: "end" | "next") => {
    setPendingAction(reason)
    setLeavingCall(true)
    setActionError("")
    const minutes = Math.max(1, Math.ceil(seconds / 60))
    try {
      await connectionRef.current?.disconnect(reason)
      if (activeMatch.participantToken) await leaveSessionParticipant(activeMatch.sessionId, activeMatch.participantToken, reason)
      else if (reason === "next") await nextSession(activeMatch.sessionId)
      else await endSession(activeMatch.sessionId)
      navigate(reason === "next" ? "/switching" : "/ended")
      dispatch({ type: "ended", minutes })
    } catch (err) {
      setLeavingCall(false)
      setActionError(err instanceof Error ? err.message : t(copy.errors.connection))
    } finally {
      setPendingAction("")
    }
  }
  const exitToHome = async () => {
    setPendingAction("end")
    setLeavingCall(true)
    setActionError("")
    try {
      await connectionRef.current?.disconnect("end")
      if (activeMatch.participantToken) await leaveSessionParticipant(activeMatch.sessionId, activeMatch.participantToken, "end")
      else await endSession(activeMatch.sessionId)
      dispatch({ type: "reset-call" })
      navigate("/")
    } catch (err) {
      setLeavingCall(false)
      setActionError(err instanceof Error ? err.message : t(copy.errors.connection))
    } finally {
      setPendingAction("")
    }
  }
  const toggleMic = async () => {
    const next = !mic
    setMic(next)
    try {
      await connectionRef.current?.setMicEnabled(next)
    } catch (err) {
      setMic(!next)
      setActionError(err instanceof Error ? err.message : t(copy.errors.connection))
    }
  }
  const toggleCamera = async () => {
    const next = !camera
    setCamera(next)
    try {
      await connectionRef.current?.setCameraEnabled(next)
    } catch (err) {
      setCamera(!next)
      setActionError(err instanceof Error ? err.message : t(copy.errors.connection))
    }
  }
  const openSeatVote = async () => {
    if (!activeMatch.participantToken) return
    setRoomBusy("vote")
    setRoomNotice("")
    try {
      const next = await startOpenSeatVote(activeMatch.sessionId, activeMatch.participantToken)
      syncRoomState(next)
      setRoomNotice(t(ui.roomVoteStarted))
    } catch (err) {
      setRoomNotice(err instanceof Error ? err.message : t(copy.errors.connection))
    } finally {
      setRoomBusy("")
    }
  }
  const castSeatBallot = async (value: "yes" | "no" | "cancel") => {
    if (!activeMatch.participantToken || !roomState?.vote) return
    setRoomBusy(value)
    setRoomNotice("")
    try {
      const next = await ballotOpenSeatVote(activeMatch.sessionId, roomState.vote.id, activeMatch.participantToken, value)
      syncRoomState(next)
      setRoomNotice(value === "cancel" ? t(ui.roomVoteCancelled) : t(ui.roomVoteSaved))
    } catch (err) {
      setRoomNotice(err instanceof Error ? err.message : t(copy.errors.connection))
    } finally {
      setRoomBusy("")
    }
  }

  return (
    <main className="call-stage">
      <section
        className={[
          state.mode === "video" ? "live-call-frame video" : "live-call-frame voice",
          `layout-${tweaks.liveLayout}`,
          connectionProvider === "mock" ? "mock-media" : "",
          isDuo ? "duo" : "",
          isDuo && !chromeVisible ? "chrome-hidden" : "",
        ].filter(Boolean).join(" ")}
      >
        {state.mode === "video" ? (
          <VideoBackdrop
            participants={orderedRemoteParticipants}
            fallbackStream={remoteStream}
            roomParticipants={roomState?.participants || activeMatch.participants || []}
            selfParticipantId={activeMatch.selfParticipantId || ""}
          />
        ) : <VoiceBackdrop layout={tweaks.liveLayout} />}
        {isDuo && (
          <button
            type="button"
            className="chrome-tap-layer"
            aria-label={chromeVisible ? t(ui.hideControls) : t(ui.showControls)}
            aria-expanded={chromeVisible}
            onClick={toggleChrome}
          />
        )}
        <div className="call-meta">
          <div className="call-topic-stack">
            <span>{topicTextOrAny(activeMatch.topicLabel, t)}</span>
            {score && <b><BoltIcon aria-hidden="true" />{f(copy.live.match_score, { score })}</b>}
          </div>
          <time>{formatTimer(seconds, locale)}</time>
          <span className={`network-quality ${networkQuality.level}`}>{networkQuality.label}</span>
        </div>
        <RoomStrip
          state={roomState}
          selfParticipantId={activeMatch.selfParticipantId || ""}
          mediaParticipants={orderedRemoteParticipants}
          micEnabled={mic}
          cameraEnabled={camera}
          canVote={Boolean(activeMatch.participantToken)}
          onOpenSeat={() => void openSeatVote()}
          onOverlayOpenChange={setStripOverlayOpen}
        />
        <RoomVotePanel
          state={roomState}
          busy={roomBusy}
          notice={roomNotice}
          canVote={Boolean(activeMatch.participantToken)}
          selfParticipantId={activeMatch.selfParticipantId || ""}
          onStart={() => void openSeatVote()}
          onBallot={(value) => void castSeatBallot(value)}
        />
        {state.mode === "video" && (
          <div className={localStream ? "self-pip has-video" : "self-pip"}>
            <StreamVideo stream={localStream} muted className="self-video" label={t(copy.live.you)} />
            <span>{t(ui.self)}</span>
          </div>
        )}
        {state.mode !== "video" && <div className="privacy-pill"><EyeOffIcon aria-hidden="true" />{t(copy.live.anon_hidden)}</div>}
        {peerReconnecting && (
          <div className="connection-toast reconnecting" role="status" aria-live="polite">
            <strong>{t(ui.peerReconnectTitle)}</strong>
            <span>{f(ui.peerReconnectBody, { seconds: formatNumber(peerLeftCountdown, locale) })}</span>
          </div>
        )}
        {peerLeft && (
          <div className="peer-left-panel" role="status" aria-live="polite">
            <strong>{t(ui.peerLeftTitle)}</strong>
            <span>{t(ui.peerLeftBody)}</span>
            <div>
              <button type="button" disabled={pendingAction !== ""} onClick={() => void end("next")}>
                {pendingAction === "next" ? t(copy.actions.pending) : t(copy.live.next)}
              </button>
              <button type="button" disabled={pendingAction !== ""} onClick={() => void exitToHome()}>
                {pendingAction === "end" ? t(copy.actions.pending) : t(ui.exitToHome)}
              </button>
            </div>
          </div>
        )}
        {connectionState !== "connected" && !peerLeft && !peerReconnecting && (
          <div className="connection-toast">
            {connectionLabel(connectionState, t)}
          </div>
        )}
        {state.reported && <div className="call-toast">{t(ui.reportSaved)}</div>}
        {actionError && <div className="call-toast error" role="alert">{actionError}</div>}
        <div className="call-action-cluster" onPointerDown={revealChrome}>
          <button className="next-person" disabled={pendingAction !== ""} onClick={() => void end("next")}>
            {pendingAction === "next" ? t(copy.actions.pending) : t(copy.live.next)}
          </button>
          <div className="call-dock">
            <button className={mic ? "dock-icon" : "dock-icon muted"} onClick={() => void toggleMic()} title={t(copy.live.ctrl_mic)} aria-label={t(copy.live.ctrl_mic)}><MicIcon aria-hidden="true" /></button>
            <button className={camera ? "dock-icon" : "dock-icon muted"} onClick={() => void toggleCamera()} title={t(copy.live.ctrl_camera)} aria-label={t(copy.live.ctrl_camera)}><VideoIcon aria-hidden="true" /></button>
            {state.mode === "video" && <button className="dock-icon" onClick={() => void togglePictureInPicture()} title={t(ui.pip)} aria-label={t(ui.pip)}><PictureInPictureIcon aria-hidden="true" /></button>}
            <button className="dock-icon" onClick={() => setShowReport(true)} title={t(copy.live.ctrl_report)} aria-label={t(copy.live.ctrl_report)}><FlagIcon aria-hidden="true" /></button>
            <button className="dock-icon hangup" onClick={() => void end("end")} title={pendingAction === "end" ? t(copy.actions.pending) : t(copy.live.ctrl_end)} aria-label={t(copy.live.ctrl_end)}><PhoneOffIcon aria-hidden="true" /></button>
          </div>
        </div>
        {showReport && (
          <ReportSheet
            sessionId={activeMatch.sessionId}
            peerToken={activeMatch.peerToken}
            participants={roomState?.participants || activeMatch.participants || []}
            selfParticipantId={activeMatch.selfParticipantId || ""}
            conversationCode={localConversationCode(activeMatch.sessionId)}
            topic={topicTextOrAny(activeMatch.topicLabel, t)}
            mode={state.mode}
            evidenceStream={remoteStream || localStream}
            onClose={() => setShowReport(false)}
            onSubmitted={(blocked) => {
              dispatch({ type: "reported" })
              if (blocked) dispatch({ type: "blocked" })
            }}
            onContinue={() => void end("next")}
            onLeaveAfterReport={() => void exitToHome()}
          />
        )}
      </section>
    </main>
  )
}

function RoomStrip({ state, selfParticipantId, mediaParticipants, micEnabled, cameraEnabled, canVote, onOpenSeat, onOverlayOpenChange }: {
  state: RoomState | null
  selfParticipantId: string
  mediaParticipants: RemoteMediaParticipant[]
  micEnabled: boolean
  cameraEnabled: boolean
  canVote: boolean
  onOpenSeat: () => void
  onOverlayOpenChange?: (open: boolean) => void
}) {
  const { locale, t } = useAppState()
  const [topicsOpen, setTopicsOpen] = useState(false)
  const [selectedParticipant, setSelectedParticipant] = useState<RoomParticipant | null>(null)
  const [contactBusy, setContactBusy] = useState(false)
  const [contactNotice, setContactNotice] = useState("")
  // The topic menu and the participant action sheet render inside .room-strip, so the
  // call screen must know they are open before it is allowed to hide the strip.
  const overlayOpen = topicsOpen || Boolean(selectedParticipant)
  useEffect(() => {
    onOverlayOpenChange?.(overlayOpen)
  }, [onOverlayOpenChange, overlayOpen])
  if (!state) return null
  const active = state.participants.filter((participant) => participant.active)
  const self = active.find((participant) => participant.id === selfParticipantId) || active[0]
  const voteActive = state.vote?.status === "active" || state.vote?.status === "approved"
  return (
    <div className="room-strip" aria-label={t(ui.roomParticipants)}>
      <div className="room-strip-head">
        <span>{formatNumber(state.attendeeCount || active.length, locale)} {t(ui.attendees)}</span>
        <small className={state.liveness.momentum}>{formatRoomLiveness(state.liveness.remainingSeconds || 0, locale)} · {momentumLabel(state.liveness.momentum, t)}</small>
      </div>
      <div className="room-participant-controls">
        <div className="room-participant-list">
          {active.map((participant) => {
            const isSelf = participant.id === self?.id
            const media = mediaParticipants.find((item) => item.id === participant.id)
            const micMuted = isSelf ? !micEnabled : Boolean(media && !media.hasAudio)
            const cameraMuted = isSelf ? !cameraEnabled : Boolean(media && !media.hasVideo)
            return (
              <button
                key={participant.id}
                type="button"
                disabled={isSelf}
                className={isSelf ? "room-participant-row self" : "room-participant-row actionable"}
                aria-label={isSelf ? t(ui.youShort) : `${t(ui.participantActions)}: ${participantDisplayName(participant, t)}`}
                onClick={() => {
                  if (!isSelf) {
                    setContactNotice("")
                    setSelectedParticipant(participant)
                  }
                }}
              >
                <div className="room-participant-name">
                  <b>{isSelf ? t(ui.youShort) : participantDisplayName(participant, t)}</b>
                  <small>{participantPublicCode(participant)}</small>
                  {(micMuted || cameraMuted) && <span className="room-media-state" aria-label={micMuted && cameraMuted ? t(copy.live.ctrl_mic) : micMuted ? t(copy.live.ctrl_mic) : t(copy.live.ctrl_camera)}>
                    {micMuted && <MicIcon aria-hidden="true" />}
                    {cameraMuted && <VideoIcon aria-hidden="true" />}
                  </span>}
                </div>
                <span className="room-avatar">
				  <AvatarFace presetId={participant.avatarPreset} />
                </span>
              </button>
            )
          })}
          <button type="button" className="room-add-seat" disabled={!canVote || voteActive} onClick={onOpenSeat} aria-label={t(ui.openSeatVote)} title={t(ui.openSeatVote)}>+</button>
        </div>
        <button type="button" className="room-topic-button" onClick={() => setTopicsOpen((value) => !value)} aria-expanded={topicsOpen} aria-label={t(ui.topicLabel)} title={t(ui.roomParticipants)}>
          <span aria-hidden="true">i</span>
        </button>
      </div>
      {topicsOpen && (
        <div className="room-topic-menu room-menu-overlay" role="menu">
          {active.map((participant) => <div key={participant.id}><b>{participant.id === selfParticipantId ? t(ui.youShort) : participantDisplayName(participant, t)}</b><span>{participantTopicText(participant, t)}</span></div>)}
        </div>
      )}
      {selectedParticipant && (
        <div className="participant-action-scrim" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setSelectedParticipant(null)
        }}>
          <section className="participant-action-sheet" role="dialog" aria-modal="true" aria-label={t(ui.participantActions)}>
            <button type="button" className="participant-sheet-close" aria-label={t(ui.cancel)} onClick={() => setSelectedParticipant(null)}><XIcon aria-hidden="true" /></button>
            <SocialAvatar user={selectedParticipant} />
            <h2>{participantDisplayName(selectedParticipant, t)}</h2>
            <p>{participantPublicCode(selectedParticipant)}</p>
            <small>{t(ui.sharedTopicOnly)}</small>
            {contactNotice && <div className="social-notice">{contactNotice}</div>}
            <button
              type="button"
              className="participant-contact-action"
              disabled={contactBusy}
              onClick={async () => {
                setContactBusy(true)
                setContactNotice("")
                try {
                  await requestContact(state.sessionId, selectedParticipant.id)
                  setContactNotice(t(ui.contactRequestSent))
                } catch (err) {
                  setContactNotice(err instanceof Error ? err.message : t(copy.errors.connection))
                } finally {
                  setContactBusy(false)
                }
              }}
            ><UsersIcon aria-hidden="true" />{contactBusy ? t(copy.actions.pending) : t(ui.requestContact)}</button>
          </section>
        </div>
      )}
    </div>
  )
}

function RoomVotePanel({ state, busy, notice, canVote, selfParticipantId, onStart, onBallot }: {
  state: RoomState | null
  busy: "" | "vote" | "yes" | "no" | "cancel"
  notice: string
  canVote: boolean
  selfParticipantId: string
  onStart: () => void
  onBallot: (value: "yes" | "no" | "cancel") => void
}) {
  const { locale, t } = useAppState()
  if (!state || !canVote) return null
  const vote = state.vote
  const activeVote = vote?.status === "active"
  const alreadyEligible = vote?.eligibleParticipantIds?.includes(selfParticipantId) ?? true
  if (!activeVote && vote?.status !== "approved" && !notice) return null
  return (
    <div className="room-vote-panel room-overlay-panel" role="status" aria-live="polite">
      {activeVote && vote ? (
        <>
          <div>
            <strong>{t(ui.roomVoteQuestion)}</strong>
            <span>{formatNumber(vote.yesVotes, locale)} / {formatNumber(vote.eligibleVoterCount, locale)} · {t(ui.strictMajority)}</span>
          </div>
          <div className="room-vote-actions">
            <button disabled={!alreadyEligible || busy !== ""} onClick={() => onBallot("yes")}>{busy === "yes" ? t(copy.actions.pending) : t(ui.voteYes)}</button>
            <button disabled={!alreadyEligible || busy !== ""} onClick={() => onBallot("no")}>{busy === "no" ? t(copy.actions.pending) : t(ui.voteNo)}</button>
            <button disabled={!alreadyEligible || busy !== ""} onClick={() => onBallot("cancel")}>{busy === "cancel" ? t(copy.actions.pending) : t(ui.voteCancel)}</button>
          </div>
        </>
      ) : vote?.status === "approved" ? (
        <div><strong>{t(ui.openSeatReady)}</strong><span>{formatRoomLiveness(secondsUntil(vote.seatExpiresAt), locale)}</span></div>
      ) : null}
      {notice && <small>{notice}</small>}
    </div>
  )
}

function EndedPage() {
  const { state, dispatch, t, f } = useAppState()
  const navigate = useNavigate()
  const [selected, setSelected] = useState<RatingValue | null>(null)
  const [pendingAction, setPendingAction] = useState("")
  const [actionError, setActionError] = useState("")
  const [showReport, setShowReport] = useState(false)
  const sessionId = state.endedSessionId || state.match?.sessionId || ""
  const peerToken = state.endedPeerToken || state.match?.peerToken || ""
  const topic = topicTextOrAny(state.endedTopic, t)
  const rate = async (value: RatingValue) => {
    if (!sessionId) return
    setSelected(value)
    setPendingAction(value)
    setActionError("")
    try {
      await rateSession(sessionId, value)
    } catch (err) {
      setSelected(null)
      setActionError(err instanceof Error ? err.message : t(copy.errors.connection))
    } finally {
      setPendingAction("")
    }
  }
  const blockEnded = async () => {
    if (!sessionId || !peerToken) return
    setPendingAction("block")
    setActionError("")
    try {
      await blockSession(sessionId, peerToken)
      dispatch({ type: "blocked" })
    } catch (err) {
      setActionError(err instanceof Error ? err.message : t(copy.errors.connection))
    } finally {
      setPendingAction("")
    }
  }
  return (
    <main className="matching-stage ended-stage">
      <section className="ended-frame">
        <div className="ended-check">✓</div>
        <h1>{t(copy.ended.title)}</h1>
        <p>{f(copy.ended.summary, { minutes: state.endedMinutes || 1, topic })}</p>
        <div className="rating-row five-ratings" aria-label={t(copy.ended.rate_prompt)}>
          {ratingOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              disabled={pendingAction !== ""}
              className={selected === option.value ? `rating selected ${option.value}` : `rating ${option.value}`}
              aria-label={t(option.label)}
              title={t(option.label)}
              onClick={() => void rate(option.value)}
            >
              <ChatterRatingIcon value={option.value} />
              <span className="sr-only">{t(option.shortLabel)}</span>
            </button>
          ))}
        </div>
        {state.reported && <div className="call-toast static">{t(ui.reportSaved)}</div>}
        {state.blocked && <div className="call-toast static">{t(ui.blockedSaved)}</div>}
        {actionError && <div className="call-toast static error">{actionError}</div>}
        <div className="safety-row">
          <button disabled={!sessionId || pendingAction !== ""} onClick={() => setShowReport(true)}><FlagIcon aria-hidden="true" />{t(copy.ended.report)}</button>
          <button disabled={!sessionId || pendingAction !== ""} onClick={() => void blockEnded()}><BanIcon aria-hidden="true" />{pendingAction === "block" ? t(copy.actions.pending) : t(copy.ended.block)}</button>
        </div>
        <button className="primary-action" onClick={() => {
          dispatch({ type: "reset-call" })
          navigate("/matching")
        }}>{t(copy.ended.cta_again)}</button>
        <Link className="back-link" to="/">{t(copy.ended.back)}</Link>
        {showReport && sessionId && peerToken && (
          <ReportSheet
            sessionId={sessionId}
            peerToken={peerToken}
            conversationCode={localConversationCode(sessionId)}
            topic={topic}
            mode={state.mode}
            onClose={() => setShowReport(false)}
            onSubmitted={(blocked) => {
              dispatch({ type: "reported" })
              if (blocked) dispatch({ type: "blocked" })
            }}
            onContinue={() => {
              dispatch({ type: "reset-call" })
              navigate("/matching")
            }}
          />
        )}
      </section>
    </main>
  )
}

function ChatterRatingIcon({ value }: { value: RatingValue }) {
  return (
    <span className={`simple-rating-face ${value}`} aria-hidden="true">
      <span className="simple-rating-eyes"><i /><i /></span>
      <span className="simple-rating-mouth" />
    </span>
  )
}

function SwitchingPage() {
  const { state, locale, t } = useAppState()
  const navigate = useNavigate()
  const topic = state.topicText || state.endedTopic || t(ui.topicPending)

  useEffect(() => {
    const timeout = window.setTimeout(() => navigate("/matching"), 1100)
    return () => window.clearTimeout(timeout)
  }, [navigate])

  return (
    <main className="matching-stage">
      <section className="switching-frame">
        <div className="switch-spinner"><span /><b>«</b></div>
        <h1>{t(copy.switching.title)}</h1>
        <p>{t(copy.switching.wiped)}</p>
        <div className="new-topic-pill"><span>{t(copy.switching.new_topic)}</span><b>{topic}</b></div>
        <button type="button" onClick={() => navigate("/topic")}>{t(copy.switching.change_topic)}</button>
      </section>
    </main>
  )
}

function IncomingCallWatcher() {
  const auth = useAuth()
  const { t } = useAppState()
  const location = useLocation()
  const navigate = useNavigate()
  const [incoming, setIncoming] = useState<DirectCallSummary | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (auth.status !== "signed-in" || location.pathname.startsWith("/calls/")) {
      setIncoming(null)
      return undefined
    }
    let cancelled = false
    const load = async () => {
      try {
        const calls = await getDirectCalls()
        if (!cancelled) setIncoming(calls.find((call) => call.direction === "incoming" && call.status === "ringing") || null)
      } catch {
        if (!cancelled) setIncoming(null)
      }
    }
    void load()
    const timer = window.setInterval(() => void load(), 3500)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [auth.status, location.pathname])

  if (!incoming) return null
  const respond = async (action: "accept" | "decline") => {
    setBusy(true)
    try {
      await transitionDirectCall(incoming.id, action)
      if (action === "accept") await answerNativeSystemCall(incoming.id).catch(() => undefined)
      else await endNativeSystemCall(incoming.id).catch(() => undefined)
      setIncoming(null)
      if (action === "accept") navigate(`/calls/${encodeURIComponent(incoming.id)}`)
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="incoming-call-scrim" role="dialog" aria-modal="true" aria-label={t(ui.incomingCall)}>
      <section className="incoming-call-card">
        <SocialAvatar user={incoming.peer} />
        <span>{t(ui.incomingCall)}</span>
        <h2>{incoming.peer.displayName}</h2>
        <p>{incoming.mode === "video" ? t(ui.videoCall) : t(ui.voiceCall)}</p>
        <div>
          <button type="button" className="call-decline" disabled={busy} onClick={() => void respond("decline")}><PhoneOffIcon aria-hidden="true" />{t(ui.decline)}</button>
          <button type="button" className="call-accept" disabled={busy} onClick={() => void respond("accept")}><PhoneIcon aria-hidden="true" />{t(ui.accept)}</button>
        </div>
      </section>
    </div>
  )
}

function NotificationsPage() {
  const { locale, t, f } = useAppState()
  const navigate = useNavigate()
  const [items, setItems] = useState<UserNotification[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const load = useCallback(async () => {
    try {
      setError("")
      const result = await getNotifications()
      setItems(result.items)
    } catch (err) {
      setError(err instanceof Error ? err.message : t(copy.errors.connection))
    } finally {
      setLoading(false)
    }
  }, [t])
  useEffect(() => { void load() }, [load])
  const openNotification = async (notification: UserNotification) => {
    if (!notification.readAt) {
      await readNotification(notification.id).catch(() => undefined)
      setItems((current) => current.map((item) => item.id === notification.id ? { ...item, readAt: new Date().toISOString() } : item))
    }
    if (notification.kind === "incoming_call" && notification.resourceId) navigate(`/calls/${encodeURIComponent(notification.resourceId)}`)
    else if (notification.kind === "contact_request" || notification.kind === "contact_accepted") navigate("/contacts")
  }
  const markAll = async () => {
    await readAllNotifications()
    setItems((current) => current.map((item) => ({ ...item, readAt: item.readAt || new Date().toISOString() })))
  }
  return (
    <main className="client-utility-stage light">
      <section className="client-utility-frame social-frame">
        <MobileTopBar title={t(ui.notifications)} backTo="/" />
        <div className="social-content">
          <h1 className="sr-only">{t(ui.notifications)}</h1>
          {items.some((item) => !item.readAt) && <div className="social-heading-row"><button type="button" onClick={() => void markAll()}>{t(ui.markAllRead)}</button></div>}
          {loading && <div className="social-empty">{t(ui.loading)}</div>}
          {!loading && error && <div className="inline-error">{error}</div>}
          {!loading && !error && !items.length && <div className="social-empty"><BellIcon aria-hidden="true" /><span>{t(ui.noNotifications)}</span></div>}
          <div className="notification-list">
            {items.map((notification) => (
              <button key={notification.id} type="button" className={notification.readAt ? "notification-row" : "notification-row unread"} onClick={() => void openNotification(notification)}>
                <SocialAvatar user={notification.actor} />
                <span><b>{notification.title}</b><small>{notification.body}</small><time>{relativeReportTime(notification.createdAt, locale, f)}</time></span>
              </button>
            ))}
          </div>
        </div>
      </section>
    </main>
  )
}

function HistoryPage() {
  const { locale, t, f } = useAppState()
  const [items, setItems] = useState<HistoryItem[]>([])
  const [loading, setLoading] = useState(true)
  const [notice, setNotice] = useState<Record<string, string>>({})
  useEffect(() => {
    let cancelled = false
    getHistory().then((result) => { if (!cancelled) setItems(result) }).catch(() => undefined).finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])
  const connect = async (sessionId: string, participantId: string) => {
    setNotice((current) => ({ ...current, [participantId]: t(copy.actions.pending) }))
    try {
      await requestContact(sessionId, participantId)
      setNotice((current) => ({ ...current, [participantId]: t(ui.contactRequestSent) }))
    } catch (err) {
      setNotice((current) => ({ ...current, [participantId]: err instanceof Error ? err.message : t(copy.errors.connection) }))
    }
  }
  return (
    <main className="client-utility-stage light">
      <section className="client-utility-frame social-frame">
        <MobileTopBar title={t(ui.history)} backTo="/" />
        <div className="social-content">
          <h1 className="sr-only">{t(ui.history)}</h1>
          {loading && <div className="social-empty">{t(ui.loading)}</div>}
          {!loading && !items.length && <div className="social-empty"><HistoryIcon aria-hidden="true" /><span>{t(ui.noHistory)}</span></div>}
          <div className="history-list">
            {items.map((item) => (
              <article className="history-item" key={item.sessionId}>
                <header><span><b>{item.topicLabel}</b><small>{item.mode === "video" ? t(ui.videoCall) : t(ui.voiceCall)}</small></span><time>{relativeReportTime(item.joinedAt, locale, f)}</time></header>
                <div>
                  {item.participants.map((participant) => (
                    <section className="history-person" key={participant.participantId}>
                      <SocialAvatar user={participant} />
                      <span><b>{participant.displayName}</b><small>{participant.publicCode}</small>{notice[participant.participantId] && <em>{notice[participant.participantId]}</em>}</span>
                      <button type="button" aria-label={t(ui.requestContact)} title={t(ui.requestContact)} onClick={() => void connect(item.sessionId, participant.participantId)}><UsersIcon aria-hidden="true" /></button>
                    </section>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </main>
  )
}

function ContactsPage() {
  const { locale, t, f } = useAppState()
  const navigate = useNavigate()
  const [contacts, setContacts] = useState<ContactSummary[]>([])
  const [requests, setRequests] = useState<ContactRequestSummary[]>([])
  const [calls, setCalls] = useState<DirectCallSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [notice, setNotice] = useState("")
  const load = useCallback(async () => {
    try {
      const [nextContacts, nextRequests, nextCalls] = await Promise.all([getContacts(), getContactRequests(), getDirectCalls()])
      setContacts(nextContacts)
      setRequests(nextRequests)
      setCalls(nextCalls)
    } catch (err) {
      setNotice(err instanceof Error ? err.message : t(copy.errors.connection))
    } finally {
      setLoading(false)
    }
  }, [t])
  useEffect(() => { void load() }, [load])
  const respond = async (request: ContactRequestSummary, action: "accept" | "decline" | "cancel") => {
    setNotice(t(copy.actions.pending))
    try {
      await respondToContactRequest(request.id, action)
      setNotice(action === "accept" ? t(ui.contactAccepted) : action === "decline" ? t(ui.contactDeclined) : t(ui.cancel))
      await load()
    } catch (err) {
      setNotice(err instanceof Error ? err.message : t(copy.errors.connection))
    }
  }
  const call = async (contact: ContactSummary, mode: Mode) => {
    setNotice(t(ui.callConnecting))
    try {
      const response = await startDirectCall(contact.id, mode)
      navigate(`/calls/${encodeURIComponent(response.call.id)}`)
    } catch (err) {
      setNotice(err instanceof Error ? err.message : t(copy.errors.connection))
    }
  }
  const remove = async (contact: ContactSummary) => {
    try {
      await removeContact(contact.id)
      setNotice(t(ui.contactRemoved))
      await load()
    } catch (err) {
      setNotice(err instanceof Error ? err.message : t(copy.errors.connection))
    }
  }
  const pendingRequests = requests.filter((request) => request.status === "pending")
  return (
    <main className="client-utility-stage light">
      <section className="client-utility-frame social-frame">
        <MobileTopBar title={t(ui.contacts)} backTo="/" />
        <div className="social-content">
          <h1 className="sr-only">{t(ui.contacts)}</h1>
          {notice && <p className="social-notice" role="status">{notice}</p>}
          {pendingRequests.length > 0 && <section className="social-section"><h2>{t(ui.contactRequests)}</h2><div className="contact-list">
            {pendingRequests.map((request) => (
              <div className="contact-row" key={request.id}>
                <SocialAvatar user={request.user} />
                <span><b>{request.user.displayName}</b><small>{request.user.publicCode} · {relativeReportTime(request.createdAt, locale, f)}</small></span>
                <div className="contact-request-actions">
                  {request.direction === "incoming" ? <><button type="button" className="accept" title={t(ui.accept)} onClick={() => void respond(request, "accept")}><CheckIcon aria-hidden="true" /></button><button type="button" title={t(ui.decline)} onClick={() => void respond(request, "decline")}><XIcon aria-hidden="true" /></button></> : <button type="button" title={t(ui.cancel)} onClick={() => void respond(request, "cancel")}><XIcon aria-hidden="true" /></button>}
                </div>
              </div>
            ))}
          </div></section>}
          <section className="social-section"><h2>{t(ui.contacts)}</h2>
            {loading && <div className="social-empty">{t(ui.loading)}</div>}
            {!loading && !contacts.length && <div className="social-empty compact"><UsersIcon aria-hidden="true" /><span>{t(ui.noContacts)}</span></div>}
            <div className="contact-list">
              {contacts.map((contact) => (
                <div className="contact-row" key={contact.id}>
                  <SocialAvatar user={contact.user} />
                  <span><b>{contact.user.displayName}</b><small>{contact.user.publicCode}</small></span>
                  <div className="contact-call-actions">
                    <button type="button" title={t(ui.voiceCall)} onClick={() => void call(contact, "voice")}><PhoneIcon aria-hidden="true" /></button>
                    <button type="button" title={t(ui.videoCall)} onClick={() => void call(contact, "video")}><VideoIcon aria-hidden="true" /></button>
                    <button type="button" className="remove" title={t(ui.remove)} onClick={() => void remove(contact)}><XIcon aria-hidden="true" /></button>
                  </div>
                </div>
              ))}
            </div>
          </section>
          {calls.length > 0 && <section className="social-section"><h2>{t(ui.call)}</h2><div className="call-history-list">{calls.slice(0, 10).map((callItem) => <button type="button" key={callItem.id} onClick={() => navigate(`/calls/${encodeURIComponent(callItem.id)}`)}><SocialAvatar user={callItem.peer} /><span><b>{callItem.peer.displayName}</b><small>{callItem.direction === "incoming" ? t(ui.incomingCall) : t(ui.outgoingCall)} · {relativeReportTime(callItem.createdAt, locale, f)}</small></span><em>{callItem.status}</em></button>)}</div></section>}
        </div>
      </section>
    </main>
  )
}

function DirectCallPage() {
  const { t } = useAppState()
  const navigate = useNavigate()
  const { callId = "" } = useParams()
  const [response, setResponse] = useState<DirectCallResponse | null>(null)
  const [connectionState, setConnectionState] = useState<ConnectionState>("connecting")
  const [peerCount, setPeerCount] = useState(0)
  const [localStream, setLocalStream] = useState<MediaStream | null>(null)
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null)
  const [mic, setMic] = useState(true)
  const [camera, setCamera] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")
  const connectionRef = useRef<RealtimeConnection | null>(null)
  const nativeOutgoingCallRef = useRef("")
  const nativeConnectedCallRef = useRef("")
  const connectedTransitionCallRef = useRef("")

  useEffect(() => {
    if (!callId) return undefined
    let cancelled = false
    const load = async () => {
      try {
        const next = await getDirectCall(callId)
        if (!cancelled) setResponse((current) => mergeDirectCallResponse(current, next))
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : t(ui.callUnavailable))
      }
    }
    void load()
    const timer = window.setInterval(() => void load(), 2000)
    return () => { cancelled = true; window.clearInterval(timer) }
  }, [callId, t])

  useEffect(() => {
    const call = response?.call
    if (!call || call.direction !== "outgoing" || nativeOutgoingCallRef.current === call.id) return
    nativeOutgoingCallRef.current = call.id
    void startNativeOutgoingCall(call.id, call.peer.displayName, call.mode === "video").catch(() => undefined)
  }, [response?.call])

  useEffect(() => {
    const call = response?.call
    const signaling = response?.signaling
    if (!call || call.mode !== "video" || !signaling?.pipTicket) return undefined
    void prepareNativePictureInPicture(signaling, call.peer.displayName).catch(() => false)
    return () => { void endNativePictureInPicture() }
  }, [response?.call.id, response?.call.mode, response?.call.peer.displayName, response?.signaling?.pipTicket, response?.signaling?.selfIdentity, response?.signaling?.url])

  const canConnectRealtime = Boolean(response?.call && (
    (response.call.direction === "outgoing" && response.call.status === "ringing") ||
    response.call.status === "accepted" ||
    response.call.status === "connected"
  ))

  useEffect(() => {
    const call = response?.call
    const signaling = response?.signaling
    if (!call || !signaling?.url || !signaling.ticket || !canConnectRealtime || connectionRef.current) return undefined
    let disposed = false
    let unsubscribeState = () => {}
    let unsubscribeLocal = () => {}
    let unsubscribeRemote = () => {}
    let unsubscribePeerCount = () => {}
    const connect = async () => {
      const match: MatchFound = {
        sessionId: call.id,
        peerToken: "",
        topicLabel: "JooW Fak contact",
        realtimeProvider: "livekit",
        signaling,
        media: { iceServers: [] },
      }
      const connection = await connectRealtime(match, call.mode)
      if (disposed) { await connection.disconnect("unmount"); return }
      connectionRef.current = connection
      setLocalStream(connection.localStream || null)
      setRemoteStream(connection.remoteStream || null)
      unsubscribeLocal = connection.onLocalStreamChange?.((stream) => setLocalStream(stream ? new MediaStream(stream.getTracks()) : null)) || (() => {})
      unsubscribeRemote = connection.onRemoteStreamChange?.((stream) => setRemoteStream(stream ? new MediaStream(stream.getTracks()) : null)) || (() => {})
      unsubscribePeerCount = connection.onPeerCountChange?.((count) => setPeerCount(count)) || (() => {})
      unsubscribeState = connection.onState?.((state) => {
        setConnectionState(state)
      }) || (() => {})
      setConnectionState(connection.state)
      setPeerCount(connection.peerCount || 0)
      setCamera(call.mode === "video")
    }
    void connect().catch((err: Error) => { if (!disposed) setError(err.message) })
    return () => { disposed = true; unsubscribeState(); unsubscribeLocal(); unsubscribeRemote(); unsubscribePeerCount() }
  }, [
    response?.call.id,
    response?.call.mode,
    canConnectRealtime,
    response?.signaling?.url,
    response?.signaling?.ticket,
  ])

  useEffect(() => {
    const call = response?.call
    if (!call || connectionState !== "connected" || peerCount < 1 || (call.status !== "accepted" && call.status !== "connected")) return
    if (nativeConnectedCallRef.current !== call.id) {
      nativeConnectedCallRef.current = call.id
      void reportNativeCallConnected(call.id).catch(() => undefined)
    }
    if (call.status !== "accepted" || connectedTransitionCallRef.current === call.id) return
    connectedTransitionCallRef.current = call.id
    void transitionDirectCall(call.id, "connected")
      .then((next) => setResponse((current) => mergeDirectCallResponse(current, next)))
      .catch(() => { connectedTransitionCallRef.current = "" })
  }, [connectionState, peerCount, response?.call.id, response?.call.status])

  useEffect(() => () => { void connectionRef.current?.disconnect("unmount") }, [])

  useEffect(() => watchAppVisibility((active) => {
    if (!active) {
      if (response?.call.mode === "video") void startNativePictureInPicture()
      return
    }
    void connectionRef.current?.resumeAfterInterruption?.({ mic, camera: response?.call.mode === "video" && camera }).catch(() => undefined)
  }), [camera, mic, response?.call.mode])

  const transition = async (action: "accept" | "decline" | "cancel" | "end") => {
    if (!callId) return
    setBusy(true)
    setError("")
    try {
      const next = await transitionDirectCall(callId, action)
      setResponse((current) => mergeDirectCallResponse(current, next))
      if (action !== "accept") {
        await connectionRef.current?.disconnect("end")
        connectionRef.current = null
        await endNativePictureInPicture().catch(() => undefined)
        await endNativeSystemCall(callId).catch(() => undefined)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t(copy.errors.connection))
    } finally {
      setBusy(false)
    }
  }
  const call = response?.call
  const terminal = Boolean(call && ["declined", "cancelled", "missed", "ended", "failed"].includes(call.status))
  useEffect(() => {
    if (!terminal || !call?.id) return
    void connectionRef.current?.disconnect("end")
    connectionRef.current = null
    void endNativeSystemCall(call.id).catch(() => undefined)
    void endNativePictureInPicture().catch(() => undefined)
  }, [call?.id, terminal])
  if (!call) return <main className="call-stage"><section className="direct-call-frame"><div className="direct-call-status">{error || t(ui.loading)}</div></section></main>
  if (call.direction === "incoming" && call.status === "ringing") {
    return <main className="call-stage"><section className="direct-call-frame incoming"><SocialAvatar user={call.peer} /><span>{t(ui.incomingCall)}</span><h1>{call.peer.displayName}</h1><p>{call.mode === "video" ? t(ui.videoCall) : t(ui.voiceCall)}</p>{error && <div className="inline-error">{error}</div>}<div className="direct-call-answer"><button disabled={busy} className="call-decline" onClick={() => void transition("decline")}><PhoneOffIcon aria-hidden="true" />{t(ui.decline)}</button><button disabled={busy} className="call-accept" onClick={() => void transition("accept")}><PhoneIcon aria-hidden="true" />{t(ui.accept)}</button></div></section></main>
  }
  if (terminal) {
    return <main className="call-stage"><section className="direct-call-frame ended"><SocialAvatar user={call.peer} /><h1>{t(ui.callEnded)}</h1><p>{call.peer.displayName}</p><button type="button" className="primary-action" onClick={() => navigate("/contacts")}>{t(ui.contacts)}</button></section></main>
  }
  const toggleMic = async () => { const next = !mic; setMic(next); await connectionRef.current?.setMicEnabled(next) }
  const toggleCamera = async () => { const next = !camera; setCamera(next); await connectionRef.current?.setCameraEnabled(next) }
  return (
    <main className="call-stage">
      <section className="direct-call-frame active">
        <StreamVideo stream={remoteStream} className="direct-call-video" label={call.peer.displayName} />
        {!remoteStream && <div className="direct-call-peer"><SocialAvatar user={call.peer} /><h1>{call.peer.displayName}</h1><p>{call.status === "ringing" ? t(ui.outgoingCall) : t(ui.callConnecting)}</p></div>}
        {call.mode === "video" && <div className="direct-call-self"><StreamVideo stream={localStream} muted className="self-video" label="You" /></div>}
        <div className="direct-call-top"><SocialAvatar user={call.peer} /><span><b>{call.peer.displayName}</b><small>{connectionLabel(connectionState, t)}</small></span></div>
        {error && <div className="call-toast error">{error}</div>}
        <div className="direct-call-dock">
          <button type="button" className={mic ? "dock-icon" : "dock-icon muted"} onClick={() => void toggleMic()}><MicIcon aria-hidden="true" /></button>
          {call.mode === "video" && <button type="button" className={camera ? "dock-icon" : "dock-icon muted"} onClick={() => void toggleCamera()}><VideoIcon aria-hidden="true" /></button>}
          {call.mode === "video" && <button type="button" className="dock-icon" title={t(ui.pip)} aria-label={t(ui.pip)} onClick={() => void startNativePictureInPicture()}><PictureInPictureIcon aria-hidden="true" /></button>}
          <button type="button" className="dock-icon hangup" disabled={busy} onClick={() => void transition(call.status === "ringing" ? "cancel" : "end")}><PhoneOffIcon aria-hidden="true" /></button>
        </div>
      </section>
    </main>
  )
}

function mergeDirectCallResponse(current: DirectCallResponse | null, next: DirectCallResponse): DirectCallResponse {
  if (!current || current.call.id !== next.call.id || !current.signaling) return next
  return {
    ...next,
    signaling: next.signaling ? { ...next.signaling, ...current.signaling } : current.signaling,
  }
}

function SocialAvatar({ user, className = "" }: { user?: { displayName?: string; avatarUrl?: string; avatarPreset?: string }; className?: string }) {
  return <span className={`social-avatar${className ? ` ${className}` : ""}`}><AvatarFace presetId={user?.avatarPreset} /></span>
}

function SettingsPage() {
  const { state, dispatch, tweaks, setTweaks, locale, theme, setLocale, setTheme, t } = useAppState()
  const auth = useAuth()
  const navigate = useNavigate()
  const [accountNotice, setAccountNotice] = useState("")
  const [avatarPreset, setAvatarPreset] = useState<string>(normalizeAvatarPresetId(auth.user?.avatarPreset))
  const [avatarPending, setAvatarPending] = useState(false)
	useEffect(() => {
	  setAvatarPreset(normalizeAvatarPresetId(auth.user?.avatarPreset))
	}, [auth.user?.avatarPreset])
  const update = <K extends keyof AppTweaks>(key: K, value: AppTweaks[K]) => {
    setTweaks((current) => ({ ...current, [key]: value }))
  }
  const signOut = async () => {
    await auth.signOut()
    navigate("/")
  }
  const removeAccount = async () => {
    try {
      await deleteAccount()
      await auth.signOut()
      setAccountNotice(t(ui.deleteAccountDone))
      navigate("/")
    } catch {
      setAccountNotice(t(ui.deleteAccountFailed))
    }
  }
  const saveAvatarPreset = async (nextPreset: string) => {
	const previousPreset = avatarPreset
    setAvatarPreset(nextPreset)
    if (!auth.user) return
    setAvatarPending(true)
    setAccountNotice("")
    try {
      const session = await updateMyProfile({
        displayName: auth.user.displayName || "JooW Member",
        avatarPreset: nextPreset,
      })
      auth.completeSignIn(session)
    } catch (error) {
	  setAvatarPreset(previousPreset)
      setAccountNotice(error instanceof Error ? error.message : t(copy.errors.connection))
    } finally {
      setAvatarPending(false)
    }
  }
  return (
    <main className="client-utility-stage light">
      <section className="client-utility-frame settings-frame">
        <MobileTopBar title={t(copy.settings.title)} backTo="/" />
        <div className="settings-content">
          <h1 className="sr-only">{t(copy.settings.title)}</h1>
          <p className="settings-subtitle">{t(copy.settings.subtitle)}</p>
          <section className="settings-group settings-display-group">
            <h2>{t(copy.settings.display)}</h2>
            <TweakChoice
              label={t(copy.settings.language)}
              options={[
                { value: "fa", label: t(ui.persian) },
                { value: "en", label: t(ui.english) },
              ]}
              value={locale}
              onChange={(value) => setLocale(value as Locale)}
            />
            <TweakChoice
              label={t(copy.settings.theme)}
              options={[
                { value: "light", label: t(ui.lightTheme) },
                { value: "dark", label: t(ui.darkTheme) },
              ]}
              value={theme}
              onChange={(value) => setTheme(value as ThemeMode)}
            />
          </section>
          <section className="settings-group settings-avatar-group">
            <h2>{t(ui.avatarTitle)}</h2>
            <p className="settings-help">{t(ui.avatarHint)}</p>
            <p className="settings-help">{auth.user?.displayName || "JooW Member"}{auth.user?.publicCode ? ` · ${auth.user.publicCode}` : ""}</p>
            <div className="current-avatar-preview"><AvatarFace presetId={avatarPreset} /><span>{avatarPreset ? t(ui.avatarTitle) : t(ui.profileMenu)}</span></div>
            <div className="avatar-preset-options" aria-label={t(ui.profileMenu)}>
              {AVATAR_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  data-preset={preset.id}
                  className={`avatar-preset-choice${avatarPreset === preset.id ? " active" : ""}`}
                  aria-label={`${t(ui.avatarTitle)}: ${preset.label}`}
                  title={preset.label}
                  aria-pressed={avatarPreset === preset.id}
                  disabled={avatarPending}
                  onClick={() => void saveAvatarPreset(preset.id)}
                >
                  <AvatarFace presetId={preset.id} />
                </button>
              ))}
            </div>
          </section>
          <section className="settings-group">
            <h2>{t(copy.settings.session)}</h2>
            <ModeSwitch value={state.mode} onChange={(mode) => dispatch({ type: "mode", mode })} />
            <label className="settings-toggle">
              <span>{t(copy.settings.voice_default)}</span>
              <input type="checkbox" checked={state.mode === "voice"} onChange={() => dispatch({ type: "mode", mode: "voice" })} />
            </label>
            <label className="settings-toggle">
              <span>{t(copy.settings.video_default)}</span>
              <input type="checkbox" checked={state.mode === "video"} onChange={() => dispatch({ type: "mode", mode: "video" })} />
            </label>
          </section>
          <section className="settings-group">
            <h2>{t(copy.settings.vibe)}</h2>
            <div className="settings-swatches">
              {(["coral", "violet", "teal", "sunset"] as Vibe[]).map((vibe) => (
                <button
                  key={vibe}
                  type="button"
                  className={tweaks.vibe === vibe ? `swatch ${vibe} active` : `swatch ${vibe}`}
                  aria-label={t(ui[vibe])}
                  onClick={() => update("vibe", vibe)}
                />
              ))}
            </div>
            {avatarPending && <p className="settings-help" role="status">{t(copy.actions.pending)}</p>}
          </section>
          <TweakChoice
            label={t(copy.settings.energy)}
            options={[
              { value: "calm", label: t(ui.calm) },
              { value: "balanced", label: t(ui.balanced) },
              { value: "lively", label: t(ui.lively) },
            ]}
            value={tweaks.energy}
            onChange={(value) => update("energy", value as Energy)}
          />
          <TweakChoice
            label={t(copy.settings.matching)}
            options={[
              { value: "radar", label: t(ui.radar) },
              { value: "constellation", label: t(ui.constellation) },
              { value: "pulse", label: t(ui.pulse) },
            ]}
            value={tweaks.matchingMotion}
            onChange={(value) => update("matchingMotion", value as MatchingMotion)}
          />
          <TweakChoice
            label={t(copy.settings.layout)}
            options={[
              { value: "full", label: t(ui.layoutFull) },
              { value: "orbs", label: t(ui.layoutOrbs) },
              { value: "minimal", label: t(ui.layoutMinimal) },
            ]}
            value={tweaks.liveLayout}
            onChange={(value) => update("liveLayout", value as LiveLayout)}
          />
          <Link className="settings-report-link" to="/reports"><FlagIcon aria-hidden="true" />{t(copy.reports.mine)}</Link>
          <section className="settings-group">
            <h2>{t(ui.profileMenu)}</h2>
            <button type="button" className="settings-report-link" onClick={() => void signOut()}>{t(ui.signOut)}</button>
            <button type="button" className="settings-report-link danger" onClick={() => void removeAccount()}>{t(ui.deleteAccount)}</button>
            {accountNotice && <p className="inline-error">{accountNotice}</p>}
          </section>
        </div>
      </section>
    </main>
  )
}

function HelpPage() {
  const { t, f } = useAppState()
  const faqs = [
    { q: t(copy.help.q_identity), a: t(copy.help.a_identity) },
    { q: t(copy.help.q_topics), a: t(copy.help.a_topics) },
    { q: t(copy.help.q_join), a: t(copy.help.a_join) },
    { q: t(copy.help.q_open_seat), a: t(copy.help.a_open_seat) },
    { q: t(copy.help.q_video), a: t(copy.help.a_video) },
    { q: t(copy.help.q_report), a: t(copy.help.a_report) },
  ]
  return (
    <main className="client-utility-stage light">
      <section className="client-utility-frame help-frame">
        <MobileTopBar title={t(copy.help.title)} backTo="/" />
        <div className="help-content">
          <h1 className="sr-only">{t(copy.help.title)}</h1>
          <p>{t(copy.help.subtitle)}</p>
          <div className="help-version">{f(copy.help.version, { version: APP_VERSION })}</div>
          <section className="help-group">
            <h2>{t(copy.help.how)}</h2>
            <ol className="help-steps">
              <li><span>1</span>{t(copy.home.how_1)}</li>
              <li><span>2</span>{t(copy.home.how_2)}</li>
              <li><span>3</span>{t(copy.home.how_3)}</li>
            </ol>
          </section>
          <section className="help-group">
            <h2>{t(copy.help.privacy)}</h2>
            <div className="help-privacy-row"><EyeOffIcon aria-hidden="true" />{t(copy.home.trust)}</div>
            <div className="help-privacy-row"><FlagIcon aria-hidden="true" />{t(copy.help.a_report)}</div>
          </section>
          <section className="help-group">
            <h2>{t(copy.help.standards)}</h2>
            <p className="help-group-copy">{t(copy.help.standards_body)}</p>
          </section>
          <section className="help-group">
            <h2>{t(copy.help.contact)}</h2>
            <p className="help-group-copy">{t(copy.help.contact_body)}</p>
            <div className="support-contact">
              <span>{COMPANY_NAME} · {SUPPORT_NAME}</span>
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
              <a href={`tel:${SUPPORT_PHONE.replace(/\s+/g, "")}`}>{SUPPORT_PHONE}</a>
            </div>
          </section>
          <section className="help-group">
            <h2>{t(copy.help.faq)}</h2>
            {faqs.map((item) => (
              <details key={item.q} className="faq-item">
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </section>
          <Link className="settings-report-link" to="/privacy"><EyeOffIcon aria-hidden="true" />{t({ fa: "سیاست حریم خصوصی", en: "Privacy policy" })}</Link>
          <a className="settings-report-link" href={`mailto:${SUPPORT_EMAIL}`}><FlagIcon aria-hidden="true" />{t(copy.help.report_issue)}</a>
        </div>
      </section>
    </main>
  )
}

function PrivacyPage() {
  const { t, f } = useAppState()
  const items = [
    {
      title: { fa: "مسئول و تماس", en: "Operator and contact" },
      body: {
        fa: `Joow Fak برای ${COMPANY_NAME} اجرا می‌شود. مسئول تماس: ${SUPPORT_NAME}، ایمیل ${SUPPORT_EMAIL}، تلفن ${SUPPORT_PHONE}.`,
        en: `Joow Fak is operated for ${COMPANY_NAME}. Contact owner: ${SUPPORT_NAME}, ${SUPPORT_EMAIL}, ${SUPPORT_PHONE}.`,
      },
    },
    {
      title: { fa: "داده‌های حساب", en: "Account data" },
      body: {
        fa: "ورود با Apple شناسهٔ پایدار کاربر و در صورت اجازهٔ Apple نام یا ایمیل را برای ساخت حساب، امنیت، نشست‌ها و حذف حساب نگه می‌دارد.",
        en: "Sign in with Apple provides a stable user identifier and, if Apple shares it, a name or email. We use it for account creation, safety, sessions, and account deletion.",
      },
    },
    {
      title: { fa: "گفت‌وگوها", en: "Conversations" },
      body: {
        fa: "موضوع انتخابی پس از پاک‌سازی برای تطبیق معنایی استفاده می‌شود. صدای/ویدئوی زنده برای تماس عبور داده می‌شود و تماس‌ها به‌صورت پیش‌فرض ضبط نمی‌شوند.",
        en: "Your selected topic is sanitized before semantic matching. Live audio/video is routed for the call, and calls are not recorded by default.",
      },
    },
    {
      title: { fa: "گزارش‌ها", en: "Reports" },
      body: {
        fa: "اگر گزارشی ثبت کنی، فقط با رضایت تو شواهد لازم برای بررسی نگه‌داری می‌شود.",
        en: "When you submit a report, evidence is stored only with your consent so it can be reviewed.",
      },
    },
    {
      title: { fa: "سرویس‌های پردازش", en: "Processing services" },
      body: {
        fa: "Apple ورود را انجام می‌دهد، LiveKit تماس زنده را مدیریت می‌کند، OpenRouter فقط متن موضوع پاک‌سازی‌شده را برای embedding می‌گیرد، و Cloudflare/VPS برای تحویل امن استفاده می‌شوند.",
        en: "Apple handles sign-in, LiveKit handles live calling, OpenRouter receives only sanitized topic text for embeddings, and Cloudflare/VPS hosting support secure delivery.",
      },
    },
    {
      title: { fa: "بدون تبلیغات و ردیابی", en: "No ads or tracking" },
      body: {
        fa: "داده‌ها برای تبلیغات فروخته نمی‌شوند و برای ردیابی بین اپ‌ها یا وب‌سایت‌ها استفاده نمی‌شوند.",
        en: "Data is not sold for advertising and is not used to track you across apps or websites.",
      },
    },
    {
      title: { fa: "کنترل کاربر", en: "User control" },
      body: {
        fa: "می‌توانی از تنظیمات حساب را حذف کنی. داده‌های لازم برای امنیت، جلوگیری از سوءاستفاده و الزامات قانونی ممکن است برای مدت محدود باقی بمانند.",
        en: "You can delete your account from settings. Safety, abuse-prevention, and legal records may be retained for a limited period.",
      },
    },
  ]
  return (
    <main className="client-utility-stage light">
      <section className="client-utility-frame help-frame">
        <MobileTopBar title={t({ fa: "حریم خصوصی", en: "Privacy" })} backTo="/help" />
        <div className="help-content">
          <h1>{t({ fa: "حریم خصوصی Joow Fak", en: "Joow Fak Privacy" })}</h1>
          <p>{t({
            fa: "این صفحه خلاصه‌ی سیاست حریم خصوصی اپ را برای کاربران و بررسی App Store نشان می‌دهد.",
            en: "This page summarizes the app privacy policy for users and App Store review.",
          })}</p>
          <div className="help-version">{f(copy.help.version, { version: APP_VERSION })}</div>
          <section className="help-group">
            {items.map((item) => (
              <details key={item.title.en} className="faq-item" open>
                <summary>{t(item.title)}</summary>
                <p>{t(item.body)}</p>
              </details>
            ))}
          </section>
          <section className="help-group">
            <h2>{t(copy.help.contact)}</h2>
            <div className="support-contact">
              <span>{COMPANY_NAME} · {SUPPORT_NAME}</span>
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
              <a href={`tel:${SUPPORT_PHONE.replace(/\s+/g, "")}`}>{SUPPORT_PHONE}</a>
              <span>{COPYRIGHT_NOTICE}</span>
            </div>
          </section>
          <Link className="settings-report-link" to="/help"><FlagIcon aria-hidden="true" />{t(copy.help.title)}</Link>
        </div>
      </section>
    </main>
  )
}

function MyReportsPage() {
  const { locale, t, f } = useAppState()
  const [reports, setReports] = useState<ReportSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  useEffect(() => {
    let cancelled = false
    getMyReports()
      .then((result) => {
        if (!cancelled) setReports(result)
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message || t(copy.errors.connection))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [t])

  const blockedReports = reports.filter((report) => report.blocked)

  return (
    <main className="mobile-stage light reports-stage">
      <section className="phone-frame my-reports-frame">
        <MobileTopBar title={t(ui.myReports)} backTo="/" />
        <div className={blockedReports.length > 0 ? "my-reports-list has-blocks" : "my-reports-list no-blocks"}>
          {loading && <div className="admin-loading">{t(ui.loading)}</div>}
          {error && <div className="admin-error">{error}</div>}
          {!loading && !error && blockedReports.length > 0 && (
            <section className="blocked-report-list">
              <h2>{t(copy.reports.blocked_title)}</h2>
              <p>{t(copy.reports.blocked_note)}</p>
              {blockedReports.map((report) => (
                <article className="blocked-report-row" key={`blocked-${report.id}`}>
                  <BanIcon aria-hidden="true" />
                  <span><b>{report.conversationCode}</b> · {reportReason(report.reason, t)}</span>
                  <small>{relativeReportTime(report.createdAt, locale, f)}</small>
                </article>
              ))}
            </section>
          )}
          {!loading && !error && reports.map((report, index) => (
            <ReportHistoryCard key={report.id} report={report} fallbackAction={index === 2 ? t(ui.actionWarning) : undefined} />
          ))}
          {!loading && !error && reports.length === 0 && <div className="admin-loading">{t(copy.home.trust)}</div>}
          <div className="reports-expiry">{t(copy.reports.expires)}</div>
        </div>
      </section>
    </main>
  )
}

function ReportHistoryCard({ report, fallbackAction }: { report: ReportSummary; fallbackAction?: string }) {
  const { locale, t, f } = useAppState()
  const statusText = report.status === "valid" ? t(ui.statusValid) : report.status === "invalid" ? t(ui.statusInvalid) : t(ui.statusPending)
  const action = report.status === "valid" ? fallbackAction || t(ui.actionValid) : report.status === "invalid" ? t(ui.actionInvalid) : ""
  return (
    <article className={`history-card ${report.status}`}>
      <div>
        <strong>{reportReason(report.reason, t)}</strong>
        <span className={`history-status ${report.status}`}>{statusText}</span>
      </div>
      <p>{t(ui.reportConversation)} <span>{report.conversationCode}</span> · {relativeReportTime(report.createdAt, locale, f)}</p>
      {report.blocked && <span className="history-blocked"><BanIcon aria-hidden="true" />{t(copy.reports.blocked_badge)}</span>}
      {action && <small>{action}</small>}
    </article>
  )
}

function ReportSheet({ sessionId, peerToken, participants, selfParticipantId, conversationCode, topic, mode, evidenceStream, onClose, onSubmitted, onContinue, onLeaveAfterReport }: {
  sessionId: string
  peerToken: string
  participants?: RoomState["participants"]
  selfParticipantId?: string
  conversationCode: string
  topic: string
  mode: Mode
  evidenceStream?: MediaStream | null
  onClose: () => void
  onSubmitted: (blocked: boolean) => void
  onContinue: () => void
  onLeaveAfterReport?: () => void
}) {
  const { t } = useAppState()
  const navigate = useNavigate()
  const [reason, setReason] = useState("")
  const [notes, setNotes] = useState("")
  const [block, setBlock] = useState(true)
  const reportableParticipants = (participants || []).filter((participant) => participant.active && participant.id !== selfParticipantId)
  const [targetParticipantId, setTargetParticipantId] = useState(() => reportableParticipants[0]?.id || "")
  const canBlock = Boolean(peerToken || targetParticipantId)
  const [evidenceConsent, setEvidenceConsent] = useState(false)
  const [pending, setPending] = useState(false)
  const [pendingLabel, setPendingLabel] = useState("")
  const [evidenceNotice, setEvidenceNotice] = useState("")
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState("")
  const reasons = reportReasons(t)
  const reasonRequired = reason === ""
  const otherMissingNotes = reason === "other" && notes.trim().length === 0
  const consentMissing = !evidenceConsent
  const effectiveBlock = canBlock && block
  const canSubmit = !pending && !reasonRequired && !otherMissingNotes && !consentMissing

  const submit = async (leaveAfter = false) => {
    if (!canSubmit) return
    setPending(true)
    setPendingLabel(t(ui.reportEvidenceCapturing))
    setEvidenceNotice("")
    setError("")
    try {
      let evidence = null
      try {
        evidence = await captureReportEvidence(evidenceStream, mode)
      } catch {
        evidence = null
      }
      setEvidenceNotice(evidence ? t(ui.reportEvidenceCaptured) : t(ui.reportEvidenceUnavailable))
      setPendingLabel(t(copy.actions.pending))
      await reportSession(sessionId, peerToken, targetParticipantId, reason, notes, effectiveBlock, evidenceConsent, evidence)
      onSubmitted(effectiveBlock)
      if (leaveAfter && onLeaveAfterReport) {
        onLeaveAfterReport()
        return
      }
      setSubmitted(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : t(copy.errors.connection))
    } finally {
      setPending(false)
      setPendingLabel("")
    }
  }

  return (
    <div className="report-overlay" role="dialog" aria-modal="true" aria-labelledby="report-title">
      <section className="report-sheet">
        <header>
          <button type="button" className="report-back" onClick={onClose} aria-label={t(ui.dismiss)}><ArrowLeftIcon aria-hidden="true" /></button>
          <strong id="report-title">{t(ui.reportPeer)}</strong>
        </header>
        {!submitted ? (
          <>
            <div className="report-content">
              <p>{t(ui.reportPrompt)}</p>
              <div className="report-context">{t(ui.reportConversation)} <span>{conversationCode}</span> · {topic}</div>
              {!peerToken && reportableParticipants.length > 0 && (
                <div className="report-targets">
                  <button type="button" className={targetParticipantId === "" ? "active" : ""} onClick={() => {
                    setTargetParticipantId("")
                    setBlock(false)
                  }}>{t(ui.wholeRoomReport)}</button>
                  {reportableParticipants.map((participant) => (
                    <button key={participant.id} type="button" className={targetParticipantId === participant.id ? "active" : ""} onClick={() => {
                      setTargetParticipantId(participant.id)
                      setBlock(true)
                    }}>{participantDisplayName(participant, t)} · {participantPublicCode(participant)}</button>
                  ))}
                </div>
              )}
              <div className="report-reasons">
                {reasons.map((item) => (
                  <button key={item.value} type="button" className={reason === item.value ? "report-option active" : "report-option"} aria-pressed={reason === item.value} onClick={() => setReason(item.value)}>
                    <span><FlagIcon aria-hidden="true" /></span>
                    <b>{item.label}</b>
                    <i />
                  </button>
                ))}
              </div>
              <label className="report-notes">
                <span>{t(ui.reportNotes)}</span>
                <textarea value={notes} onChange={(event) => setNotes(event.target.value)} placeholder={t(ui.reportNotesPlaceholder)} />
              </label>
              <label className="report-block">
                <input type="checkbox" checked={effectiveBlock} disabled={!canBlock} onChange={(event) => setBlock(event.target.checked)} />
                <span>{t(ui.reportBlock)}</span>
              </label>
              <label className="report-consent">
                <input type="checkbox" checked={evidenceConsent} onChange={(event) => setEvidenceConsent(event.target.checked)} />
                <span>{t(ui.reportEvidenceConsent)}</span>
              </label>
              {evidenceNotice && <div className="report-privacy"><FlagIcon aria-hidden="true" />{evidenceNotice}</div>}
              <div className="report-privacy"><EyeOffIcon aria-hidden="true" />{effectiveBlock ? t(ui.reportBlockedPrivate) : t(copy.home.trust)}</div>
              {reasonRequired && <div className="inline-error">{t(ui.reasonRequired)}</div>}
              {otherMissingNotes && <div className="inline-error">{t(ui.otherReasonRequired)}</div>}
              {consentMissing && <div className="inline-error">{t(ui.evidenceConsentRequired)}</div>}
              {error && <div className="admin-error">{error}</div>}
            </div>
            <div className="report-actions">
              <button type="button" className="primary-action" disabled={!canSubmit} onClick={() => void submit()}>{pending ? pendingLabel || t(copy.actions.pending) : t(ui.sendReport)}</button>
              {onLeaveAfterReport && <button type="button" className="report-leave-action" disabled={!canSubmit} onClick={() => void submit(true)}>{pending ? pendingLabel || t(copy.actions.pending) : t(ui.reportAndLeave)}</button>}
              <button type="button" onClick={onClose}>{t(ui.dismiss)}</button>
            </div>
          </>
        ) : (
          <div className="report-success">
            <div>✓</div>
            <h1>{t(ui.reportSubmittedTitle)}</h1>
            <p>{t(ui.reportSubmittedBody)}</p>
            <article className="history-card pending">
              <div><strong>{reasons.find((item) => item.value === reason)?.label}</strong><span className="history-status pending">{t(ui.statusPending)}</span></div>
              <p>{t(ui.reportConversation)} <span>{conversationCode}</span></p>
            </article>
            {effectiveBlock && <small>{t(ui.reportBlockedPrivate)}</small>}
            <div className="report-success-actions">
              <button type="button" className="primary-action" onClick={onContinue}>{t(ui.continueNext)}</button>
              <button type="button" className="secondary-action" onClick={() => navigate("/reports")}>{t(ui.myReports)}</button>
            </div>
          </div>
        )}
      </section>
    </div>
  )
}

function AdminPage() {
  const { locale, t, f } = useAppState()
  const auth = useAuth()
  const location = useLocation()
  const { reportId } = useParams()
  const section = reportId ? "report-detail" : adminSectionFromPath(location.pathname)
  const [dashboard, setDashboard] = useState<AdminDashboard | null>(null)
  const [adminRooms, setAdminRooms] = useState<AdminRoomSummary[]>([])
  const [reports, setReports] = useState<ReportSummary[]>([])
  const [detail, setDetail] = useState<ReportSummary | null>(null)
  const [adminProfile, setAdminProfile] = useState<MeResponse | null>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [error, setError] = useState("")
  const [reviewed, setReviewed] = useState("")
  const [pendingReview, setPendingReview] = useState("")
  const [refreshing, setRefreshing] = useState(false)
  const [lastRefreshed, setLastRefreshed] = useState<number | null>(null)
  const [metricHistory, setMetricHistory] = useState<AdminMetricSample[]>([])
  const [reportedUsers, setReportedUsers] = useState<ReportedUserSummary[]>([])
  const [blockedUsers, setBlockedUsers] = useState<UserModerationBlock[]>([])
  const [reportedUsersLoading, setReportedUsersLoading] = useState(false)
  const [reportedUserRange, setReportedUserRange] = useState<AdminRange>("7d")
  const [minReporters, setMinReporters] = useState(10)
  const [bannedTopics, setBannedTopics] = useState<BannedTopic[]>([])
  const [pendingModeration, setPendingModeration] = useState("")
  const [moderationNotice, setModerationNotice] = useState("")
  const [adminAccessRevoked, setAdminAccessRevoked] = useState(false)

  useEffect(() => {
    let cancelled = false
    getMe()
      .then((me) => {
        if (!cancelled) setAdminProfile(me)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [])

  const handleAdminLoadError = useCallback((err: unknown) => {
    if (isAuthRequiredError(err)) {
      setAdminAccessRevoked(true)
      setError("")
      void auth.signOut()
      return
    }
    const message = adminLoadErrorMessage(err, t)
    if (isAdminAccessError(err)) setAdminAccessRevoked(true)
    setError(message)
  }, [auth, t])

  const loadDashboard = useCallback(async () => {
    if (adminAccessRevoked) return
    setRefreshing(true)
    try {
      const result = await getAdminDashboard()
      const fetchedAt = Date.now()
      setDashboard(result)
      setLastRefreshed(fetchedAt)
      setMetricHistory((current) => appendAdminMetricSample(current, result.metrics, fetchedAt))
      setError("")
    } catch (err) {
      handleAdminLoadError(err)
    } finally {
      setRefreshing(false)
    }
  }, [adminAccessRevoked, handleAdminLoadError])

  const loadReports = useCallback(async () => {
    if (adminAccessRevoked) return
    try {
      const items = await getAdminReports("all", searchQuery)
      setReports(items)
      setError("")
    } catch (err) {
      handleAdminLoadError(err)
    }
  }, [adminAccessRevoked, handleAdminLoadError, searchQuery])

  const loadRooms = useCallback(async () => {
    if (adminAccessRevoked) return
    try {
      const rooms = await getAdminRooms()
      setAdminRooms(rooms)
      setError("")
    } catch (err) {
      handleAdminLoadError(err)
    }
  }, [adminAccessRevoked, handleAdminLoadError])

  const loadDetail = useCallback(async () => {
    if (adminAccessRevoked) return
    if (!reportId) {
      setDetail(null)
      return
    }
    try {
      const report = await getAdminReport(reportId)
      setDetail(report)
      setError("")
    } catch (err) {
      handleAdminLoadError(err)
    }
  }, [adminAccessRevoked, handleAdminLoadError, reportId])

  const loadReportedUsers = useCallback(async () => {
    if (adminAccessRevoked) return
    setReportedUsersLoading(true)
    try {
      const result = await getAdminReportedUsers(reportedUserRange, minReporters)
      setReportedUsers(result.reportedUsers)
      setBlockedUsers(result.blockedUsers)
      setError("")
    } catch (err) {
      handleAdminLoadError(err)
    } finally {
      setReportedUsersLoading(false)
    }
  }, [adminAccessRevoked, handleAdminLoadError, minReporters, reportedUserRange])

  const loadTopicModeration = useCallback(async () => {
    if (adminAccessRevoked) return
    try {
      const bans = await getAdminTopicBans()
      setBannedTopics(bans)
      setError("")
    } catch (err) {
      handleAdminLoadError(err)
    }
  }, [adminAccessRevoked, handleAdminLoadError])

  const refreshAll = useCallback(async () => {
    if (adminAccessRevoked) return
    await Promise.all([
      loadDashboard(),
      section === "rooms" ? loadRooms() : Promise.resolve(),
      section === "reports" || section === "security" || section === "report-detail" ? loadReports() : Promise.resolve(),
      section === "users" ? loadReportedUsers() : Promise.resolve(),
      section === "topics" ? loadTopicModeration() : Promise.resolve(),
      reportId ? loadDetail() : Promise.resolve(),
    ])
  }, [adminAccessRevoked, loadDashboard, loadRooms, loadReports, loadReportedUsers, loadTopicModeration, loadDetail, reportId, section])

  useEffect(() => {
    if (adminAccessRevoked) return undefined
    void loadDashboard()
    const timer = window.setInterval(() => void loadDashboard(), 5000)
    return () => window.clearInterval(timer)
  }, [adminAccessRevoked, loadDashboard])

  useEffect(() => {
    if (adminAccessRevoked) return undefined
    if (section !== "reports" && section !== "security" && section !== "report-detail") return
    void loadReports()
    const timer = window.setInterval(() => void loadReports(), 7000)
    return () => window.clearInterval(timer)
  }, [adminAccessRevoked, loadReports, section])

  useEffect(() => {
    if (adminAccessRevoked) return undefined
    if (section !== "rooms") return
    void loadRooms()
    const timer = window.setInterval(() => void loadRooms(), 5000)
    return () => window.clearInterval(timer)
  }, [adminAccessRevoked, loadRooms, section])

  useEffect(() => {
    if (adminAccessRevoked) return undefined
    void loadDetail()
    if (!reportId) return undefined
    const timer = window.setInterval(() => void loadDetail(), 7000)
    return () => window.clearInterval(timer)
  }, [adminAccessRevoked, loadDetail, reportId])

  useEffect(() => {
    if (adminAccessRevoked) return undefined
    if (section !== "users") return
    void loadReportedUsers()
    const timer = window.setInterval(() => void loadReportedUsers(), 7000)
    return () => window.clearInterval(timer)
  }, [adminAccessRevoked, loadReportedUsers, section])

  useEffect(() => {
    if (adminAccessRevoked) return undefined
    if (section !== "topics") return
    void loadTopicModeration()
    const timer = window.setInterval(() => void loadTopicModeration(), 7000)
    return () => window.clearInterval(timer)
  }, [adminAccessRevoked, loadTopicModeration, section])

  const review = async (report: ReportSummary, status: ReportStatus) => {
    setPendingReview(report.id)
    setReviewed("")
    try {
      await reviewAdminReport(report.id, status)
      const reviewedAt = status === "pending" ? null : new Date().toISOString()
      const pendingDelta = report.status === "pending" && status !== "pending" ? -1 : report.status !== "pending" && status === "pending" ? 1 : 0
      const updatedReport = { ...report, status, reviewedAt }
      setDashboard((current) => {
        if (!current) return current
        const reportQueue = status === "pending"
          ? (current.reportQueue.some((item) => item.id === report.id) ? current.reportQueue.map((item) => item.id === report.id ? updatedReport : item) : [updatedReport, ...current.reportQueue])
          : current.reportQueue.filter((item) => item.id !== report.id)
        return {
          ...current,
          metrics: {
            ...current.metrics,
            pendingReports: Math.max(0, current.metrics.pendingReports + pendingDelta),
          },
          reportQueue,
        }
      })
      setReports((current) => current.map((item) => item.id === report.id ? { ...item, status, reviewedAt } : item))
      setDetail((current) => current?.id === report.id ? { ...current, status, reviewedAt } : current)
      setReviewed(`${report.conversationCode} · ${status === "valid" ? t(ui.markValid) : status === "invalid" ? t(ui.markInvalid) : t(ui.markPending)}`)
      await Promise.all([loadDashboard(), loadReports(), reportId ? loadDetail() : Promise.resolve()])
    } catch (err) {
      handleAdminLoadError(err)
    } finally {
      setPendingReview("")
    }
  }

  const blockReportedUser = async (subjectKey: string) => {
    setPendingModeration(subjectKey)
    setModerationNotice("")
    try {
      await blockAdminReportedUser(subjectKey, "multi-reporter moderation threshold")
      setModerationNotice(t(ui.userBlocked))
      await loadReportedUsers()
    } catch (err) {
      handleAdminLoadError(err)
    } finally {
      setPendingModeration("")
    }
  }

  const unblockReportedUser = async (subjectKey: string) => {
    setPendingModeration(subjectKey)
    setModerationNotice("")
    try {
      await unblockAdminReportedUser(subjectKey)
      setModerationNotice(t(ui.userUnblocked))
      await loadReportedUsers()
    } catch (err) {
      handleAdminLoadError(err)
    } finally {
      setPendingModeration("")
    }
  }

  const banTopic = async (topicText: string) => {
    const trimmed = topicText.trim()
    if (!trimmed) return
    setPendingModeration(`topic:${trimmed}`)
    setModerationNotice("")
    try {
      await banAdminTopic(trimmed, "admin topic moderation")
      setModerationNotice(t(ui.topicBanned))
      await Promise.all([loadDashboard(), loadTopicModeration()])
    } catch (err) {
      handleAdminLoadError(err)
    } finally {
      setPendingModeration("")
    }
  }

  const unbanTopic = async (normalizedTopic: string) => {
    setPendingModeration(`topic:${normalizedTopic}`)
    setModerationNotice("")
    try {
      await unbanAdminTopic(normalizedTopic)
      setModerationNotice(t(ui.topicRestored))
      await Promise.all([loadDashboard(), loadTopicModeration()])
    } catch (err) {
      handleAdminLoadError(err)
    } finally {
      setPendingModeration("")
    }
  }

  const data = dashboard ? {
    ...dashboard,
    liveSessions: filterAdminSessions(dashboard.liveSessions, searchQuery),
    reportQueue: filterAdminReports(dashboard.reportQueue, searchQuery),
  } : null
  const visibleReports = filterAdminReports(reports, searchQuery)
  const visibleReportedUsers = filterAdminReportedUsers(reportedUsers, searchQuery)
  const visibleBlockedUsers = filterAdminUserBlocks(blockedUsers, searchQuery)
  const heading = adminSectionTitle(section, t)
  const adminName = adminProfile?.displayName || (locale === "fa" ? "ادمین" : "Admin")
  const accessDenied = adminAccessRevoked || isAdminAccessDenied(error)
  if (adminAccessRevoked) return <AdminAccessDeniedPage />
  return (
    <main className="admin-stage">
      <section className="admin-frame">
        <header className="admin-topbar">
          <LogoMark />
          <strong>{t(copy.brand.name)}</strong>
          <span className="divider" />
          <span className="admin-subtitle">{t(ui.adminTitle)}</span>
          <label className="admin-search">
            <SearchIcon aria-hidden="true" />
            <input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={t(ui.adminSearch)}
              aria-label={t(ui.adminSearch)}
            />
          </label>
          <AdminProfileMenu profile={adminProfile} fallbackName={adminName} />
        </header>
        {!accessDenied && <aside className="admin-sidebar">
          <AdminNavItem active={section === "overview"} to="/admin" label={t(copy.admin.overview)} icon="overview" />
          <AdminNavItem active={section === "live"} to="/admin/live" label={t(copy.admin.live)} icon="live" />
          <AdminNavItem active={section === "rooms"} to="/admin/rooms" label={t(ui.adminRooms)} icon="rooms" />
          <AdminNavItem active={section === "reports" || section === "report-detail"} to="/admin/reports" label={t(copy.admin.reports)} icon="reports" badge={data?.metrics.pendingReports || 0} />
          <AdminNavItem active={section === "topics"} to="/admin/topics" label={t(copy.admin.topics)} icon="topics" />
          <AdminNavItem active={section === "users"} to="/admin/users" label={t(copy.admin.users)} icon="users" />
          <AdminNavItem active={section === "security"} to="/admin/security" label={t(copy.admin.security)} icon="safety" />
          <AdminNavItem active={section === "settings"} to="/admin/settings" label={t(copy.admin.settings)} icon="settings" />
          <div className="admin-privacy-note"><EyeOffIcon aria-hidden="true" />{t(ui.adminNoIdentity)}</div>
        </aside>}
        <section className={accessDenied ? "admin-content denied" : "admin-content"}>
          {accessDenied ? (
            <AdminAccessDenied />
          ) : (
            <>
          <div className="admin-heading">
            <div>
              <h1>{heading}</h1>
              <p>{t(ui.adminRealtime)}</p>
            </div>
            <div className="admin-toolbar">
              <span>{lastRefreshed ? f(ui.lastUpdated, { time: formatClock(lastRefreshed, locale) }) : t(ui.liveApi)}</span>
              <button
                type="button"
                className={refreshing ? "admin-refresh-button spinning" : "admin-refresh-button"}
                onClick={() => void refreshAll()}
                aria-label={refreshing ? t(ui.refreshing) : t(ui.refresh)}
                title={refreshing ? t(ui.refreshing) : t(ui.refresh)}
              >
                <RefreshCwIcon aria-hidden="true" />
                <span className="sr-only">{refreshing ? t(ui.refreshing) : t(ui.refresh)}</span>
              </button>
            </div>
          </div>
          <label className="admin-mobile-search">
            <SearchIcon aria-hidden="true" />
            <input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={t(ui.adminSearch)}
              aria-label={t(ui.adminSearch)}
            />
          </label>
          <div className="identity-alert compact admin-identity-notice"><EyeOffIcon aria-hidden="true" />{t(ui.adminNoIdentity)}</div>
          {error && <div className="admin-error">{error}</div>}
          {!data && !error && <div className="admin-loading">{t(ui.loading)}</div>}
          {data && section === "overview" && <AdminOverview data={data} metricHistory={metricHistory} pendingReview={pendingReview} reviewed={reviewed} onReview={(report, status) => void review(report, status)} />}
          {data && section === "live" && <AdminLiveSessions data={data} />}
          {data && section === "rooms" && <AdminRoomsPage rooms={filterAdminRooms(adminRooms, searchQuery)} />}
          {data && section === "reports" && <AdminReportsPage reports={visibleReports} pendingReview={pendingReview} reviewed={reviewed} onReview={(report, status) => void review(report, status)} />}
          {data && section === "topics" && <AdminTopicsPage data={data} bannedTopics={bannedTopics} pending={pendingModeration} notice={moderationNotice} onBan={(topicText) => void banTopic(topicText)} onUnban={(normalizedTopic) => void unbanTopic(normalizedTopic)} />}
          {data && section === "users" && <AdminReportedUsersPage users={visibleReportedUsers} blockedUsers={visibleBlockedUsers} range={reportedUserRange} minReporters={minReporters} loading={reportedUsersLoading} pending={pendingModeration} notice={moderationNotice} onRangeChange={setReportedUserRange} onMinReportersChange={setMinReporters} onBlock={(subjectKey) => void blockReportedUser(subjectKey)} onUnblock={(subjectKey) => void unblockReportedUser(subjectKey)} />}
          {data && section === "security" && <AdminSecurityPage reports={visibleReports} />}
          {data && section === "settings" && <AdminSettingsPage />}
          {data && section === "report-detail" && (detail ? (
            <AdminReportReview report={detail} pending={pendingReview === detail.id} reviewed={reviewed} onReview={(status) => void review(detail, status)} />
          ) : !error ? <div className="admin-loading">{t(ui.loading)}</div> : null)}
            </>
          )}
        </section>
        {!accessDenied && <nav className="admin-mobile-nav">
          <AdminNavItem active={section === "overview"} compact to="/admin" label={t(copy.admin.overview)} icon="overview" />
          <AdminNavItem active={section === "live"} compact to="/admin/live" label={t(copy.admin.live)} icon="live" />
          <AdminNavItem active={section === "rooms"} compact to="/admin/rooms" label={t(ui.adminRooms)} icon="rooms" />
          <AdminNavItem active={section === "reports" || section === "report-detail"} compact to="/admin/reports" label={t(copy.admin.reports)} icon="reports" badge={data?.metrics.pendingReports || 0} />
          <AdminNavItem active={section === "topics"} compact to="/admin/topics" label={t(copy.admin.topics)} icon="topics" />
          <AdminNavItem active={section === "users"} compact to="/admin/users" label={t(copy.admin.users)} icon="users" />
          <AdminNavItem active={section === "security"} compact to="/admin/security" label={t(copy.admin.security)} icon="safety" />
          <AdminNavItem active={section === "settings"} compact to="/admin/settings" label={t(copy.admin.settings)} icon="settings" />
        </nav>}
      </section>
    </main>
  )
}

function AdminAccessDeniedPage() {
  return (
    <main className="admin-stage access-denied-only">
      <AdminAccessDenied showSignOut />
    </main>
  )
}

function AdminAccessDenied({ showSignOut = false }: { showSignOut?: boolean }) {
  const { t } = useAppState()
  const auth = useAuth()
  const navigate = useNavigate()
  const signOut = async () => {
    await auth.signOut()
    navigate("/")
  }
  return (
    <div className="admin-denied-card">
      <div className="admin-denied-icon"><EyeOffIcon aria-hidden="true" /></div>
      <h1>{t(ui.adminDenied)}</h1>
      <p>{t(ui.adminDeniedBody)}</p>
      <div className="admin-denied-actions">
        <Link className="secondary-wide link-like" to="/">{t(ui.adminDeniedAction)}</Link>
        {showSignOut && <button type="button" className="secondary-wide" onClick={() => void signOut()}>{t(ui.signOut)}</button>}
      </div>
    </div>
  )
}

function AdminProfileMenu({ profile, fallbackName }: { profile: MeResponse | null; fallbackName: string }) {
  const { locale, t } = useAppState()
  const auth = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const avatarUrl = safeAvatarUrl(profile?.avatarUrl)
  const initial = profileInitialFor(profile?.displayName || fallbackName, locale)
  const signOut = async () => {
    setOpen(false)
    await auth.signOut()
    navigate("/")
  }
  return (
    <div className="admin-profile-menu">
      <button
        type="button"
        className="admin-user"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t(ui.adminProfile)}
        onClick={() => setOpen((value) => !value)}
      >
        <span>{avatarUrl ? <img src={avatarUrl} alt="" referrerPolicy="no-referrer" /> : initial}</span>
        <b>{fallbackName} · {t(ui.adminTitle)}</b>
      </button>
      {open && (
        <nav className="admin-profile-dropdown" role="menu">
          <Link role="menuitem" to="/admin/settings" onClick={() => setOpen(false)}>{t(ui.adminSettingsMenu)}</Link>
          <button type="button" role="menuitem" onClick={() => void signOut()}>{t(ui.signOut)}</button>
        </nav>
      )}
    </div>
  )
}

type AdminSection = "overview" | "live" | "rooms" | "reports" | "report-detail" | "topics" | "users" | "security" | "settings"

function adminSectionFromPath(pathname: string): AdminSection {
  if (pathname.includes("/admin/live")) return "live"
  if (pathname.includes("/admin/rooms")) return "rooms"
  if (pathname.includes("/admin/reports")) return "reports"
  if (pathname.includes("/admin/topics")) return "topics"
  if (pathname.includes("/admin/users")) return "users"
  if (pathname.includes("/admin/security")) return "security"
  if (pathname.includes("/admin/settings")) return "settings"
  return "overview"
}

function adminSectionTitle(section: AdminSection, t: (value: LocalizedText) => string): string {
  if (section === "live") return t(copy.admin.live)
  if (section === "rooms") return t(ui.adminRooms)
  if (section === "reports") return t(copy.admin.reports)
  if (section === "report-detail") return t(copy.admin.review_title)
  if (section === "topics") return t(copy.admin.topics)
  if (section === "users") return t(copy.admin.users)
  if (section === "security") return t(copy.admin.security)
  if (section === "settings") return t(copy.admin.settings)
  return t(copy.admin.overview)
}

function filterAdminSessions(sessions: AdminDashboard["liveSessions"], query: string): AdminDashboard["liveSessions"] {
  const normalized = query.trim().toLowerCase()
  if (!normalized) return sessions
  return sessions.filter((session) => [
    session.conversationCode,
    session.topicLabel,
    session.topicKeywords,
    session.mode,
    session.endReason,
  ].some((value) => String(value || "").toLowerCase().includes(normalized)))
}

function filterAdminRooms(rooms: AdminRoomSummary[], query: string): AdminRoomSummary[] {
  const normalized = query.trim().toLowerCase()
  if (!normalized) return rooms
  return rooms.filter((room) => [
    room.conversationCode,
    room.topicLabel,
    room.topicKeywords,
    room.mode,
    room.activeVote?.status,
    ...room.participants.map((participant) => participant.code),
  ].some((value) => String(value || "").toLowerCase().includes(normalized)))
}

function adminRoomStatus(room: AdminRoomSummary): Exclude<AdminRoomFilter, "all"> {
  if (room.isStale) return "stale"
  const activeCount = room.participants.length > 0
    ? room.participants.filter((participant) => participant.active).length
    : room.attendeeCount
  return activeCount >= 2 ? "live" : "solo"
}

function filterAdminReports(reports: ReportSummary[], query: string): ReportSummary[] {
  const normalized = query.trim().toLowerCase()
  if (!normalized) return reports
  return reports.filter((report) => [
    report.conversationCode,
    report.topicLabel,
    report.topicKeywords,
    report.reason,
    report.status,
    report.mode,
  ].some((value) => String(value || "").toLowerCase().includes(normalized)))
}

function topicTextOrAny(value: string | undefined | null, t: (value: LocalizedText) => string): string {
  const trimmed = String(value || "").trim()
  return trimmed || t(ui.topicPending)
}

function sessionTopicText(session: AdminDashboard["liveSessions"][number], t: (value: LocalizedText) => string): string {
  return topicTextOrAny(session.topicKeywords || session.topicLabel, t)
}

function participantTopicText(participant: { topicKeywords?: string; topicLabel?: string }, t: (value: LocalizedText) => string): string {
  return topicTextOrAny(participant.topicKeywords || participant.topicLabel, t)
}

function participantDisplayName(participant: Pick<RoomParticipant, "displayName" | "publicCode" | "code">, t: (value: LocalizedText) => string): string {
  return participant.displayName?.trim() || t(ui.member)
}

function participantPublicCode(participant: Pick<RoomParticipant, "publicCode" | "code">): string {
  return participant.publicCode?.trim() || participant.code
}

function participantInitial(participant: Pick<RoomParticipant, "displayName" | "publicCode" | "code">, t: (value: LocalizedText) => string): string {
  const label = participantDisplayName(participant, t)
  return [...label.trim()][0]?.toUpperCase() || "J"
}

function reportTopicText(report: ReportSummary, t: (value: LocalizedText) => string): string {
  return topicTextOrAny(report.topicKeywords || report.topicLabel, t)
}

function filterAdminReportedUsers(users: ReportedUserSummary[], query: string): ReportedUserSummary[] {
  const normalized = query.trim().toLowerCase()
  if (!normalized) return users
  return users.filter((user) => [
    user.subjectCode,
    user.subjectKey,
    user.blocked ? "blocked" : "reported",
  ].some((value) => String(value || "").toLowerCase().includes(normalized)))
}

function filterAdminUserBlocks(blocks: UserModerationBlock[], query: string): UserModerationBlock[] {
  const normalized = query.trim().toLowerCase()
  if (!normalized) return blocks
  return blocks.filter((block) => [
    block.subjectCode,
    block.subjectKey,
    block.reason,
  ].some((value) => String(value || "").toLowerCase().includes(normalized)))
}

function isAdminAccessDenied(error: string): boolean {
  const normalized = error.trim().toLowerCase()
  return normalized.includes("admin access disabled") ||
    normalized.includes("admin permission denied") ||
    normalized.includes("admin access is unavailable")
}

function isAdminAccessError(error: unknown): boolean {
  return (error instanceof ApiError && error.status === 403) || isAdminAccessDenied(errorText(error))
}

function isAuthRequiredError(error: unknown): boolean {
  const normalized = errorText(error).toLowerCase()
  return (error instanceof ApiError && error.status === 401) ||
    normalized.includes("missing bearer") ||
    normalized.includes("missing_bearer") ||
    normalized.includes("session required") ||
    normalized.includes("auth expired")
}

function adminLoadErrorMessage(error: unknown, t: (value: LocalizedText) => string): string {
  if (isAdminAccessError(error)) return t(ui.adminDenied)
  const message = errorText(error)
  if (!message || message === "[object Object]") return t(ui.adminDenied)
  return message
}

function errorText(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === "string") return error
  if (!error || typeof error !== "object") return ""
  const record = error as Record<string, unknown>
  return ["error", "code", "message", "reason"]
    .map((key) => record[key])
    .filter((value) => typeof value === "string" && value.trim())
    .map(String)
    .join(" ")
}

function withinAdminRange(createdAt: string, range: AdminRange): boolean {
  const days = range === "1d" ? 1 : range === "7d" ? 7 : 30
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000
  return new Date(createdAt).getTime() >= cutoff
}

function adminRangeLabel(range: AdminRange, t: (value: LocalizedText) => string): string {
  if (range === "1d") return t(ui.today)
  if (range === "7d") return t(ui.sevenDays)
  return t(ui.thirtyDays)
}

function clampReporterThreshold(value: number): number {
  if (!Number.isFinite(value)) return 10
  return Math.min(100, Math.max(10, Math.trunc(value)))
}

function appendAdminMetricSample(current: AdminMetricSample[], metrics: AdminDashboard["metrics"], timestamp: number): AdminMetricSample[] {
  const sample: AdminMetricSample = {
    timestamp,
    onlineNow: metrics.onlineNow,
    activeSessions: metrics.activeSessions,
    averageDurationSeconds: metrics.averageDurationSeconds,
    matchRatePercent: Math.round(metrics.matchRate * 100),
    reportsToday: metrics.reportsToday,
  }
  return [...current, sample].slice(-24)
}

function metricTrend(
  values: number[],
  locale: Locale,
  t: (value: LocalizedText) => string,
  formatter: (value: number, locale: Locale) => string = formatNumber,
): MetricTrend {
  if (values.length < 2) return { direction: "steady", label: t(ui.liveApi) }
  const previous = values[values.length - 2]
  const current = values[values.length - 1]
  const delta = current - previous
  if (Math.abs(delta) < 0.5) return { direction: "steady", label: t(ui.steady) }
  return {
    direction: delta > 0 ? "up" : "down",
    label: `${formatter(Math.abs(delta), locale)} ${delta > 0 ? t(ui.positiveMore) : t(ui.positiveLess)}`,
  }
}

function sparklinePoints(values: number[], width = 112, height = 34): string {
  if (values.length < 2) return `0,${height / 2} ${width},${height / 2}`
  const min = Math.min(...values)
  const max = Math.max(...values)
  if (max === min) return `0,${height / 2} ${width},${height / 2}`
  const range = max - min
  return values.map((value, index) => {
    const x = (index / (values.length - 1)) * width
    const y = height - 4 - ((value - min) / range) * (height - 8)
    return `${x.toFixed(2)},${y.toFixed(2)}`
  }).join(" ")
}

function AdminOverview({ data, metricHistory, pendingReview, reviewed, onReview }: {
  data: AdminDashboard
  metricHistory: AdminMetricSample[]
  pendingReview: string
  reviewed: string
  onReview: (report: ReportSummary, status: ReportStatus) => void
}) {
  const { locale, t } = useAppState()
  const series = (key: AdminMetricKey) => metricHistory.map((sample) => sample[key])
  return (
    <>
      <div className="metric-grid">
        <MetricCard
          label={t(ui.metricOnline)}
          value={formatNumber(data.metrics.onlineNow, locale)}
          detail={t(ui.liveApi)}
          history={series("onlineNow")}
          trend={metricTrend(series("onlineNow"), locale, t)}
        />
        <MetricCard
          label={t(ui.metricActive)}
          value={formatNumber(data.metrics.activeSessions, locale)}
          detail={t(ui.liveApi)}
          history={series("activeSessions")}
          trend={metricTrend(series("activeSessions"), locale, t)}
        />
        <MetricCard
          label={t(ui.metricAverage)}
          value={formatDuration(data.metrics.averageDurationSeconds, locale)}
          detail={t(ui.liveApi)}
          history={series("averageDurationSeconds")}
          trend={metricTrend(series("averageDurationSeconds"), locale, t, formatDuration)}
        />
        <MetricCard
          label={t(ui.metricMatch)}
          value={`${formatNumber(Math.round(data.metrics.matchRate * 100), locale)}٪`}
          detail={t(ui.liveApi)}
          history={series("matchRatePercent")}
          trend={metricTrend(series("matchRatePercent"), locale, t, formatPercentPoints)}
        />
        <MetricCard
          label={t(ui.metricReports)}
          value={formatNumber(data.metrics.reportsToday, locale)}
          detail={t(ui.liveApi)}
          history={series("reportsToday")}
          trend={metricTrend(series("reportsToday"), locale, t)}
        />
      </div>
      <div className="admin-main-grid">
        <AdminLiveSessions data={data} compact />
        <aside className="admin-side-stack">
          <section className="admin-panel reports-panel">
            <div className="report-title"><strong>{t(ui.reportQueue)}</strong><span>{formatNumber(data.metrics.pendingReports, locale)}</span></div>
            <div className="report-list">
              {data.reportQueue.slice(0, 3).map((report, index) => (
                <ReportQueueRow key={report.id} report={report} severe={index === 0} busy={pendingReview === report.id} onReview={(status) => onReview(report, status)} />
              ))}
              {!data.reportQueue.length && <div className="admin-empty">{t(ui.noReports)}</div>}
            </div>
            {reviewed && <div className="reviewed-note">{reviewed}</div>}
            <Link className="secondary-wide link-like" to="/admin/reports">{t(ui.seeAllReports)}</Link>
          </section>
          <HotTopics topics={data.hotTopics} />
        </aside>
      </div>
    </>
  )
}

function AdminLiveSessions({ data, compact = false }: { data: AdminDashboard; compact?: boolean }) {
  const { locale, t, f } = useAppState()
  return (
    <section className={compact ? "admin-panel live-panel" : "admin-panel full-panel live-panel"}>
      <div className="panel-title"><strong>{t(copy.admin.live)}</strong><span>{t(ui.liveApi)}</span></div>
      <div className="session-table header">
        <span>{t(ui.code)}</span><span>{t(ui.topicLabel)}</span><span>{t(ui.mode)}</span><span>{t(ui.duration)}</span><span>{t(ui.vibe)}</span><span>{t(ui.status)}</span><span />
      </div>
      {data.liveSessions.map((session) => (
        <div className="session-table" key={session.conversationCode}>
          <span className="code-cell">{session.conversationCode}</span>
          <span className="topic-cell">{sessionTopicText(session, t)}</span>
          <span>{modeLabel(session.mode, t)}</span>
          <span>{relativeMinutes(session.createdAt, locale)}</span>
          <span>{session.matchScore ? `${toPercent(session.matchScore, locale)}٪` : "—"}</span>
          <span><span className="status-chip">{session.endReason ? t(ui.status) : t(ui.metricActiveShort)}</span></span>
          <span>•••</span>
        </div>
      ))}
      {!data.liveSessions.length && <div className="admin-empty">{t(ui.noLiveSessions)}</div>}
      {data.metrics.devActiveSessions > 0 && (
        <div className="admin-muted-note">{f(ui.devLiveSessions, { count: formatNumber(data.metrics.devActiveSessions, locale) })}</div>
      )}
    </section>
  )
}

function AdminRoomsPage({ rooms }: { rooms: AdminRoomSummary[] }) {
  const { locale, t } = useAppState()
  const [filter, setFilter] = useState<AdminRoomFilter>("all")
  const [closedSessionIds, setClosedSessionIds] = useState<string[]>([])
  const [pendingClose, setPendingClose] = useState("")
  const [notice, setNotice] = useState("")
  const visibleRooms = rooms
    .filter((room) => !closedSessionIds.includes(room.sessionId))
    .filter((room) => filter === "all" || adminRoomStatus(room) === filter)
  const filterOptions: Array<{ value: AdminRoomFilter; label: string; count: number }> = [
    { value: "all", label: t(ui.roomsAll), count: rooms.length },
    { value: "live", label: t(ui.roomsLive), count: rooms.filter((room) => adminRoomStatus(room) === "live").length },
    { value: "solo", label: t(ui.roomsSolo), count: rooms.filter((room) => adminRoomStatus(room) === "solo").length },
    { value: "stale", label: t(ui.roomsStale), count: rooms.filter((room) => adminRoomStatus(room) === "stale").length },
  ]
  const closeRoom = async (sessionId: string) => {
    setPendingClose(sessionId)
    setNotice("")
    try {
      await closeAdminRoom(sessionId)
      setClosedSessionIds((current) => [...current, sessionId])
      setNotice(t(ui.staleRoomClosed))
    } catch (err) {
      setNotice(err instanceof Error ? err.message : t(copy.errors.connection))
    } finally {
      setPendingClose("")
    }
  }
  return (
    <section className="admin-panel full-panel rooms-panel">
      <div className="panel-title"><strong>{t(ui.adminRooms)}</strong><span>{t(ui.liveApi)}</span></div>
      <div className="room-filter-tabs" role="tablist" aria-label={t(ui.filter)}>
        {filterOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            className={filter === option.value ? "active" : ""}
            aria-pressed={filter === option.value}
            onClick={() => setFilter(option.value)}
          >
            {option.label}<span>{formatNumber(option.count, locale)}</span>
          </button>
        ))}
      </div>
      {notice && <div className="reviewed-note">{notice}</div>}
      <div className="room-admin-list">
        {visibleRooms.map((room) => {
          const participantActiveCount = room.participants.filter((participant) => participant.active).length
          const activeCount = room.participants.length > 0 ? participantActiveCount : room.attendeeCount
          const status = adminRoomStatus(room)
          const isLiveConversation = status === "live"
          return (
            <article key={room.sessionId} className={status === "stale" ? "room-admin-card stale" : isLiveConversation ? "room-admin-card" : "room-admin-card solo"}>
              <div className="room-admin-main">
                <span className="code-cell">{room.conversationCode}</span>
                <strong>{reportTopicText({ topicLabel: room.topicLabel, topicKeywords: room.topicKeywords } as ReportSummary, t)}</strong>
                <small>{modeLabel(room.mode, t)} · {relativeMinutes(room.createdAt, locale)}</small>
              </div>
              <div className="room-admin-metrics">
                <span className={`room-state ${status}`}>{status === "stale" ? t(ui.roomStale) : isLiveConversation ? t(ui.roomLive) : t(ui.roomSolo)}</span>
                <span>{formatNumber(activeCount, locale)} {t(ui.attendees)}</span>
                <span className={room.liveness?.momentum || "steady"}>{formatRoomLiveness(room.liveness?.remainingSeconds || 0, locale)} · {momentumLabel(room.liveness?.momentum || "steady", t)}</span>
                <span>{room.openSeatExpiresAt ? `${t(ui.openSeatReady)} · ${formatRoomLiveness(secondsUntil(room.openSeatExpiresAt), locale)}` : t(ui.noOpenSeat)}</span>
              </div>
              <div className="room-admin-participants">
                {room.participants.map((participant) => (
                  <b key={participant.id} className={participant.active ? "" : "left"} title={participantTopicText(participant, t)}>
                    <span>{participantDisplayName(participant, t)}</span>
                    <small>{participantPublicCode(participant)}</small>
                    <em>{participantTopicText(participant, t)}</em>
                  </b>
                ))}
              </div>
              {room.activeVote && (
                <div className="room-admin-vote">
                  <span>{room.activeVote.status}</span>
                  <b>{formatNumber(room.activeVote.yesVotes, locale)} / {formatNumber(room.activeVote.eligibleVoterCount, locale)}</b>
                </div>
              )}
              {status === "stale" && (
                <button className="room-force-close" type="button" disabled={pendingClose === room.sessionId} onClick={() => void closeRoom(room.sessionId)}>
                  {pendingClose === room.sessionId ? t(copy.actions.pending) : t(ui.closeStaleRoom)}
                </button>
              )}
            </article>
          )
        })}
        {!visibleRooms.length && <div className="admin-empty">{t(ui.noRooms)}</div>}
      </div>
    </section>
  )
}

function AdminReportsPage({ reports, pendingReview, reviewed, onReview }: {
  reports: ReportSummary[]
  pendingReview: string
  reviewed: string
  onReview: (report: ReportSummary, status: ReportStatus) => void
}) {
  const { t } = useAppState()
  return (
    <section className="admin-panel full-panel">
      <div className="panel-title"><strong>{t(copy.admin.queue)}</strong><span>{t(ui.liveApi)}</span></div>
      <div className="admin-report-table header">
        <span>{t(ui.code)}</span><span>{t(copy.reports.reason)}</span><span>{t(ui.topicLabel)}</span><span>{t(ui.status)}</span><span />
      </div>
      {reports.map((report) => (
        <div className="admin-report-table" key={report.id}>
          <Link className="code-cell" to={`/admin/reports/${report.id}`}>{report.conversationCode}</Link>
          <span>{reportReason(report.reason, t)}</span>
          <span>{reportTopicText(report, t)}</span>
          <span><span className={`history-status ${report.status}`}>{report.status === "valid" ? t(copy.reports.valid) : report.status === "invalid" ? t(copy.reports.invalid) : t(copy.reports.pending)}</span></span>
          <span className="table-actions">
            <button
              disabled={pendingReview === report.id}
              className={report.status === "valid" ? "selected" : ""}
              aria-pressed={report.status === "valid"}
              onClick={() => onReview(report, report.status === "valid" ? "pending" : "valid")}
            >{t(ui.valid)}</button>
            <button
              disabled={pendingReview === report.id}
              className={report.status === "invalid" ? "selected" : ""}
              aria-pressed={report.status === "invalid"}
              onClick={() => onReview(report, report.status === "invalid" ? "pending" : "invalid")}
            >{t(ui.invalid)}</button>
          </span>
        </div>
      ))}
      {!reports.length && <div className="admin-empty">{t(ui.noReports)}</div>}
      {reviewed && <div className="reviewed-note">{reviewed}</div>}
    </section>
  )
}

function AdminReportReview({ report, pending, reviewed, onReview }: {
  report: ReportSummary
  pending: boolean
  reviewed: string
  onReview: (status: ReportStatus) => void
}) {
  const { locale, t, f } = useAppState()
  const targetLabel = [report.targetDisplayName, report.targetPublicCode || report.targetParticipantCode].filter(Boolean).join(" · ") || t(copy.admin.room_participant)
  return (
    <div className="admin-review-grid">
      <section className="admin-panel report-review-card">
        <div className="report-review-header">
          <span className={`history-status ${report.status}`}>{report.status === "valid" ? t(copy.reports.valid) : report.status === "invalid" ? t(copy.reports.invalid) : t(copy.reports.pending)}</span>
          <strong>{report.conversationCode}</strong>
        </div>
        <dl className="report-facts">
          <div><dt>{t(copy.admin.reporter)}</dt><dd>{t(ui.reporterPrivate)}</dd></div>
          <div><dt>{t(copy.admin.reported)}</dt><dd>{targetLabel}</dd></div>
          <div><dt>{t(copy.reports.reason)}</dt><dd>{reportReason(report.reason, t)}</dd></div>
          <div><dt>{t(ui.topicLabel)}</dt><dd>{reportTopicText(report, t)}</dd></div>
          <div><dt>{t(ui.duration)}</dt><dd>{relativeReportTime(report.createdAt, locale, f)}</dd></div>
          <div><dt>{t(ui.evidenceConsent)}</dt><dd>{report.evidenceConsent ? `${t(ui.consentCaptured)}${report.evidenceConsentedAt ? ` · ${relativeReportTime(report.evidenceConsentedAt, locale, f)}` : ""}` : t(ui.consentMissing)}</dd></div>
          <div><dt>{t(ui.evidenceClip)}</dt><dd>{report.evidence ? `${report.evidence.kind === "video" ? t(ui.evidenceVideo) : t(ui.evidenceAudio)} · ${formatEvidenceSize(report.evidence.sizeBytes)} · ${formatNumber(report.evidence.durationSeconds || 0, locale)}s` : t(ui.evidenceUnavailable)}</dd></div>
        </dl>
        {report.evidence?.dataUrl && (
          <div className="report-evidence-box">
            <span>{t(ui.evidenceClip)}</span>
            {report.evidence.kind === "video" ? (
              <video controls preload="metadata" src={report.evidence.dataUrl} />
            ) : (
              <audio controls preload="metadata" src={report.evidence.dataUrl} />
            )}
          </div>
        )}
        {report.evidence && !report.evidence.dataUrl && (
          <div className="report-evidence-box metadata-only">
            <span>{t(ui.evidenceClip)}</span>
            <p>{t(ui.evidenceMetadataOnly)}</p>
          </div>
        )}
        {report.notes && (
          <div className="report-note-box">
            <span>{t(ui.reportNotes)}</span>
            <p>{report.notes}</p>
          </div>
        )}
      </section>
      <aside className="admin-panel decision-panel">
        <h2>{t(copy.admin.decision)}</h2>
        <button disabled={pending} className={report.status === "valid" ? "decision-valid selected" : "decision-valid"} aria-pressed={report.status === "valid"} onClick={() => onReview(report.status === "valid" ? "pending" : "valid")}>{pending ? t(copy.actions.pending) : t(copy.admin.valid)}</button>
        <button disabled={pending} className={report.status === "invalid" ? "decision-invalid selected" : "decision-invalid"} aria-pressed={report.status === "invalid"} onClick={() => onReview(report.status === "invalid" ? "pending" : "invalid")}>{pending ? t(copy.actions.pending) : t(copy.admin.invalid)}</button>
        {report.status !== "pending" && <button disabled={pending} className="decision-reset" onClick={() => onReview("pending")}>{t(ui.resetReview)}</button>}
        {reviewed && <div className="reviewed-note">{reviewed}</div>}
        <p>{t(copy.admin.identity_rule)}</p>
      </aside>
    </div>
  )
}

function AdminTopicsPage({ data, bannedTopics, pending, notice, onBan, onUnban }: {
  data: AdminDashboard
  bannedTopics: BannedTopic[]
  pending: string
  notice: string
  onBan: (topicText: string) => void
  onUnban: (normalizedTopic: string) => void
}) {
  const { locale, t } = useAppState()
  const [topicText, setTopicText] = useState("")
  const [topicSuggestions, setTopicSuggestions] = useState<TopicSuggestion[]>([])
  const [suggestionsLoading, setSuggestionsLoading] = useState(false)
  const activeBans = bannedTopics.filter((topic) => topic.enabled)
  const isActiveBan = (label: string) => activeBans.some((topic) => topic.normalizedTopic === label || topic.label === label)
  const trimmedTopicText = topicText.trim()
  useEffect(() => {
    const query = topicText.trim()
    if (query.length < 3) {
      setTopicSuggestions([])
      setSuggestionsLoading(false)
      return
    }
    let cancelled = false
    setSuggestionsLoading(true)
    const timer = window.setTimeout(() => {
      getAdminTopicSuggestions(query)
        .then((items) => {
          if (!cancelled) setTopicSuggestions(items)
        })
        .catch(() => {
          if (!cancelled) setTopicSuggestions([])
        })
        .finally(() => {
          if (!cancelled) setSuggestionsLoading(false)
        })
    }, 180)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [topicText])
  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (!trimmedTopicText) return
    onBan(trimmedTopicText)
    setTopicText("")
    setTopicSuggestions([])
  }
  return (
    <div className="admin-topic-stack">
      <section className="admin-panel full-panel">
        <div className="panel-title"><strong>{t(copy.admin.topics)}</strong><span>{t(ui.hotTopics)}</span></div>
        <form className="topic-ban-form" onSubmit={submit}>
          <div className="topic-ban-field">
            <input
              value={topicText}
              onChange={(event) => setTopicText(event.target.value)}
              placeholder={t(ui.topicBanPlaceholder)}
              aria-controls="topic-ban-suggestions"
              aria-expanded={trimmedTopicText.length >= 3 && topicSuggestions.length > 0}
            />
            {trimmedTopicText.length >= 3 && (suggestionsLoading || topicSuggestions.length > 0) && (
              <div className="topic-suggestion-list" id="topic-ban-suggestions" role="listbox">
                {suggestionsLoading && <span>{t(ui.loading)}</span>}
                {!suggestionsLoading && topicSuggestions.map((topic) => (
                  <button key={topic.label} type="button" role="option" onClick={() => setTopicText(topic.label)}>
                    <strong>{topic.label}</strong>
                    <small>{formatNumber(topic.count, locale)}</small>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button type="submit" className="topic-ban-icon-button" aria-label={t(ui.topicBan)} title={t(ui.topicBan)} disabled={!trimmedTopicText || pending === `topic:${trimmedTopicText}`}>
            <BanIcon aria-hidden="true" />
            <span className="sr-only">{t(ui.topicBan)}</span>
          </button>
        </form>
        <div className="topic-admin-grid">
          {data.hotTopics.map((topic) => {
            const active = isActiveBan(topic.label)
            return (
              <article key={topic.label} className={active ? "topic-admin-card banned" : "topic-admin-card"}>
                <div>
                  <strong>{topic.label}</strong>
                  <span>{formatNumber(topic.count, locale)}</span>
                </div>
                <button type="button" className="topic-ban-icon-button" aria-label={active ? t(ui.activeBan) : t(ui.topicBan)} title={active ? t(ui.activeBan) : t(ui.topicBan)} disabled={active || pending === `topic:${topic.label}`} onClick={() => onBan(topic.label)}>
                  <BanIcon aria-hidden="true" />
                  <span className="sr-only">{active ? t(ui.activeBan) : t(ui.topicBan)}</span>
                </button>
              </article>
            )
          })}
          {!data.hotTopics.length && <div className="admin-empty wide">{t(ui.noHotTopics)}</div>}
        </div>
      </section>
      <section className="admin-panel full-panel">
        <div className="panel-title"><strong>{t(ui.bannedTopics)}</strong><span>{t(ui.liveApi)}</span></div>
        <div className="topic-ban-list">
          {bannedTopics.map((topic) => (
            <div key={topic.normalizedTopic} className={topic.enabled ? "topic-ban-row" : "topic-ban-row restored"}>
              <div>
                <strong>{topic.label || topic.normalizedTopic}</strong>
                <span>{topic.enabled ? t(ui.activeBan) : t(ui.restored)}</span>
              </div>
              <button type="button" disabled={!topic.enabled || pending === `topic:${topic.normalizedTopic}`} onClick={() => onUnban(topic.normalizedTopic)}>
                {t(ui.topicRestore)}
              </button>
            </div>
          ))}
          {!bannedTopics.length && <div className="admin-empty compact">{t(ui.noBannedTopics)}</div>}
        </div>
        {notice && <div className="reviewed-note">{notice}</div>}
      </section>
    </div>
  )
}

type AdminRange = "1d" | "7d" | "30d"

function AdminReportedUsersPage({ users, blockedUsers, range, minReporters, loading, pending, notice, onRangeChange, onMinReportersChange, onBlock, onUnblock }: {
  users: ReportedUserSummary[]
  blockedUsers: UserModerationBlock[]
  range: AdminRange
  minReporters: number
  loading: boolean
  pending: string
  notice: string
  onRangeChange: (range: AdminRange) => void
  onMinReportersChange: (value: number) => void
  onBlock: (subjectKey: string) => void
  onUnblock: (subjectKey: string) => void
}) {
  const { locale, t, f } = useAppState()
  const unblockClaims = blockedUsers.filter((block) => block.unblockClaimStatus === "pending")
  const [draftMinReporters, setDraftMinReporters] = useState(String(minReporters))
  useEffect(() => setDraftMinReporters(String(minReporters)), [minReporters])
  const commitMinReporters = (value = Number(draftMinReporters)) => {
    const next = clampReporterThreshold(value)
    setDraftMinReporters(String(next))
    onMinReportersChange(next)
  }
  const stepMinReporters = (delta: number) => {
    const next = clampReporterThreshold((Number(draftMinReporters) || minReporters) + delta)
    setDraftMinReporters(String(next))
    onMinReportersChange(next)
  }
  return (
    <div className="admin-user-stack">
      <section className="admin-panel full-panel reported-users-panel">
        <div className="panel-title admin-users-title">
          <div><strong>{t(copy.admin.users)}</strong><span>{t(ui.userBlockHint)}</span></div>
          <div className="admin-user-filters">
            <div className="period-tabs admin-range-tabs">
              {(["1d", "7d", "30d"] as AdminRange[]).map((option) => (
                <button key={option} type="button" className={range === option ? "active" : ""} onClick={() => onRangeChange(option)}>
                  {adminRangeLabel(option, t)}
                </button>
              ))}
            </div>
            <label>
              <span>{t(ui.minReporters)}</span>
              <span className="admin-stepper">
                <button type="button" aria-label={t(ui.decreaseReporters)} disabled={minReporters <= 10} onClick={() => stepMinReporters(-1)}>-</button>
                <input
                  type="number"
                  inputMode="numeric"
                  min={10}
                  max={100}
                  step={1}
                  value={draftMinReporters}
                  onChange={(event) => {
                    const digits = event.target.value.replace(/\D/g, "").slice(0, 3)
                    setDraftMinReporters(digits ? String(clampReporterThreshold(Number(digits))) : "")
                  }}
                  onBlur={() => commitMinReporters()}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault()
                      commitMinReporters()
                    }
                  }}
                />
                <button type="button" aria-label={t(ui.increaseReporters)} disabled={minReporters >= 100} onClick={() => stepMinReporters(1)}>+</button>
              </span>
              <button type="button" className="admin-filter-apply" onClick={() => commitMinReporters()}>{t(ui.applyFilters)}</button>
            </label>
            <span className={loading ? "admin-filter-state busy" : "admin-filter-state"}>{loading ? t(ui.filterUpdating) : t(ui.filtersApplied)}</span>
            <p className="admin-filter-hint">{t(ui.distinctReportersHint)}</p>
          </div>
        </div>
        <div className="reported-user-table header">
          <span>{t(ui.code)}</span><span>{t(ui.reportedUserRisk)}</span><span>{t(ui.status)}</span><span />
        </div>
        {users.map((user) => (
          <div className="reported-user-table" key={user.subjectKey}>
            <span className="code-cell">{user.subjectCode}</span>
            <div className="risk-cell">
              <strong>{formatNumber(user.reporterCount, locale)} {t(ui.reporters)}</strong>
              <small>{formatNumber(user.reportCount, locale)} {t(ui.reportEvents)} · {formatNumber(user.sessionCount, locale)} {t(ui.sessionsCount)} · {relativeReportTime(user.lastReportedAt, locale, f)}</small>
            </div>
            <span><span className={user.blocked ? "history-status valid" : "history-status pending"}>{user.blocked ? t(ui.activeBan) : t(copy.reports.pending)}</span></span>
            <span className="table-actions">
              {user.blocked ? (
                <button type="button" disabled={pending === user.subjectKey} onClick={() => onUnblock(user.subjectKey)}>{t(ui.unblockUser)}</button>
              ) : (
                <button type="button" disabled={pending === user.subjectKey} onClick={() => onBlock(user.subjectKey)}>{t(ui.blockUser)}</button>
              )}
            </span>
          </div>
        ))}
        {!users.length && <div className="admin-empty">{t(ui.noReportedUsers)}</div>}
        {notice && <div className="reviewed-note">{notice}</div>}
      </section>
      <section className="admin-panel full-panel">
        <div className="panel-title"><strong>{t(ui.unblockClaims)}</strong><span>{formatNumber(unblockClaims.length, locale)}</span></div>
        <div className="blocked-user-list">
          {unblockClaims.map((block) => (
            <div className="blocked-user-row claim" key={`claim-${block.subjectKey}`}>
              <div>
                <strong>{block.subjectCode}</strong>
                <span>{t(ui.unblockClaimHint)}</span>
                <p>{block.unblockClaim || t(ui.claimReviewRequested)} · {block.unblockClaimedAt ? relativeReportTime(block.unblockClaimedAt, locale, f) : t(ui.claimReviewRequested)}</p>
              </div>
              <button type="button" disabled={pending === block.subjectKey} onClick={() => onUnblock(block.subjectKey)}>{t(ui.unblockUser)}</button>
            </div>
          ))}
          {!unblockClaims.length && <div className="admin-empty compact">{t(ui.noUnblockClaims)}</div>}
        </div>
      </section>
      <section className="admin-panel full-panel">
        <div className="panel-title"><strong>{t(ui.blockedUsers)}</strong><span>{t(ui.liveApi)}</span></div>
        <div className="blocked-user-list">
          {blockedUsers.map((block) => (
            <div className="blocked-user-row" key={block.subjectKey}>
              <div>
                <strong>{block.subjectCode}</strong>
                <span>{block.reason || t(ui.blockUser)}</span>
                {block.unblockClaimStatus === "pending" && <p>{t(ui.claimReviewRequested)} · {block.unblockClaimedAt ? relativeReportTime(block.unblockClaimedAt, locale, f) : t(ui.claimReviewRequested)}</p>}
              </div>
              <button type="button" disabled={pending === block.subjectKey} onClick={() => onUnblock(block.subjectKey)}>{t(ui.unblockUser)}</button>
            </div>
          ))}
          {!blockedUsers.length && <div className="admin-empty compact">{t(ui.noBlockedUsers)}</div>}
        </div>
      </section>
    </div>
  )
}

function AdminSecurityPage({ reports }: { reports: ReportSummary[] }) {
  const { locale, t } = useAppState()
  const [range, setRange] = useState<AdminRange>("1d")
  const filtered = reports.filter((report) => withinAdminRange(report.createdAt, range))
  const total = filtered.length
  const pending = filtered.filter((report) => report.status === "pending").length
  const valid = filtered.filter((report) => report.status === "valid").length
  const invalid = filtered.filter((report) => report.status === "invalid").length
  const blocked = filtered.filter((report) => report.blocked).length
  const reasonCounts = reportReasons(t).map((reason) => ({
    label: reason.label,
    count: filtered.filter((report) => reportReason(report.reason, t) === reason.label).length,
  })).filter((item) => item.count > 0)
  return (
    <section className="admin-panel full-panel settings-admin-panel">
      <div className="panel-title">
        <strong>{t(copy.admin.security)}</strong>
        <div className="period-tabs admin-range-tabs">
          {(["1d", "7d", "30d"] as AdminRange[]).map((option) => (
            <button key={option} type="button" className={range === option ? "active" : ""} onClick={() => setRange(option)}>
              {adminRangeLabel(option, t)}
            </button>
          ))}
        </div>
      </div>
      <div className="metric-grid security-metrics">
        <MetricCard label={t(ui.reportTotal)} value={formatNumber(total, locale)} detail={adminRangeLabel(range, t)} />
        <MetricCard label={t(copy.reports.pending)} value={formatNumber(pending, locale)} detail={t(ui.liveApi)} />
        <MetricCard label={t(ui.confirmedReports)} value={formatNumber(valid, locale)} detail={adminRangeLabel(range, t)} />
        <MetricCard label={t(ui.dismissedReports)} value={formatNumber(invalid, locale)} detail={adminRangeLabel(range, t)} />
        <MetricCard label={t(ui.blockedReports)} value={formatNumber(blocked, locale)} detail={adminRangeLabel(range, t)} />
      </div>
      <div className="admin-setting-list">
        <AdminSettingRow title={t(copy.admin.threshold)} value={`${formatNumber(3, locale)} / 10 min`} readonly />
        <AdminSettingRow title={t(copy.admin.evidence)} value={t(ui.readOnly)} readonly />
        <AdminSettingRow title={t(copy.help.report_issue)} value={formatNumber(total, locale)} readonly />
      </div>
      <div className="security-reasons">
        {reasonCounts.map((item) => (
          <div key={item.label} className="hot-row">
            <span>{item.label}</span>
            <b><i style={{ width: `${Math.max(18, (item.count / Math.max(total, 1)) * 100)}%` }} /></b>
            <em>{formatNumber(item.count, locale)}</em>
          </div>
        ))}
        {!reasonCounts.length && <div className="admin-empty compact">{t(ui.noSecurityEvents)}</div>}
      </div>
    </section>
  )
}

function AdminSettingsPage() {
  const { locale, theme, setLocale, setTheme, tweaks, setTweaks, t } = useAppState()
  const update = <K extends keyof AppTweaks>(key: K, value: AppTweaks[K]) => {
    setTweaks((current) => ({ ...current, [key]: value }))
  }
  return (
    <section className="admin-panel full-panel settings-admin-panel">
      <div className="panel-title"><strong>{t(copy.admin.settings)}</strong><span>{t(ui.adminLocalControls)}</span></div>
      <div className="admin-settings-controls">
        <TweakChoice
          label={t(copy.settings.language)}
          options={[
            { value: "fa", label: t(ui.persian) },
            { value: "en", label: t(ui.english) },
          ]}
          value={locale}
          onChange={(value) => setLocale(value as Locale)}
        />
        <TweakChoice
          label={t(copy.settings.theme)}
          options={[
            { value: "light", label: t(ui.lightTheme) },
            { value: "dark", label: t(ui.darkTheme) },
          ]}
          value={theme}
          onChange={(value) => setTheme(value as ThemeMode)}
        />
        <section className="settings-group admin-accent-group">
          <h2>{t(copy.settings.vibe)}</h2>
          <div className="settings-swatches">
            {(["coral", "violet", "teal", "sunset"] as Vibe[]).map((vibe) => (
              <button
                key={vibe}
                type="button"
                className={tweaks.vibe === vibe ? `swatch ${vibe} active` : `swatch ${vibe}`}
                aria-label={t(ui[vibe])}
                onClick={() => update("vibe", vibe)}
              />
            ))}
          </div>
        </section>
      </div>
    </section>
  )
}

function AdminSettingRow({ title, value, readonly = false }: { title: string; value: string; readonly?: boolean }) {
  return (
    <div className="admin-setting-row">
      <span>{title}</span>
      <b className={readonly ? "readonly" : ""}>{value}</b>
    </div>
  )
}

function ClientHeader() {
  const { locale, setLocale, t } = useAppState()
  const auth = useAuth()
  const [open, setOpen] = useState(false)
  const [showSignIn, setShowSignIn] = useState(false)
  const [me, setMe] = useState<MeResponse | null>(null)
  const [unreadNotifications, setUnreadNotifications] = useState(0)
  useEffect(() => {
    if (auth.status !== "signed-in") {
      setMe(null)
      return
    }
    let cancelled = false
    getMe()
      .then((profile) => {
        if (!cancelled) setMe(profile)
      })
      .catch(() => {
        if (!cancelled) setMe(null)
      })
    return () => {
      cancelled = true
    }
	}, [auth.status, auth.user?.avatarPreset])
  useEffect(() => {
    if (auth.status !== "signed-in") {
      setUnreadNotifications(0)
      return undefined
    }
    let cancelled = false
    const load = () => getNotifications()
      .then((result) => { if (!cancelled) setUnreadNotifications(result.unreadCount) })
      .catch(() => undefined)
    void load()
    const timer = window.setInterval(() => void load(), 15000)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [auth.status])
  const displayUser = {
    displayName: me?.displayName || auth.user?.displayName || auth.user?.email || "",
    avatarUrl: me?.avatarUrl || auth.user?.avatarUrl || "",
	avatarPreset: auth.user?.avatarPreset || me?.avatarPreset || "",
  }
  const profileLabel = t(ui.profileMenu)
  return (
    <header className="client-header">
      <LogoMark />
      <strong>{t(copy.brand.name)}</strong>
      <span className="header-spacer" />
      {auth.status === "signed-in" ? (
        <>
          <Link className="notification-bell" to="/notifications" aria-label={t(ui.notifications)} title={t(ui.notifications)}>
            <BellIcon aria-hidden="true" />
            {unreadNotifications > 0 && <span>{Math.min(unreadNotifications, 99)}</span>}
          </Link>
          <div className="profile-menu">
            <button
              type="button"
              className="avatar-dot"
              aria-haspopup="menu"
              aria-expanded={open}
              aria-label={profileLabel}
              title={profileLabel}
              onClick={() => setOpen((value) => !value)}
            >
              <AvatarFace presetId={displayUser.avatarPreset} />
            </button>
            {open && (
              <nav className="profile-dropdown" role="menu">
                <Link role="menuitem" to="/history" onClick={() => setOpen(false)}><HistoryIcon aria-hidden="true" />{t(ui.history)}</Link>
                <Link role="menuitem" to="/contacts" onClick={() => setOpen(false)}><UsersIcon aria-hidden="true" />{t(ui.contacts)}</Link>
                <Link role="menuitem" to="/settings" onClick={() => setOpen(false)}><SettingsIcon aria-hidden="true" />{t(copy.settings.title)}</Link>
                <Link role="menuitem" to="/help" onClick={() => setOpen(false)}>{t(copy.help.title)}</Link>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false)
                    void auth.signOut()
                  }}
                >
                  {t(ui.signOut)}
                </button>
              </nav>
            )}
          </div>
        </>
      ) : (
        <div className="signed-out-actions">
          <button
            type="button"
            className="header-language-toggle"
            aria-label={locale === "fa" ? t(ui.switchToEnglish) : t(ui.switchToPersian)}
            title={locale === "fa" ? t(ui.switchToEnglish) : t(ui.switchToPersian)}
            onClick={() => setLocale(locale === "fa" ? "en" : "fa")}
          >
            {locale === "fa" ? "EN" : "FA"}
          </button>
          <button type="button" className="header-sign-in" disabled={auth.status === "checking"} onClick={() => setShowSignIn(true)}>
            {auth.status === "checking" ? t(copy.actions.pending) : t(ui.signInHeader)}
          </button>
        </div>
      )}
      {showSignIn && <AuthModal onClose={() => setShowSignIn(false)} />}
    </header>
  )
}

function TweakPanel() {
  const { tweaks, setTweaks, t } = useAppState()
  const update = <K extends keyof AppTweaks>(key: K, value: AppTweaks[K]) => {
    setTweaks((current) => ({ ...current, [key]: value }))
  }
  return (
    <div className="tweak-popover">
      <div className="tweak-title">{t(ui.settingsTitle)}</div>
      <div className="tweak-section">
        <span>{t(ui.accent)}</span>
        <div className="tweak-swatches">
          {(["coral", "violet", "teal", "sunset"] as Vibe[]).map((vibe) => (
            <button
              key={vibe}
              type="button"
              className={tweaks.vibe === vibe ? `swatch ${vibe} active` : `swatch ${vibe}`}
              aria-label={t(ui[vibe])}
              onClick={() => update("vibe", vibe)}
            />
          ))}
        </div>
      </div>
      <TweakChoice
        label={t(ui.energy)}
        options={[
          { value: "calm", label: t(ui.calm) },
          { value: "balanced", label: t(ui.balanced) },
          { value: "lively", label: t(ui.lively) },
        ]}
        value={tweaks.energy}
        onChange={(value) => update("energy", value as Energy)}
      />
      <TweakChoice
        label={t(ui.matchingStyle)}
        options={[
          { value: "radar", label: t(ui.radar) },
          { value: "constellation", label: t(ui.constellation) },
          { value: "pulse", label: t(ui.pulse) },
        ]}
        value={tweaks.matchingMotion}
        onChange={(value) => update("matchingMotion", value as MatchingMotion)}
      />
      <TweakChoice
        label={t(ui.liveLayout)}
        options={[
          { value: "full", label: t(ui.layoutFull) },
          { value: "orbs", label: t(ui.layoutOrbs) },
          { value: "minimal", label: t(ui.layoutMinimal) },
        ]}
        value={tweaks.liveLayout}
        onChange={(value) => update("liveLayout", value as LiveLayout)}
      />
      <Link className="tweak-report-link" to="/reports"><FlagIcon aria-hidden="true" />{t(ui.myReports)}</Link>
    </div>
  )
}

function TweakChoice({ label, options, value, onChange, disabled = false }: {
  label: string
  options: Array<{ value: string; label: string }>
  value: string
  onChange: (value: string) => void
  disabled?: boolean
}) {
  return (
    <div className={disabled ? "tweak-section disabled" : "tweak-section"}>
      <span>{label}</span>
      <div className="tweak-options">
        {options.map((option) => (
          <button key={option.value} type="button" disabled={disabled} className={value === option.value ? "active" : ""} onClick={() => onChange(option.value)}>
            {option.label}
          </button>
        ))}
      </div>
    </div>
  )
}

function MobileTopBar({ title, backTo }: { title: string; backTo: string }) {
  return (
    <header className="mobile-topbar">
      <Link to={backTo} aria-label="back" className="back-chevron"><ArrowLeftIcon aria-hidden="true" /></Link>
      <strong>{title}</strong>
    </header>
  )
}

function LogoMark() {
  return (
    <span className="logo-mark" aria-hidden="true">
      <svg viewBox="0 0 88 88" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path className="logo-mark-wave-left" d="M22 29C12 39 12 51 22 61" />
        <path className="logo-mark-wave-right" d="M66 29C76 39 76 51 66 61" />
        <path className="logo-mark-j-curve" d="M43 24V50C43 60 35 67 24 67" />
        <path className="logo-mark-j-stem" d="M53 24V65" />
        <circle className="logo-mark-dot" cx="64" cy="25" r="6" />
        <circle className="logo-mark-dot-shine" cx="66" cy="23" r="2.1" />
      </svg>
    </span>
  )
}

function ModeSwitch({ value, active = true, onChange }: { value: Mode; active?: boolean; onChange: (mode: Mode) => void }) {
  const { t } = useAppState()
  return (
    <div className="segmented-mode" role="tablist" aria-label="call mode">
      <button className={active && value === "voice" ? "active" : ""} onClick={() => onChange("voice")}><MicIcon aria-hidden="true" />{t(copy.home.mode_voice)}</button>
      <button className={active && value === "video" ? "active" : ""} onClick={() => onChange("video")}><VideoIcon aria-hidden="true" />{t(copy.home.mode_video)}</button>
    </div>
  )
}

function TopicChips({ value, activeValue = value, onChange, onSelect, counts = false }: {
  value: string
  activeValue?: string
  onChange: (value: string) => void
  onSelect?: (value: string) => void
  counts?: boolean
}) {
  const { locale } = useAppState()
  const [suggestions, setSuggestions] = useState<TopicSuggestion[]>([])
  useEffect(() => {
    if (!counts) return undefined
    let cancelled = false
    const query = value.trim()
    getTopicSuggestions(query.length >= 3 ? query : "")
      .then((items) => {
        if (!cancelled) setSuggestions(items)
      })
      .catch(() => {
        if (!cancelled) setSuggestions([])
      })
    return () => {
      cancelled = true
    }
  }, [counts, value])
  const rows = counts && suggestions.length > 0
    ? suggestions.map((item) => ({ label: item.label, count: item.count }))
    : copy.topic.chips.map((chip) => ({ label: text(chip, locale), count: 0 }))
  return (
    <div className="topic-chips">
      {rows.map((row) => {
        const label = row.label
        return (
          <button key={label} className={activeValue === label ? "chip active" : "chip"} onClick={() => { onChange(label); onSelect?.(label) }}>
            {label}
            {counts && row.count > 0 && <span>{formatNumber(row.count, locale)}</span>}
          </button>
        )
      })}
    </div>
  )
}

function remoteParticipantsFromStream(stream: MediaStream | null): RemoteMediaParticipant[] {
  if (!stream) return []
  const audioTracks = stream.getAudioTracks().filter((track) => track.readyState === "live")
  const videoTracks = stream.getVideoTracks().filter((track) => track.readyState === "live")
  if (audioTracks.length === 0 && videoTracks.length === 0) return []
  return [{
    id: "legacy-remote",
    audioStream: audioTracks.length > 0 ? new MediaStream(audioTracks) : undefined,
    videoStream: videoTracks.length > 0 ? new MediaStream(videoTracks) : undefined,
    hasAudio: audioTracks.length > 0,
    hasVideo: videoTracks.length > 0,
  }]
}

function orderRemoteParticipants(participants: RemoteMediaParticipant[], roomState: RoomState | null, selfParticipantId: string, fallbackStream: MediaStream | null): RemoteMediaParticipant[] {
  const source = participants.length > 0 ? participants : remoteParticipantsFromStream(fallbackStream)
  const rank = new Map<string, number>()
  ;(roomState?.participants || [])
    .filter((participant) => participant.active && participant.id !== selfParticipantId)
    .forEach((participant, index) => rank.set(participant.id, index))
  return [...source].sort((left, right) => {
    const leftRank = rank.get(left.id) ?? Number.MAX_SAFE_INTEGER
    const rightRank = rank.get(right.id) ?? Number.MAX_SAFE_INTEGER
    if (leftRank !== rightRank) return leftRank - rightRank
    if (left.hasVideo !== right.hasVideo) return left.hasVideo ? -1 : 1
    return left.id.localeCompare(right.id)
  })
}

function VideoBackdrop({ participants, fallbackStream, roomParticipants, selfParticipantId }: {
  participants: RemoteMediaParticipant[]
  fallbackStream: MediaStream | null
  roomParticipants: RoomParticipant[]
  selfParticipantId: string
}) {
  const { t } = useAppState()
  const remoteParticipants = participants.length > 0 ? participants : remoteParticipantsFromStream(fallbackStream)
  const participantLookup = new Map(roomParticipants.filter((participant) => participant.id !== selfParticipantId).map((participant) => [participant.id, participant]))
  const primary = remoteParticipants.find((participant) => participant.hasVideo) || remoteParticipants[0]
  const secondary = remoteParticipants.filter((participant) => participant.id !== primary?.id)
  const primaryStream = primary?.videoStream || null
  const hasRemoteVideo = Boolean(primaryStream?.getVideoTracks().some((track) => track.readyState === "live"))
  const labelFor = (participant: RemoteMediaParticipant, index: number) => participantLookup.get(participant.id)?.code || participant.name || `${t(copy.live.anon)} ${index + 1}`
  return (
    <div className={secondary.length > 0 ? "video-stage has-secondary" : "video-stage"}>
      <div className="primary-video-frame">
        <StreamVideo stream={hasRemoteVideo ? primaryStream : null} muted className="remote-video" label={primary ? labelFor(primary, 0) : t(copy.live.anon)} />
      </div>
      {!hasRemoteVideo && <div className="video-placeholder"><VideoIcon aria-hidden="true" />{t(copy.live.connecting)}</div>}
      {secondary.length > 0 && (
        <div className="remote-tile-strip" aria-label={t(ui.roomParticipants)}>
          {secondary.slice(0, 5).map((participant, index) => {
            const roomParticipant = participantLookup.get(participant.id)
            const tileStream = participant.videoStream || null
            return (
              <div key={participant.id} className={participant.hasVideo ? "remote-tile has-video" : "remote-tile"}>
                <StreamVideo stream={participant.hasVideo ? tileStream : null} muted className="remote-tile-video" label={labelFor(participant, index + 1)} />
                {!participant.hasVideo && <span>{labelFor(participant, index + 1).replace("#P", "")}</span>}
                {roomParticipant && <small>{participantTopicText(roomParticipant, t)}</small>}
              </div>
            )
          })}
        </div>
      )}
      {remoteParticipants.map((participant) => (
        <StreamAudio key={`audio-${participant.id}`} stream={participant.audioStream || null} />
      ))}
    </div>
  )
}

function StreamVideo({ stream, muted = false, className, label }: { stream: MediaStream | null; muted?: boolean; className: string; label: string }) {
  const ref = useRef<HTMLVideoElement | null>(null)

  useEffect(() => {
    const node = ref.current
    if (!node) return
    node.srcObject = stream
    if (stream) void node.play().catch(() => undefined)
    return () => {
      node.srcObject = null
    }
  }, [stream])

  return (
    <video
      ref={ref}
      className={className}
      aria-label={label}
      data-has-stream={stream ? "true" : "false"}
      autoPlay
      playsInline
      muted={muted}
      onLoadedMetadata={(event) => {
        const node = event.currentTarget
        if (!node.videoWidth || !node.videoHeight) return
        const ratio = node.videoWidth / node.videoHeight
        node.dataset.orientation = ratio > 1.15 ? "landscape" : ratio < 0.87 ? "portrait" : "square"
      }}
    />
  )
}

function StreamAudio({ stream }: { stream: MediaStream | null }) {
  const ref = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    const node = ref.current
    if (!node) return
    node.srcObject = stream
    return () => {
      node.srcObject = null
    }
  }, [stream])

  return <audio ref={ref} className="remote-audio" autoPlay />
}

function VoiceBackdrop({ layout }: { layout: LiveLayout }) {
  const { t } = useAppState()
  if (layout === "orbs") {
    return (
      <div className="voice-backdrop twin-orbs">
        <div className="twin-peer">
          <div className="voice-orb"><MicIcon aria-hidden="true" /></div>
          <strong>{t(copy.live.anon)}</strong>
          <span>{t(copy.live.status_voice)}</span>
        </div>
        <div className="twin-divider" aria-hidden="true"><span /><BoltIcon /><span /></div>
        <div className="twin-self"><div><span>{t(ui.self)}</span></div></div>
      </div>
    )
  }
  if (layout === "minimal") {
    return (
      <div className="voice-backdrop minimal-card">
        <div className="minimal-call-card">
          <div className="voice-orb"><MicIcon aria-hidden="true" /></div>
          <div className="privacy-pill inline"><EyeOffIcon aria-hidden="true" />{t(copy.live.anon)}</div>
          <p>{t(copy.live.status_voice)}</p>
          <div className="waveform inline" aria-hidden="true">{Array.from({ length: 8 }).map((_, i) => <i key={i} />)}</div>
        </div>
      </div>
    )
  }
  return (
    <div className="voice-backdrop">
      <div className="voice-orb"><MicIcon aria-hidden="true" /></div>
      <div className="waveform" aria-hidden="true">{Array.from({ length: 18 }).map((_, i) => <i key={i} />)}</div>
    </div>
  )
}

function AdminNavItem({ label, icon, active = false, compact = false, badge, to = "/admin" }: {
  label: string
  icon: string
  active?: boolean
  compact?: boolean
  badge?: number
  to?: string
}) {
  return (
    <Link className={active ? compact ? "admin-nav-item compact active" : "admin-nav-item active" : compact ? "admin-nav-item compact" : "admin-nav-item"} to={to} aria-label={label}>
      <span className="nav-glyph" aria-hidden="true"><AdminNavGlyph icon={icon} /></span>
      <span>{label}</span>
      {Boolean(badge) && <b>{badge}</b>}
    </Link>
  )
}

function AdminNavGlyph({ icon }: { icon: string }) {
  if (icon === "live") return <VideoIcon />
  if (icon === "rooms") {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="8" height="7" rx="2" />
        <rect x="13" y="4" width="8" height="7" rx="2" />
        <rect x="3" y="13" width="8" height="7" rx="2" />
        <rect x="13" y="13" width="8" height="7" rx="2" />
      </svg>
    )
  }
  if (icon === "reports") return <FlagIcon />
  if (icon === "topics") return <SearchIcon />
  if (icon === "users") return <BanIcon />
  if (icon === "safety") return <EyeOffIcon />
  if (icon === "settings") return <SettingsIcon />
  return <BoltIcon />
}

function MetricCard({ label, value, detail, history = [], trend }: {
  label: string
  value: string
  detail?: string
  history?: number[]
  trend?: MetricTrend
}) {
  const points = sparklinePoints(history)
  const direction = trend?.direction || "steady"
  return (
    <div className="metric-card">
      <div><span>{label}</span><BoltIcon aria-hidden="true" /></div>
      <strong>{value}</strong>
      <div className={`metric-sparkline ${direction}`} aria-hidden="true">
        <svg viewBox="0 0 112 34" preserveAspectRatio="none">
          <polyline points={points} />
        </svg>
      </div>
      <small className={`metric-trend ${direction}`}>{trend?.label || detail}</small>
    </div>
  )
}

function ReportQueueRow({ report, severe, busy, onReview }: {
  report: ReportSummary
  severe: boolean
  busy: boolean
  onReview: (status: ReportStatus) => void
}) {
  const { locale, t, f } = useAppState()
  return (
    <div className="report-row">
      <span className={severe ? "report-alert severe" : "report-alert"}><FlagIcon aria-hidden="true" /></span>
      <div className="report-row-body">
        <div className="report-row-copy">
          <strong>{reportReason(report.reason, t)}</strong>
          <p><span>{report.conversationCode}</span> · {f(ui.minutesAgo, { minutes: relativeMinutesNumber(report.createdAt, locale) })}</p>
        </div>
        <span className="report-actions-mini">
          <button disabled={busy || report.status !== "pending"} onClick={() => onReview("valid")}>{busy ? t(copy.actions.pending) : t(ui.valid)}</button>
          <button disabled={busy || report.status !== "pending"} onClick={() => onReview("invalid")}>{t(ui.invalid)}</button>
        </span>
      </div>
    </div>
  )
}

function HotTopics({ topics }: { topics: AdminDashboard["hotTopics"] }) {
  const { locale, t } = useAppState()
  const rows = topics
  const max = Math.max(...rows.map((topic) => topic.count), 1)
  return (
    <section className="admin-panel topics-panel">
      <div className="hot-title"><BoltIcon aria-hidden="true" /><strong>{t(ui.hotTopics)}</strong></div>
      <div className="hot-list">
        {rows.slice(0, 4).map((topic) => (
          <div key={topic.label} className="hot-row">
            <span>{topic.label}</span>
            <b><i style={{ width: `${Math.max(18, (topic.count / max) * 100)}%` }} /></b>
            <em>{formatNumber(topic.count, locale)}</em>
          </div>
        ))}
        {!rows.length && <div className="admin-empty compact">{t(ui.noHotTopics)}</div>}
      </div>
    </section>
  )
}

function reportReasons(t: (value: LocalizedText) => string) {
  return [
    { value: "harassment", label: t(ui.harassment) },
    { value: "sexual", label: t(ui.inappropriate) },
    { value: "violence", label: t({ fa: "خشونت یا تهدید", en: "Violence or threats" }) },
    { value: "scam", label: t({ fa: "کلاهبرداری یا اسپم", en: "Scam or spam" }) },
    { value: "misinfo", label: t({ fa: "اطلاعات نادرست", en: "Misinformation" }) },
    { value: "other", label: t({ fa: "موارد دیگر", en: "Other" }) },
  ]
}

function localConversationCode(sessionId: string): string {
  const compact = sessionId.replace(/[^a-z0-9]/gi, "").toUpperCase()
  return `#${(compact.slice(-4) || "F8K2").padStart(4, "0")}`
}

function toPercent(value: number, locale: Locale): string {
  return formatNumber(Math.round(value * 100), locale)
}

function formatPercentPoints(value: number, locale: Locale): string {
  return `${formatNumber(Math.round(value), locale)}٪`
}

function formatNumber(value: number, locale: Locale): string {
  return new Intl.NumberFormat(locale === "fa" ? "fa-IR" : "en-US", { maximumFractionDigits: 0 }).format(value)
}

function formatEvidenceSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 KB"
  if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

function formatClock(value: number, locale: Locale): string {
  return new Intl.DateTimeFormat(locale === "fa" ? "fa-IR" : "en-US", { hour: "2-digit", minute: "2-digit" }).format(new Date(value))
}

function formatDuration(seconds: number, locale: Locale): string {
  if (!seconds) return locale === "fa" ? "۰:۰۰" : "0:00"
  const minutes = Math.floor(seconds / 60)
  const sec = seconds % 60
  const localizedSeconds = new Intl.NumberFormat(locale === "fa" ? "fa-IR" : "en-US", {
    minimumIntegerDigits: 2,
    maximumFractionDigits: 0,
    useGrouping: false,
  }).format(sec)
  return `${formatNumber(minutes, locale)}:${localizedSeconds}`
}

function formatRoomLiveness(seconds: number, locale: Locale): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return locale === "fa" ? "فعلاً بدون زمان ذخیره" : "No saved time yet"
  const minutes = Math.ceil(seconds / 60)
  if (minutes < 60) return locale === "fa" ? `${formatNumber(minutes, locale)} دقیقه زنده` : `${formatNumber(minutes, locale)}m live`
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return locale === "fa" ? `${formatNumber(hours, locale)}س ${formatNumber(rest, locale)}د زنده` : `${formatNumber(hours, locale)}h ${formatNumber(rest, locale)}m live`
}

function secondsUntil(value?: string | null): number {
  if (!value) return 0
  return Math.max(0, Math.floor((new Date(value).getTime() - Date.now()) / 1000))
}

function momentumLabel(momentum: string, t: (value: LocalizedText) => string): string {
  if (momentum === "up") return t(ui.momentumUp)
  if (momentum === "down") return t(ui.momentumDown)
  return t(ui.momentumSteady)
}

function matchingQualityCopy(seconds: number, topicText: string, t: (value: LocalizedText) => string): string {
  if (!topicText.trim()) return t(ui.searchingNoTopic)
  if (seconds < 8) return t(ui.searchingExact)
  if (seconds < 20) return t(ui.searchingSemantic)
  return t(ui.searchingExpanded)
}

function vibrateMatchFound(): void {
  try {
    navigator.vibrate?.([25, 35, 25])
  } catch {
    // Haptics are optional and unsupported on many browsers.
  }
}

function relativeMinutesNumber(createdAt: string, locale: Locale): string {
  const minutes = Math.max(1, Math.round((Date.now() - new Date(createdAt).getTime()) / 60000))
  return formatNumber(minutes, locale)
}

function relativeMinutes(createdAt: string, locale: Locale): string {
  const minutes = Math.max(1, Math.round((Date.now() - new Date(createdAt).getTime()) / 60000))
  return locale === "fa" ? `${formatNumber(minutes, locale)} دقیقه` : `${formatNumber(minutes, locale)}m`
}

function relativeReportTime(createdAt: string, locale: Locale, f: (value: LocalizedText, vars: Record<string, string | number>) => string): string {
  const minutes = Math.max(1, Math.round((Date.now() - new Date(createdAt).getTime()) / 60000))
  if (minutes < 60) return f(ui.minutesAgo, { minutes: formatNumber(minutes, locale) })
  const days = Math.round(minutes / 1440)
  if (days <= 1) return locale === "fa" ? "دیروز" : "yesterday"
  if (days >= 7) return locale === "fa" ? "هفتهٔ پیش" : "last week"
  return locale === "fa" ? `${formatNumber(days, locale)} روز پیش` : `${formatNumber(days, locale)} days ago`
}

function connectionLabel(state: ConnectionState, t: (value: LocalizedText) => string): string {
  if (state === "connected") return t(copy.live.connected)
  if (state === "connecting") return t(copy.live.connecting)
  if (state === "reconnecting") return t(copy.errors.connection)
  if (state === "unavailable") return t(copy.live.unavailable)
  return t(copy.live.failed)
}

function formatTimer(seconds: number, locale: Locale): string {
  const min = Math.floor(seconds / 60)
  const sec = seconds % 60
  const formatter = new Intl.NumberFormat(locale === "fa" ? "fa-IR" : "en-US", {
    minimumIntegerDigits: 2,
    maximumFractionDigits: 0,
    useGrouping: false,
  })
  return `${formatter.format(min)}:${formatter.format(sec)}`
}

function modeLabel(mode: Mode | "", t: (value: LocalizedText) => string): string {
  if (mode === "video") return t(copy.home.mode_video)
  if (mode === "voice") return t(copy.home.mode_voice)
  return "—"
}

function reportReason(reason: string, t: (value: LocalizedText) => string): string {
  const normalized = reason.toLowerCase()
  if (normalized.includes("harassment") || reason === ui.harassment.fa || reason === "مزاحمت") return t(ui.harassment)
  if (normalized.includes("sexual") || normalized.includes("inappropriate") || reason === ui.inappropriate.fa) return t(ui.inappropriate)
  if (normalized.includes("scam") || normalized.includes("spam") || reason === ui.spam.fa) return t({ fa: "کلاهبرداری یا اسپم", en: "Scam or spam" })
  if (normalized.includes("misinfo")) return t({ fa: "اطلاعات نادرست", en: "Misinformation" })
  if (normalized.includes("violence")) return t({ fa: "خشونت یا تهدید", en: "Violence or threats" })
  if (normalized.includes("other")) return t({ fa: "موارد دیگر", en: "Other" })
  return reason
}

function initialFor(value: string, locale: Locale): string {
  const trimmed = value.trim()
  if (trimmed) return trimmed[0]
  return locale === "fa" ? "س" : "A"
}

function profileInitialFor(value: string | undefined, locale: Locale): string {
  const trimmed = value?.trim() || ""
  if (trimmed) return Array.from(trimmed)[0].toLocaleUpperCase(locale === "fa" ? "fa-IR" : "en-US")
  return locale === "fa" ? "ج" : "J"
}

function safeAvatarUrl(value: string | undefined): string {
  const trimmed = value?.trim() || ""
  if (!trimmed) return ""
  if (trimmed.startsWith("data:image/")) return trimmed
  try {
    const url = new URL(trimmed, window.location.origin)
    if (url.protocol === "https:" || url.protocol === "http:") return url.href
  } catch {
    return ""
  }
  return ""
}
